import { useState, useMemo, useEffect, useRef, useCallback, useContext, createContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { useApi } from '../hooks/useApi';
// Countries come from the SAME file the Export Customers map draws, so the picker can
// never offer a country the map has no shape for, and both spell it identically.
import { COUNTRY_OPTIONS, COUNTRY_BY_CODE } from './ExportCustomers';
import { MAJANS_TENANT_ID } from '../config/auth';
import { Badge, Button } from '../components/ui';
import { fetchCustomerCorrespondence, fetchCustomerCalendar, isSafeContactEmail, CORRESPONDENCE_LOOKBACK_MONTHS } from '../services/graphMail';

// Legacy localStorage key — read once on first API load, push to DB, then deleted.
const LEGACY_STORAGE_KEY = 'crm-data';

// Schema version stamped onto saved blobs. Bump when the shape changes in a
// non-backward-compatible way; add migration logic to the hydration path.
// History:
//   1 — initial DB-backed shape (customers, contacts, activities, tasks)
const CRM_SCHEMA_VERSION = 1;

// ─── Seed data ────────────────────────────────────────────────────────────────
const SEED_CUSTOMERS = {
  'CUS0044': { code: 'CUS0044', name: 'Woolworths', short_name: 'Woolworths', segment: 'Major Grocery', notes: 'Two supply routes under the same account: most SKUs go to Regional DCs (RDCs) under CUS0044; some volume is directed to the National DC (NDC) in Sydney under CUS0183 — both are the same commercial relationship.', created: '2026-03-27' },
  'CUS0027': { code: 'CUS0027', name: 'Coles Supermarkets', short_name: 'Coles', segment: 'Major Grocery', notes: '', created: '2026-03-27' },
  'CUS0004': { code: 'CUS0004', name: 'Metcash Trading Ltd Australia', short_name: 'Metcash / IGA', segment: 'Wholesale', notes: '', created: '2026-03-27' },
  'CUS0003': { code: 'CUS0003', name: 'Costco Wholesale Australia', short_name: 'Costco AU', segment: 'Warehouse Club', notes: '', created: '2026-03-27' },
  'CUS0076': { code: 'CUS0076', name: 'ALDI', short_name: 'ALDI', segment: 'Discount', notes: '', created: '2026-03-27' },
  'CUS0002': { code: 'CUS0002', name: 'Big W', short_name: 'Big W', segment: 'Discount Variety', notes: '', created: '2026-03-27' },
  'CUS0099': { code: 'CUS0099', name: 'Drakes Supermarkets Pty Ltd', short_name: 'Drakes', segment: 'Independent', notes: '', created: '2026-03-27' },
  'CUS0038': { code: 'CUS0038', name: 'SPAR', short_name: 'SPAR', segment: 'Independent', notes: '', created: '2026-03-27' },
  'CUS0100': { code: 'CUS0100', name: 'Amazon', short_name: 'Amazon', segment: 'Online', notes: '', created: '2026-03-27' },
  'CUS0043': { code: 'CUS0043', name: 'The Reject Shop', short_name: 'Reject Shop', segment: 'Discount Variety', notes: '', created: '2026-03-27' },
  'CUS0001': { code: 'CUS0001', name: 'Coles Homebrand', short_name: 'Coles PL', segment: 'Private Label', notes: '', created: '2026-03-27' },
  'CUS0224': { code: 'CUS0224', name: 'Costco Wholesale USA', short_name: 'Costco USA', segment: 'Export', notes: '', created: '2026-03-27' },
  'CUS0031': { code: 'CUS0031', name: 'JHF Australia Exports Pty Ltd', short_name: 'JHF', segment: 'Export', notes: '', created: '2026-03-27' },
  // Retailers
  'CUS0201': { code: 'CUS0201', name: 'TK Maxx / TJX Companies', short_name: 'TK Maxx', segment: 'Discount Variety', notes: 'US-based off-price retailer. Heidi Cooper is the buyer for AU/NZ.', created: '2026-03-30' },
  'CUS0202': { code: 'CUS0202', name: 'Harris Farm Markets', short_name: 'Harris Farm', segment: 'Independent', notes: '', created: '2026-03-30' },
  'CUS0203': { code: 'CUS0203', name: 'Morsl', short_name: 'Morsl', segment: 'Online', notes: 'Workplace food platform / micro-market operator.', created: '2026-03-30' },
  'CUS0204': { code: 'CUS0204', name: 'Statewide Independent Wholesalers (SIW)', short_name: 'Statewide Tasmania', segment: 'Wholesale', notes: 'Tasmanian retailer/wholesaler. Operates as SIW Merchandising (siw.com.au).', created: '2026-03-30' },
  'CUS0205': { code: 'CUS0205', name: 'AAFCANS', short_name: 'AAFCANS', segment: 'Other', notes: 'Australian Army Canteen Service — defence retail.', created: '2026-03-30' },
  'CUS0206': { code: 'CUS0206', name: 'WHSmith / Relay', short_name: 'WHSmith', segment: 'Other', notes: 'Travel retail — airports and transport hubs.', created: '2026-03-30' },
  'CUS0207': { code: 'CUS0207', name: 'Virgin Australia', short_name: 'Virgin Australia', segment: 'Other', notes: 'Airline retail — inflight and lounge.', created: '2026-03-30' },
  'CUS0208': { code: 'CUS0208', name: 'Gate Gourmet', short_name: 'Gate Gourmet', segment: 'Other', notes: 'Airline catering / inflight food supplier (gategroup).', created: '2026-03-30' },
  // Clearance
  'CUS0209': { code: 'CUS0209', name: 'Golden Circle Food Outlet', short_name: 'GCFO', segment: 'Clearance', notes: 'Clearance customer. Buys short-dated or surplus stock.', created: '2026-03-30' },
  'CUS0210': { code: 'CUS0210', name: "Silly Solly's", short_name: "Silly Solly's", segment: 'Clearance', notes: 'Discount/clearance retailer.', created: '2026-03-30' },
  // Export / Distributor
  'CUS0211': { code: 'CUS0211', name: 'Source Atlantique (Food Import Group)', short_name: 'Source Atlantique', segment: 'Export', notes: 'US distributor. Also known as Food Import Group (foodimportgroup.com). Primary US export partner.', created: '2026-03-30' },
  'CUS0212': { code: 'CUS0212', name: 'Unique Health Distributors', short_name: 'Unique Health', segment: 'Distributor', notes: 'Australian health food distributor (uhp.com.au).', created: '2026-03-30' },
};

// The segment vocabulary is DATA, not code (migration 061): it lives in
// commercial.CrmSegment and is added to / renamed / retired in the app itself via
// /api/crm-segments. This array is only the fallback the picker uses when that call
// fails, so a network blip can never leave the form with an empty dropdown. Keep it
// in step with the seed in db/migrations/commercial-061-crm-segment.sql.
const FALLBACK_SEGMENTS = ['Major Grocery', 'Wholesale', 'Warehouse Club', 'Discount', 'Discount Variety', 'Independent', 'Online', 'Private Label', 'Export', 'Clearance', 'Distributor', 'Other'];

// ─── Seed contacts ─────────────────────────────────────────────────────────────
const SEED_CONTACTS = {
  // Coles
  'sc-col-01': { id: 'sc-col-01', customer_code: 'CUS0027', first_name: 'Hannah',    last_name: 'Bannerman',      title: 'Asst Category Manager – Snacks', email: 'hannah.bannerman@coles.com.au',       phone: '', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  'sc-col-02': { id: 'sc-col-02', customer_code: 'CUS0027', first_name: 'Lydia',     last_name: 'Graham (Binder)', title: 'Asst Category Manager – Snacks Impulse', email: 'Lydia.Binder@coles.com.au', phone: '0466 566 435', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  'sc-col-03': { id: 'sc-col-03', customer_code: 'CUS0027', first_name: 'David',     last_name: 'Carpinteri',     title: 'Buyer / Category',               email: 'David.Carpinteri@coles.com.au',       phone: '', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  'sc-col-04': { id: 'sc-col-04', customer_code: 'CUS0027', first_name: 'Stephanie', last_name: 'Venuto',         title: 'Buyer',                          email: 'Stephanie.Venuto@coles.com.au',       phone: '', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  'sc-col-05': { id: 'sc-col-05', customer_code: 'CUS0027', first_name: 'Jenny',     last_name: 'Burns',          title: 'BCC Snacks',                     email: 'Jenny.Burns@coles.com.au',            phone: '', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  'sc-col-06': { id: 'sc-col-06', customer_code: 'CUS0027', first_name: 'Olivia',    last_name: 'Thompson',       title: 'Range Review',                   email: 'Olivia.Thompson@coles.com.au',        phone: '', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  'sc-col-07': { id: 'sc-col-07', customer_code: 'CUS0027', first_name: 'Georgia',   last_name: 'Moshopoulos',    title: 'Flybuys / Media',                email: 'Georgia.Moshopoulos@coles.com.au',    phone: '', linkedin: '', notes: 'Flybuys campaign contact at Coles. Works on loyalty media proposals.', active: true, created: '2026-04-10' },
  'sc-col-08': { id: 'sc-col-08', customer_code: 'CUS0027', first_name: 'Milenia',   last_name: 'Nanda',          title: 'Coles Online Media',             email: 'Milenia.Nanda@coles.com.au',          phone: '', linkedin: '', notes: 'Coles Online digital campaign contact. Manages online media specs and deadlines.', active: true, created: '2026-04-10' },
  'sc-col-09': { id: 'sc-col-09', customer_code: 'CUS0027', first_name: 'Trudy',     last_name: 'Pickett',        title: 'Supply Chain / MLOR',            email: 'Trudy.Pickett@coles.com.au',          phone: '', linkedin: '', notes: 'Supply chain contact. Handles MLOR (Minimum Life on Receipt) approvals.', active: true, created: '2026-04-10' },
  // Woolworths
  'sc-ww-01': { id: 'sc-ww-01', customer_code: 'CUS0044', first_name: 'James',    last_name: 'MacMahon',    title: 'Buyer',                        email: 'jmacmahon@woolworths.com.au',      phone: '', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  'sc-ww-02': { id: 'sc-ww-02', customer_code: 'CUS0044', first_name: 'Andrew',   last_name: 'Nichols',     title: 'Buyer',                        email: 'anichols@woolworths.com.au',       phone: '', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  'sc-ww-03': { id: 'sc-ww-03', customer_code: 'CUS0044', first_name: 'Thai',     last_name: 'Lim',         title: 'Buyer',                        email: 'tlim@woolworths.com.au',           phone: '', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  'sc-ww-04': { id: 'sc-ww-04', customer_code: 'CUS0044', first_name: 'C',        last_name: 'Norman',      title: 'Buyer',                        email: 'cnorman@woolworths.com.au',        phone: '', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  'sc-ww-05': { id: 'sc-ww-05', customer_code: 'CUS0044', first_name: 'Amy',      last_name: 'Chang',       title: 'Buyer',                        email: 'xchang@woolworths.com.au',         phone: '', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  'sc-ww-06': { id: 'sc-ww-06', customer_code: 'CUS0044', first_name: 'Jo',       last_name: 'Thompson',    title: 'Buyer',                        email: 'jthompson4@woolworths.com.au',     phone: '', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  'sc-ww-07': { id: 'sc-ww-07', customer_code: 'CUS0044', first_name: 'Amy',      last_name: 'Vicary',      title: 'Buyer',                        email: 'avicary@woolworths.com.au',        phone: '', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  'sc-ww-08': { id: 'sc-ww-08', customer_code: 'CUS0044', first_name: 'Jaimi',    last_name: 'Storch',      title: 'Finance / Co-Op',              email: 'jstorch@woolworths.com.au',        phone: '', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  'sc-ww-09': { id: 'sc-ww-09', customer_code: 'CUS0044', first_name: 'Emily',    last_name: 'Groenewald',  title: 'Category',                     email: 'EGroenewald@woolworths.com.au',    phone: '', linkedin: '', notes: '', active: true, created: '2026-03-27' },
  // Woolworths – Cartology (retail media arm)
  'sc-ww-10': { id: 'sc-ww-10', customer_code: 'CUS0044', first_name: 'Katie',  last_name: 'Goulder', title: 'Retail Media (Cartology)',         email: 'kgoulder@cartology.com.au',    phone: '', linkedin: '', notes: 'Cartology is Woolworths retail media arm.', active: true, created: '2026-03-27' },
  'sc-ww-11': { id: 'sc-ww-11', customer_code: 'CUS0044', first_name: 'E',      last_name: 'Johnston', title: 'Agency Partnerships (Cartology)', email: 'ejohnston1@cartology.com.au',  phone: '', linkedin: '', notes: 'Cartology is Woolworths retail media arm.', active: true, created: '2026-03-27' },
  'sc-ww-12': { id: 'sc-ww-12', customer_code: 'CUS0044', first_name: 'P',      last_name: 'Condon',   title: 'Retail Media (Cartology)',         email: 'pcondon1@cartology.com.au',    phone: '', linkedin: '', notes: 'Cartology is Woolworths retail media arm.', active: true, created: '2026-03-27' },
  // TK Maxx
  'sc-tkx-01': { id: 'sc-tkx-01', customer_code: 'CUS0201', first_name: 'Heidi',    last_name: 'Cooper',   title: 'Buyer – US',    email: 'heidi_cooper@tjx.com',              phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  // Harris Farm
  'sc-hfm-01': { id: 'sc-hfm-01', customer_code: 'CUS0202', first_name: 'Tina',     last_name: 'Xue',      title: 'Buyer',         email: 'txue@harrisfarm.com.au',             phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  // Morsl
  'sc-mor-01': { id: 'sc-mor-01', customer_code: 'CUS0203', first_name: 'King',     last_name: 'Cabrera',  title: 'Operations',    email: 'king.cabrera@morsl.com.au',         phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  // Statewide Tasmania
  'sc-siw-01': { id: 'sc-siw-01', customer_code: 'CUS0204', first_name: 'Brandon',  last_name: 'Bassett',  title: 'Contact',       email: 'bbassett2@siw.com.au',              phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  'sc-siw-02': { id: 'sc-siw-02', customer_code: 'CUS0204', first_name: 'Ricky',    last_name: 'Parker',   title: 'Contact',       email: 'rickparker@siw.com.au',             phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  // AAFCANS
  'sc-aaf-01': { id: 'sc-aaf-01', customer_code: 'CUS0205', first_name: 'Rod',      last_name: 'Brindle',  title: 'Procurement',   email: 'rod.brindle@aafcans.gov.au',        phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  // WHSmith
  'sc-whs-01': { id: 'sc-whs-01', customer_code: 'CUS0206', first_name: 'Mansheel', last_name: 'Gill',     title: 'Contact',       email: 'mansheel.gill@whsmith.com.au',      phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  'sc-whs-02': { id: 'sc-whs-02', customer_code: 'CUS0206', first_name: 'Laura',    last_name: 'Hurst',    title: 'Contact',       email: 'laura.hurst@whsmith.com.au',        phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  // Virgin Australia
  'sc-vir-01': { id: 'sc-vir-01', customer_code: 'CUS0207', first_name: 'Louise',   last_name: 'DiPauli',  title: 'Contact',       email: 'Louise.DiPauli@virginaustralia.com', phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  // Gate Gourmet
  'sc-gg-01': { id: 'sc-gg-01', customer_code: 'CUS0208', first_name: 'S',        last_name: 'Nambiar',  title: 'Purchasing',    email: 'SNambiar@gategroup.com',            phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  'sc-gg-02': { id: 'sc-gg-02', customer_code: 'CUS0208', first_name: 'R',        last_name: 'Pooni',    title: 'Purchasing',    email: 'RPooni@gategroup.com',              phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  // GCFO
  'sc-gcf-01': { id: 'sc-gcf-01', customer_code: 'CUS0209', first_name: 'Liam',    last_name: 'Corboy',   title: 'Contact',       email: 'liam@gcfo.com.au',                  phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  'sc-gcf-02': { id: 'sc-gcf-02', customer_code: 'CUS0209', first_name: 'Mick',    last_name: 'Corboy',   title: 'Contact',       email: 'mick@gcfo.com.au',                  phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  // Silly Solly's
  'sc-ssl-01': { id: 'sc-ssl-01', customer_code: 'CUS0210', first_name: 'Bradley', last_name: 'Walton',   title: 'Buyer',         email: 'brad@sillysollys.com.au',           phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  // Source Atlantique (FIG)
  'sc-saf-01': { id: 'sc-saf-01', customer_code: 'CUS0211', first_name: 'Christian', last_name: 'Rojas',      title: 'Primary Contact', email: 'crojas@foodimportgroup.com',     phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  'sc-saf-02': { id: 'sc-saf-02', customer_code: 'CUS0211', first_name: 'Scott',     last_name: 'Zirul',      title: 'Logistics',       email: 'SZirul@foodimportgroup.com',     phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  'sc-saf-03': { id: 'sc-saf-03', customer_code: 'CUS0211', first_name: 'D',         last_name: 'Cook',       title: 'Sales',           email: 'DCook@foodimportgroup.com',      phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  'sc-saf-04': { id: 'sc-saf-04', customer_code: 'CUS0211', first_name: 'K',         last_name: 'Van Schaack', title: 'Contact',        email: 'KVanSchaack@foodimportgroup.com', phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
  // Unique Health Distributors
  'sc-uhp-01': { id: 'sc-uhp-01', customer_code: 'CUS0212', first_name: 'Inventory', last_name: 'Team', title: 'Orders', email: 'Inventory@uhp.com.au', phone: '', linkedin: '', notes: '', active: true, created: '2026-03-30' },
};

// ─── Seed activities ───────────────────────────────────────────────────────────
const SEED_ACTIVITIES = {
  'sa-jmm-01': { id: 'sa-jmm-01', customer_code: 'CUS0044', contact_id: 'sc-ww-01', type: 'Email', date: '2026-03-31', subject: 'Majans range review — follow-up on proposal', notes: 'James following up on range review proposal and meeting. Asked for a timeline on Majans response. Mentioned marketing competitiveness as a concern.', outcome: 'James requesting a price decrease on Bhuja Core. All additional investment recognised in P&L.', next_action: 'Prepare Majans response on range review proposal', completed: false, created: '2026-03-31' },
  'sa-jmm-02': { id: 'sa-jmm-02', customer_code: 'CUS0044', contact_id: 'sc-ww-01', type: 'Email', date: '2026-03-31', subject: 'Majans range review — investment spend benefit', notes: 'Matt responded to James: queried whether increased investment in promotions/marketing would be of benefit given 2-fold improvement in profitability and sales.', outcome: '', next_action: '', completed: true, created: '2026-03-31' },
  'sa-jmm-03': { id: 'sa-jmm-03', customer_code: 'CUS0044', contact_id: 'sc-ww-01', type: 'Email', date: '2026-04-01', subject: 'Majans range review — cost impact & mix suggestion', notes: 'James replied: confirmed there is an underlying cost impact through these initiatives. Suggested a mix of both investment and pricing may help create a strong proposal.', outcome: 'James open to a blended approach (investment + pricing).', next_action: 'Develop blended proposal for range review', completed: false, created: '2026-04-01' },
  'sa-jmm-04': { id: 'sa-jmm-04', customer_code: 'CUS0044', contact_id: 'sc-ww-01', type: 'Email', date: '2026-04-01', subject: 'Infuzions Poppeas — WFI Awards congratulations', notes: 'James replied to Hayley\'s WFI Awards update email. Congratulated the team on Poppeas being shortlisted for 3 Global Food Innovation Awards (Finalist: Best Children\'s/Baby Product & Best Snack Product; Commended: Best Plant-Based Product).', outcome: 'Positive engagement from James.', next_action: '', completed: true, created: '2026-04-01' },
  'sa-jmm-05': { id: 'sa-jmm-05', customer_code: 'CUS0044', contact_id: 'sc-ww-01', type: 'Email', date: '2026-04-07', subject: 'Majans range review — post-Easter follow-up', notes: 'James followed up again after Easter asking what timeline to expect a response from Majans on the range review proposal.', outcome: '', next_action: 'Respond to James with range review timeline', completed: false, created: '2026-04-07' },
  'sa-jmm-06': { id: 'sa-jmm-06', customer_code: 'CUS0044', contact_id: 'sc-ww-01', type: 'Email', date: '2026-04-07', subject: 'Bluey Bhuja Noodle Multipack — BBC licence update', notes: 'Hayley emailed James with update on Bluey Bhuja Noodle Multipack. BBC licence approval process timing does not align — cannot proceed with the Bluey collaboration at this stage.', outcome: 'Bluey collaboration shelved for now due to BBC timing.', next_action: '', completed: true, created: '2026-04-07' },
  'sa-jmm-07': { id: 'sa-jmm-07', customer_code: 'CUS0044', contact_id: 'sc-ww-01', type: 'Email', date: '2026-04-08', subject: 'Letter of support — Majans manufacturing investment grant', notes: 'Hayley (high importance) requested James provide a letter of support for Majans\' QLD Government manufacturing grant application to upgrade extrusion lines at the Sumner facility.', outcome: '', next_action: 'Follow up with James on letter of support', completed: false, created: '2026-04-08' },
  'sa-jmm-08': { id: 'sa-jmm-08', customer_code: 'CUS0044', contact_id: 'sc-ww-01', type: 'Email', date: '2026-04-08', subject: 'Majans range review — requesting phone call', notes: 'Matt emailed James asking when he has time to discuss the range review proposal over the phone.', outcome: '', next_action: 'Await James response on call availability', completed: false, created: '2026-04-08' },
  'sa-jmm-09': { id: 'sa-jmm-09', customer_code: 'CUS0044', contact_id: 'sc-ww-01', type: 'Email', date: '2026-04-09', subject: 'Bhuja Noodles Multipack — BFY health claims', notes: 'Matt sent James health claims for a proposed Bhuja Noodles Multipack: HSR 3–4 stars, vegetable content, Better For You positioning. Followed up on conversation from previous day.', outcome: '', next_action: 'Send packaging image and organise sample for James', completed: false, created: '2026-04-09' },
  'sa-jmm-10': { id: 'sa-jmm-10', customer_code: 'CUS0044', contact_id: 'sc-ww-01', type: 'Email', date: '2026-04-09', subject: 'Bhuja Noodles Multipack — James requests packaging image & sample', notes: 'James replied asking for a packaging image and sample to be sent down.', outcome: '', next_action: 'Send packaging image and arrange sample shipment to James', completed: false, created: '2026-04-09' },
  'sa-jmm-11': { id: 'sa-jmm-11', customer_code: 'CUS0044', contact_id: 'sc-ww-01', type: 'Meeting', date: '2026-04-23', subject: 'Business Review — Woolworths', location: '', notes: 'Business review meeting with James MacMahon.', outcome: '', next_action: '', completed: false, created: '2026-04-10' },
  'sa-jmm-12': { id: 'sa-jmm-12', customer_code: 'CUS0044', contact_id: '', type: 'Meeting', date: '2026-04-15', subject: 'Blue Head Office Activation', location: 'Woolworths Head Office', notes: 'Blue Head Office Activation event at Woolworths Head Office.', outcome: '', next_action: '', completed: false, created: '2026-04-10' },
  // Coles
  'sa-col-01': { id: 'sa-col-01', customer_code: 'CUS0027', contact_id: 'sc-col-04', type: 'Email', date: '2026-04-07', subject: 'OFD opportunity YTD — Phase 2 & 3 recommendations', notes: 'Stephanie provided OFD Phase 2 & 3 recommendations for remaining Q4 weeks (limited availability). Confirmed Bhuja HP CO campaign secured: FY26 Q4 May, Campaign ID #48549. Content card secured (not feature row) — no 4-SKU minimum required for phases 2 and 3 (03.06 and 17.06).', outcome: 'Bhuja Q4 OFD campaign confirmed. Content card in Phase 2 (3 Jun) and Phase 3 (17 Jun).', next_action: 'Confirm asset requirements with Coles Online team', completed: true, created: '2026-04-10' },
  'sa-col-02': { id: 'sa-col-02', customer_code: 'CUS0027', contact_id: 'sc-col-04', type: 'Email', date: '2026-04-08', subject: 'OFD opportunity YTD — Hayley confirms Phase 2 & 3', notes: 'Hayley confirmed Phase 2 and 3 OFD bookings to Stephanie. Stephanie confirmed Milenia Nanda (Coles Online) will be in contact with specs and required elements for the campaign.', outcome: 'Coles Online media contact (Milenia Nanda) to follow up with specs.', next_action: '', completed: true, created: '2026-04-10' },
  'sa-col-03': { id: 'sa-col-03', customer_code: 'CUS0027', contact_id: 'sc-col-08', type: 'Email', date: '2026-04-07', subject: '[URGENT] Coles Online – Specs & Deadlines – Campaign #45542', notes: 'Milenia Nanda (Coles Online, high importance) urgently requested digital assets for Coles Online campaign #45542. Provided link to Coles Online Digital Specs and best-dressed asset examples. Flagged tight deadline.', outcome: '', next_action: 'Submit digital assets for campaign #45542 to Milenia', completed: false, created: '2026-04-10' },
  'sa-col-04': { id: 'sa-col-04', customer_code: 'CUS0027', contact_id: 'sc-col-07', type: 'Email', date: '2026-04-09', subject: 'Flybuys – Q4 campaign brief sent to Georgia', notes: 'Hayley sent Flybuys Q4 campaign brief to Georgia Moshopoulos (Coles Flybuys). Georgia acknowledged receipt and confirmed she would work on a proposal.', outcome: '', next_action: '', completed: true, created: '2026-04-10' },
  'sa-col-05': { id: 'sa-col-05', customer_code: 'CUS0027', contact_id: 'sc-col-07', type: 'Email', date: '2026-04-09', subject: 'Flybuys Campaign Proposal – Bhuja received', notes: 'Georgia Moshopoulos (Coles Flybuys) sent campaign proposal for Q4 Bhuja Flybuys campaign (with attachment).', outcome: '', next_action: 'Review Flybuys campaign proposal and respond to Georgia', completed: false, created: '2026-04-10' },
  'sa-col-06': { id: 'sa-col-06', customer_code: 'CUS0027', contact_id: 'sc-col-03', type: 'Email', date: '2026-04-08', subject: 'POs 49474302A & 49473321A — MLOR Assist (Infuzions WA)', notes: 'David Carpinteri flagged two POs with stock under MLOR destined for WA: Infuzions Veggie Straws Sour Cream & Herb 90G (240 cartons, 5/10 UBD, must clear by 21 May) and Infuzions Prawn Crackers BBQ 110G. Higher-than-actuals forecast led to excess WA stock. Escalated via Trudy Pickett to Hannah Bannerman for approval.', outcome: 'Hannah Bannerman approved accepting stock under reduced MLOR, confident it will clear.', next_action: '', completed: true, created: '2026-04-10' },
  'sa-col-07': { id: 'sa-col-07', customer_code: 'CUS0027', contact_id: 'sc-col-02', type: 'Email', date: '2026-04-09', subject: 'BPV New Line Setup — form requested urgently', notes: 'Lydia Graham (Binder) urgently requested the BPV new line setup form be completed and returned by 11:30am for submission to the Coles Central Team. Sent wrong form initially, then corrected with the right attachment. Matthew confirmed he would proceed.', outcome: 'BPV new line setup form submitted to Coles.', next_action: '', completed: true, created: '2026-04-10' },
};

const ACT_TYPES  = ['Meeting', 'Call', 'Email', 'Site Visit', 'Video Call', 'Trade Show', 'Range Review', 'Promo Discussion', 'JBP Session', 'Other'];
const PRIORITIES = ['High', 'Medium', 'Low'];
const STATUSES   = ['Open', 'In Progress', 'Done'];

function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }
function contactName(c) { return [c.first_name, c.last_name].filter(Boolean).join(' ') || c.name || ''; }
// Null-safe ascending string sort: missing / empty / non-string values sort to the end.
// Used for due_date and date fields that may be undefined on legacy or partially-filled records.
function cmpAsc(a, b) { return (a || '￿').localeCompare(b || '￿'); }
function cmpDesc(a, b) { return (b || '').localeCompare(a || ''); }

/* ─── 057: attendees + next actions ──────────────────────────────────────────
 * An activity used to carry ONE contact_id and ONE free-text next action. It now
 * carries a list of everyone who was there (both sides) and its next actions are
 * real CrmTask rows.
 *
 * Attendee shape: { id, side: 'customer' | 'majans', contact_id, name, email, title }
 *
 * Every reader goes through attendeesOf() so a pre-057 activity (contact_id set,
 * attendees empty) and a post-057 one render identically — there is deliberately
 * no "legacy branch" scattered through the views.
 */
function attendeesOf(activity, contactsById = {}) {
  const list = Array.isArray(activity?.attendees) ? activity.attendees : [];
  if (list.length > 0) return list;
  const c = activity?.contact_id ? contactsById[activity.contact_id] : null;
  return c ? [{ id: c.id, side: 'customer', contact_id: c.id, name: contactName(c), email: c.email || '', title: c.title || '' }] : [];
}

/* The name to show for one attendee: the live contact record when it still
 * exists (so renaming a contact updates every activity), otherwise the snapshot
 * taken when the activity was logged. The snapshot is why deleting a contact
 * doesn't blank out the history it appears in. */
function attendeeName(att, contactsById = {}) {
  const live = att?.contact_id ? contactsById[att.contact_id] : null;
  return (live ? contactName(live) : '') || att?.name || '';
}

/* One-line "who was there", customer side first. Used by the compact views and
 * by the CSV / AI-context exports so they all say the same thing. */
function attendeeSummary(activity, contactsById = {}) {
  const list = attendeesOf(activity, contactsById);
  const named = side => list.filter(a => a.side === side).map(a => attendeeName(a, contactsById)).filter(Boolean);
  const cust = named('customer');
  const majans = named('majans');
  return [cust.join(', '), majans.length ? `Majans: ${majans.join(', ')}` : ''].filter(Boolean).join(' · ');
}

/* The tasks an activity's Next Actions produced. Sorted by due date, the same
 * order the Tasks tab uses, so the two views never disagree. */
function nextActionTasks(tasksById, activityId) {
  if (!activityId) return [];
  return Object.values(tasksById || {}).filter(t => t.activity_id === activityId).sort((a, b) => cmpAsc(a.due_date, b.due_date));
}

/* contact_id stays the activity's PRIMARY contact — it drives the avatar, the
 * 056 correspondence fold and the CSV — but it is now derived from the attendee
 * list rather than typed separately, so there is only one thing to keep right. */
function primaryContactId(attendees) {
  const first = (Array.isArray(attendees) ? attendees : []).find(a => a.side === 'customer' && a.contact_id);
  return first ? first.contact_id : '';
}

// Responsive layout overrides. The CRM is desktop-first (fixed-pixel sidebar,
// 2-column grids); these media queries collapse it sanely on phones/tablets
// without rewriting every inline style. Selectors are scoped to .crm-root so
// they cannot leak to other pages.
const RESPONSIVE_CSS = `
@media (max-width: 900px) {
  .crm-root { flex-direction: column !important; height: auto !important; min-height: calc(100vh - 110px); }
  .crm-sidebar { width: 100% !important; min-width: 0 !important; max-height: 260px; border-right: 0 !important; border-bottom: 1px solid var(--border); }
  .crm-sidebar-list { max-height: 160px; }
  .crm-content { padding: 0 0 16px !important; }
  .crm-overview-grid { grid-template-columns: 1fr !important; }
  .crm-overdue-rail { width: 100% !important; min-width: 0 !important; border-left: 0 !important; border-top: 1px solid var(--border); }
  .crm-customer-header { padding: 12px 16px !important; }
  .crm-tab-content { padding: 16px !important; }
  .crm-contacts-grid { grid-template-columns: 1fr !important; }
  .crm-filter-row { gap: 6px !important; }
  .crm-filter-row select { font-size: 11px !important; }
}
@media (max-width: 480px) {
  .crm-customer-header h2 { font-size: 16px !important; }
  .crm-tab-content { padding: 12px !important; }
}
`;

// Phase 23-25: AI helper. Wraps POST /api/ai/chat. Returns {text, usage, model}.
// Throws on non-2xx so callers get a single try/catch surface. Surfaces the
// 503 "no API key" case as a special error code so the UI can show a clearer
// message than the generic "AI call failed".
async function callAi(apiFetch, { system, prompt, maxTokens = 1024 }) {
  const res = await apiFetch('/ai/chat', {
    method: 'POST',
    body: JSON.stringify({ system, prompt, maxTokens }),
  });
  if (res.status === 503) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body?.detail || 'AI features are not configured');
    err.code = 'not-configured';
    throw err;
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`AI call failed: HTTP ${res.status} ${body.slice(0, 200)}`);
  }
  return res.json();
}

// Build a compact summary of a customer's recent activity for AI prompts.
// Keeps the prompt small enough to fit Phase 24/25's per-customer reasoning
// without blowing the input cap.
function summariseCustomerContext(data, customerCode, opts = {}) {
  const daysBack = opts.daysBack ?? 90;
  const cust = data.customers[customerCode];
  if (!cust) return '';
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - daysBack);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  const contacts = Object.values(data.contacts).filter(c => c.customer_code === customerCode);
  const activities = Object.values(data.activities)
    .filter(a => a.customer_code === customerCode && (a.date || '') >= cutoffStr)
    .sort((a, b) => cmpDesc(a.date, b.date))
    .slice(0, 30);
  const tasks = Object.values(data.tasks)
    .filter(t => t.customer_code === customerCode && t.status !== 'Done')
    .sort((a, b) => cmpAsc(a.due_date, b.due_date));

  const lines = [];
  lines.push(`Customer: ${cust.name} (${cust.code}) — segment: ${cust.segment}`);
  const owners = (cust.majans_owners || []).map(o => o?.name).filter(Boolean);
  if (owners.length) lines.push(`Majans owner${owners.length === 1 ? '' : 's'}: ${owners.join(', ')}`);
  if (cust.notes) lines.push(`Customer notes: ${cust.notes}`);
  lines.push('');
  lines.push(`Active contacts (${contacts.length}):`);
  for (const c of contacts.slice(0, 15)) {
    lines.push(`- ${contactName(c)} (${c.title || 'unknown role'})${c.active === false ? ' [inactive]' : ''}`);
  }
  lines.push('');
  lines.push(`Recent activities (last ${daysBack} days, max 30):`);
  for (const a of activities) {
    // 057: give the model the whole room and every follow-up, not one name and
    // one sentence — "who have we actually been dealing with" is a question it
    // gets asked and previously could not answer.
    const who = attendeeSummary(a, data.contacts);
    const by = a.created_by_name ? ` (logged by ${a.created_by_name})` : '';
    const actionTasks = nextActionTasks(data.tasks, a.id);
    let line = `- ${a.date} · ${a.type}${who ? ` with ${who}` : ''}${by} · ${a.subject}`;
    if (a.outcome) line += `  outcome: ${a.outcome}`;
    if (actionTasks.length > 0) line += `  next: ${actionTasks.map(t => `${t.title}${t.owner ? ` [${t.owner}]` : ''} (${t.status})`).join('; ')}`;
    else if (a.next_action) line += `  next: ${a.next_action}`;
    if (a.notes) line += `  notes: ${String(a.notes).slice(0, 300)}`;
    lines.push(line);
  }
  lines.push('');
  lines.push(`Open tasks (${tasks.length}):`);
  for (const t of tasks) {
    lines.push(`- ${t.due_date || '(no due date)'} · [${t.priority}] · ${t.title}${t.owner ? ` · owner ${t.owner}` : ''}${t.notes ? ' — ' + String(t.notes).slice(0, 200) : ''}`);
  }
  return lines.join('\n');
}

// Phase 23: per-customer AI summary card. Sits on the Overview tab.
// User clicks "Generate" -> backend calls Claude with a digest of
// activities/contacts/tasks for this customer -> renders the response.
// Cleared when user switches customer so we don't show stale.
function AiSummaryCard({ apiFetch, data, customerCode }) {
  const [state, setState] = useState({ status: 'idle', text: '', error: '', model: '' });
  useEffect(() => { setState({ status: 'idle', text: '', error: '', model: '' }); }, [customerCode]);

  const cust = data.customers[customerCode];
  if (!cust) return null;

  async function generate() {
    setState({ status: 'loading', text: '', error: '', model: '' });
    const context = summariseCustomerContext(data, customerCode, { daysBack: 90 });
    if (!context) { setState({ status: 'error', text: '', error: 'No customer context available', model: '' }); return; }
    try {
      const result = await callAi(apiFetch, {
        system: 'You are a commercial-team analyst at Majans, an Australian FMCG snack manufacturer (brands: Bhuja, Infuzions). Major retail customers include Woolworths, Coles, Costco, ALDI. Be concise, plain-language, factual. Never invent details that are not in the context provided.',
        prompt: `Summarise the last 90 days of activity on this customer in 3-5 short bullets. Group by theme (range review, promo, supply, NPD, etc). Call out the single most important next action at the end. Plain text only.\n\n${context}`,
        maxTokens: 800,
      });
      setState({ status: 'done', text: result.text, error: '', model: result.model });
    } catch (err) {
      setState({ status: 'error', text: '', error: err.message || 'unknown error', model: '' });
    }
  }

  return (
    <div style={{ background: 'var(--ai-soft)', border: '1px solid var(--ai-border)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ai-text)', textTransform: 'uppercase', letterSpacing: 0.5 }}>✨ AI summary</div>
          <div style={{ fontSize: 11, color: 'var(--ai-text)', opacity: 0.8, marginTop: 2 }}>Last 90 days of activity for {cust.short_name || cust.name}, summarised by Claude.</div>
        </div>
        <button onClick={generate} disabled={state.status === 'loading'}
          style={{ background: 'var(--ai)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: state.status === 'loading' ? 'wait' : 'pointer' }}>
          {state.status === 'loading' ? 'Thinking…' : state.text ? 'Regenerate' : 'Generate'}
        </button>
      </div>
      {state.error && (
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--danger)', background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 'var(--radius)', padding: '8px 10px' }}>
          {state.error}
        </div>
      )}
      {state.text && (
        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-body)', background: 'var(--bg-card)', border: '1px solid var(--ai-border)', borderRadius: 'var(--radius)', padding: '10px 12px', whiteSpace: 'pre-wrap' }}>
          {state.text}
          {state.model && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6, fontStyle: 'italic' }}>generated by {state.model}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Correspondence intelligence (migration 055) ──────────────────────────────
//
// Matthew Blake, 2026-08-04: mirror app-magic's sell-date correspondence summary
// onto the CRM. Same mechanism, same privacy boundary:
//
//   YOUR browser reads YOUR mailbox (delegated Graph Mail.Read), scoped to this
//   customer's contact addresses over a 6-month window. The API summarises and
//   stores the summary — never the email bodies — as YOUR contribution. Several
//   owners can each contribute from their own inbox; nobody reads anyone else's
//   mail. See the scope note in commercial-055-crm-correspondence.sql.
//
// Suggested tasks are PROPOSALS: nothing reaches commercial.CrmTask until someone
// clicks Add, which goes through the page's normal governed task write.

/** Re-summarise automatically when the signed-in user's own view is older than this. */
const CORRESPONDENCE_STALE_DAYS = 7;

function daysSince(iso) {
  if (!iso) return Infinity;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return Infinity;
  return (Date.now() - t) / 86_400_000;
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

function CorrespondenceCard({ apiFetch, customerCode, customer, contacts, tasks, activities, currentUserOid, onAddTask, onAddActivities }) {
  const queryClient = useQueryClient();
  // All transient state is STAMPED WITH THE CUSTOMER CODE it belongs to, rather
  // than reset by an effect when the code changes. Two reasons: an effect that
  // resets state cascades an extra render, and — the real bug — a summary that is
  // still running when the user clicks to another customer would otherwise show
  // its spinner, error or "no emails found" against the wrong account.
  const [busy, setBusy] = useState(null);            // { code, phase: 'mail'|'ai' }
  const [msg, setMsg] = useState(null);              // { code, notice, error }
  const [confirmClearFor, setConfirmClearFor] = useState(null);
  // Set when a run couldn't read the whole diary, so the card says so rather than
  // quietly presenting a partial result as the whole picture. { code, reason }
  const [calendarNoteFor, setCalendarNoteFor] = useState(null);
  // Customers this session has already auto-run (or failed to auto-run) for. Keyed
  // on the customer CODE, not a one-shot boolean — the key changes as the user
  // moves between customers, which is exactly the trap DR.jsx hit with useDbState.
  const autoRunRef = useRef(new Set());

  const { data: byCustomer, isLoading } = useQuery({
    queryKey: ['crm-correspondence'],
    queryFn: async () => {
      const res = await apiFetch('/crm-correspondence');
      if (!res.ok) throw new Error(`Failed to load correspondence summaries: HTTP ${res.status}`);
      return res.json();
    },
    staleTime: 60_000,
    retry: false,
  });

  const entries = useMemo(() => (byCustomer?.[customerCode] ?? []), [byCustomer, customerCode]);
  const mine = useMemo(() => entries.find(e => e.generated_by_oid === currentUserOid) || null, [entries, currentUserOid]);

  // Only active contacts with an address Graph can actually be asked about.
  const contactEmails = useMemo(
    () => contacts.filter(c => c.active !== false && isSafeContactEmail(c.email)).map(c => c.email.trim()),
    [contacts],
  );

  // Owners with nothing on the record yet — so an incomplete picture looks
  // incomplete instead of looking finished, and Matt can chase the right person.
  const ownersYetToContribute = useMemo(() => {
    const covered = new Set(entries.map(e => (e.generated_by_email || '').trim().toLowerCase()).filter(Boolean));
    return (customer?.majans_owners ?? [])
      .filter(o => (o?.email || '').trim() && !covered.has(o.email.trim().toLowerCase()))
      .map(o => o.name?.trim() || o.email.trim());
  }, [customer, entries]);

  // A suggestion already on the board shows as added rather than offering a
  // duplicate — survives a reload, unlike local "I clicked it" state.
  const existingTaskTitles = useMemo(
    () => new Set(tasks.map(t => (t.title || '').trim().toLowerCase()).filter(Boolean)),
    [tasks],
  );

  // Which proposed activities are already in the feed. Keyed on source_key, so
  // this survives a reload and stays right even if someone edits the subject.
  const loggedSourceKeys = useMemo(
    () => new Set((activities || []).map(a => a.source_key).filter(Boolean)),
    [activities],
  );

  // `code` and `emails` are passed in rather than read from the closure so an
  // auto-run that was scheduled for one customer can never post its result
  // against another.
  async function generate(code, emails) {
    if (emails.length === 0) return;
    setMsg(null);
    setBusy({ code, phase: 'mail' });
    try {
      // Calendar runs alongside mail and is allowed to come back null — it needs
      // a permission the shared app registration doesn't carry yet, and a missing
      // diary must never cost you the email summary.
      const [{ messages, windowFrom, windowTo }, cal] = await Promise.all([
        fetchCustomerCorrespondence(emails),
        fetchCustomerCalendar(emails).catch(() => null),
      ]);
      const events = cal?.events ?? [];
      setCalendarNoteFor(
        cal === null
          ? { code, reason: 'Calendar isn’t switched on for this app yet, so this run covered email only.' }
          : cal.truncated
            ? { code, reason: 'Your diary was too large to read in full, so some older meetings may be missing.' }
            : null,
      );
      if (messages.length === 0 && events.length === 0) {
        setMsg({ code, notice: `No emails or meetings with ${emails.length === 1 ? emails[0] : `the ${emails.length} contacts on this account`} in the last ${CORRESPONDENCE_LOOKBACK_MONTHS} months.`, error: '' });
        return;
      }
      setBusy({ code, phase: 'ai' });
      const res = await apiFetch(`/crm-correspondence/${encodeURIComponent(code)}`, {
        method: 'POST',
        body: JSON.stringify({ messages, events, calendarUnavailable: cal === null, windowFrom, windowTo }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        let detail = `HTTP ${res.status}`;
        try { const j = JSON.parse(body); detail = j.detail || j.error || detail; } catch { /* keep default */ }
        throw new Error(detail);
      }
      await queryClient.invalidateQueries({ queryKey: ['crm-correspondence'] });
    } catch (err) {
      // MSAL popup dismissed / consent declined comes back as user_cancelled.
      setMsg({ code, notice: '', error: err?.errorCode === 'user_cancelled'
        ? 'Mailbox access declined. The summary needs one-time permission to read your own mail — click Summarise again to retry.'
        : (err.message || 'Summary failed') });
    } finally {
      setBusy(b => (b?.code === code ? null : b));
    }
  }

  async function clearMine() {
    const code = customerCode;
    setMsg(null);
    try {
      const res = await apiFetch(`/crm-correspondence/${encodeURIComponent(code)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setConfirmClearFor(null);
      await queryClient.invalidateQueries({ queryKey: ['crm-correspondence'] });
    } catch (err) {
      setMsg({ code, notice: '', error: err.message || 'Clear failed' });
    }
  }

  // Auto-refresh when this user's view is missing or stale. Guarded four ways so
  // it can neither loop nor quietly burn tokens or mailbox reads: once per
  // customer per session (the ref), only when there is something to search, only
  // when the existing view is actually stale, and behind a short timer that is
  // cancelled on unmount or customer switch — so clicking down the customer list
  // doesn't fire a Graph query per account passed through. A failure still marks
  // the customer attempted, so an error doesn't retry on every render.
  useEffect(() => {
    if (isLoading || !byCustomer || !customerCode) return;
    if (contactEmails.length === 0) return;
    if (autoRunRef.current.has(customerCode)) return;
    if (mine && daysSince(mine.generated_at) < CORRESPONDENCE_STALE_DAYS) return;
    const code = customerCode;
    const emails = contactEmails;
    const timer = setTimeout(() => {
      autoRunRef.current.add(code);
      generate(code, emails);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCode, isLoading, byCustomer, contactEmails, mine]);

  // Transient state only renders for the customer it was recorded against.
  const busyPhase = busy?.code === customerCode ? busy.phase : null;
  const notice = msg?.code === customerCode ? msg.notice : '';
  const error = msg?.code === customerCode ? msg.error : '';
  const confirmClear = confirmClearFor === customerCode;
  const calendarNote = calendarNoteFor?.code === customerCode ? calendarNoteFor.reason : '';
  const busyLabel = busyPhase === 'mail' ? 'Reading your mail…' : busyPhase === 'ai' ? 'Summarising…' : null;

  return (
    <div style={{ background: 'var(--ai-soft)', border: '1px solid var(--ai-border)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ai-text)', textTransform: 'uppercase', letterSpacing: 0.5 }}>✉️ Correspondence summary</div>
          <div style={{ fontSize: 11, color: 'var(--ai-text)', opacity: 0.8, marginTop: 2 }}>
            {contactEmails.length === 0
              ? 'Add a contact with an email address to summarise your correspondence with this account.'
              : `Reads your own mailbox and diary for the ${contactEmails.length} contact${contactEmails.length === 1 ? '' : 's'} on this account, and offers what it finds for Recent Activity — it never touches anyone else's mail.`}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ai-text)', opacity: 0.8, marginTop: 2 }}>
            {calendarNote}
          </div>
        </div>
        <button onClick={() => generate(customerCode, contactEmails)} disabled={!!busyPhase || contactEmails.length === 0}
          style={{ background: 'var(--ai)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: busyPhase ? 'wait' : 'pointer', opacity: contactEmails.length === 0 ? 0.5 : 1 }}>
          {busyLabel || (mine ? 'Update my summary' : 'Summarise my correspondence')}
        </button>
      </div>

      {notice && <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>{notice}</div>}
      {error && (
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--danger)', background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 'var(--radius)', padding: '8px 10px' }}>
          {error}
        </div>
      )}

      {entries.map(e => {
        const isMine = e.generated_by_oid === currentUserOid;
        return (
          <div key={e.generated_by_oid} style={{ marginTop: 10, background: 'var(--bg-card)', border: '1px solid var(--ai-border)', borderRadius: 'var(--radius)', padding: '10px 12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)' }}>
                {e.generated_by_name || e.generated_by_email || 'Unknown'}
                {isMine && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> (you)</span>}
              </div>
              {/* You clear your own entry only — a shared account must not let one
                  person wipe a colleague's contribution. */}
              {isMine && (confirmClear ? (
                <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: 'var(--danger)' }}>Clear yours?</span>
                  <Button onClick={() => setConfirmClearFor(null)} size="sm" style={{ fontSize: 10, padding: '2px 7px' }}>Cancel</Button>
                  <Button onClick={clearMine} variant="destructive" size="sm" style={{ fontSize: 10, padding: '2px 7px' }}>Confirm</Button>
                </span>
              ) : (
                <Button onClick={() => setConfirmClearFor(customerCode)} size="sm" style={{ fontSize: 10, padding: '2px 7px' }}>Clear</Button>
              ))}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-body)', whiteSpace: 'pre-wrap' }}>{e.summary}</div>

            {/* Proposed Recent Activity entries (Matthew, 2026-08-04). One per
                email THREAD and one per meeting — not one per email, which would
                bury the feed. Nothing is logged until someone clicks. */}
            {isMine && (e.suggested_activities?.length ?? 0) > 0 && (() => {
              const pending = e.suggested_activities.filter(a => !loggedSourceKeys.has(a.sourceKey));
              return (
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      For Recent Activity — {pending.length} to add{pending.length !== e.suggested_activities.length ? `, ${e.suggested_activities.length - pending.length} already logged` : ''}
                    </div>
                    {pending.length > 0 && (
                      <Button onClick={() => onAddActivities(pending)} size="sm" style={{ fontSize: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                        + Add all {pending.length}
                      </Button>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {e.suggested_activities.map(a => {
                      const logged = loggedSourceKeys.has(a.sourceKey);
                      return (
                        <div key={a.sourceKey} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', justifyContent: 'space-between', opacity: logged ? 0.55 : 1 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>
                              <span style={{ marginRight: 6 }}>{ACT_ICON[a.type] || '📝'}</span>{a.subject}
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
                              {[a.date, a.type, a.sourceType === 'calendar' ? 'from your calendar' : 'from your email', a.contactEmail, a.outcome].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                          {logged
                            ? <Badge text="Logged" color="var(--success)" />
                            : <Button onClick={() => onAddActivities([a])} size="sm" style={{ fontSize: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>+ Add</Button>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {isMine && (e.suggested_tasks?.length ?? 0) > 0 && (
              <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  Suggested follow-ups — review before adding
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {e.suggested_tasks.map((t, i) => {
                    const added = existingTaskTitles.has((t.title || '').trim().toLowerCase());
                    return (
                      <div key={`${t.title}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>{t.title}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
                            {[t.priority, t.due_date ? `due ${t.due_date}` : null, t.notes].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        {added
                          ? <Badge text="Added" color="var(--success)" />
                          : <Button onClick={() => onAddTask(t)} size="sm" style={{ fontSize: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>+ Add task</Button>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Retrieval is keyed on the contacts only, so the transcript is wider
                than this relationship. Show the in-scope count where we have it,
                so 3-of-14 can't read as 14-of-14. */}
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 8, fontStyle: 'italic' }}>
              {`AI-generated from ${e.relevant_count != null ? `${e.relevant_count} of ${e.message_count} emails relevant to this account` : `${e.message_count} email${e.message_count === 1 ? '' : 's'}`} in ${e.generated_by_name || 'their'}${(e.generated_by_name || '').endsWith('s') ? "'" : "'s"} mailbox, ${fmtDate(e.window_from)} – ${fmtDate(e.window_to)} · generated ${fmtDate(e.generated_at)}${e.model ? ` by ${e.model}` : ''}`}
            </div>
          </div>
        );
      })}

      {ownersYetToContribute.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
          Not yet added: {ownersYetToContribute.join(', ')} — each owner runs this from their own mailbox.
        </div>
      )}
    </div>
  );
}

// Majans-side owners on a customer (migration 055). The analogue of app-magic's
// ContactListEditor. Recording an owner does NOT grant access to their mailbox —
// it tells the team whose inbox the correspondence is expected to live in.
function OwnerListEditor({ value, onChange }) {
  const rows = Array.isArray(value) ? value : [];
  const update = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={LB}>Majans owner(s) for this account</label>
      {rows.map((r, i) => (
        <div key={r.id || i} style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 0.8fr auto', gap: 6, marginBottom: 6 }}>
          <input style={IS} value={r.name || ''} onChange={e => update(i, { name: e.target.value })} placeholder="Name" aria-label={`Owner ${i + 1} name`} />
          <input style={IS} value={r.email || ''} onChange={e => update(i, { email: e.target.value })} placeholder="name@majans.com" aria-label={`Owner ${i + 1} email`} />
          <input style={IS} value={r.role || ''} onChange={e => update(i, { role: e.target.value })} placeholder="Role / cover" aria-label={`Owner ${i + 1} role`} />
          <Button onClick={() => onChange(rows.filter((_, j) => j !== i))} variant="destructive" size="sm" style={{ fontSize: 10, padding: '2px 8px' }}>×</Button>
        </div>
      ))}
      <Button onClick={() => onChange([...rows, { id: uid(), name: '', email: '', role: '' }])} size="sm" style={{ fontSize: 11 }}>+ Add owner</Button>
    </div>
  );
}

/* ─── 057: Attendee picker ───────────────────────────────────────────────────
 * Who was in the room, both sides.
 *
 * The customer side is a toggle list over this customer's real CrmContacts, so
 * an attendee normally carries a contact_id and stays joined to the person
 * record. "+ someone not on file" exists because meetings routinely include a
 * buyer's colleague we have never logged, and making people create a contact
 * record first is exactly how you train them to leave the field blank instead.
 *
 * The Majans side is free text with suggestions rather than a directory lookup.
 * The suggestions are built from people already present in the data — this
 * account's owners, anyone who has logged an activity or task, and you — which
 * needs no new Graph permission on the app registration SHARED with app-magic.
 * Same self-populating idea as the Governance user picker: no name is hardcoded.
 */
function AttendeePicker({ value, onChange, contacts, staffSuggestions = [], currentUserName = '' }) {
  const rows = Array.isArray(value) ? value : [];
  const linkedIds = new Set(rows.filter(r => r.contact_id).map(r => r.contact_id));
  const freeCustomer = rows.filter(r => r.side === 'customer' && !r.contact_id);
  const majans = rows.filter(r => r.side === 'majans');
  const replace = (row, patch) => onChange(rows.map(r => (r.id === row.id ? { ...r, ...patch } : r)));
  const remove = row => onChange(rows.filter(r => r.id !== row.id));
  const add = row => onChange([...rows, row]);

  const toggleContact = (c) => {
    if (linkedIds.has(c.id)) onChange(rows.filter(r => r.contact_id !== c.id));
    else add({ id: uid(), side: 'customer', contact_id: c.id, name: contactName(c), email: c.email || '', title: c.title || '' });
  };
  const meAlreadyIn = majans.some(r => (r.name || '').trim().toLowerCase() === currentUserName.trim().toLowerCase());

  return (
    <div style={{ marginBottom: 14 }}>
      <label style={LB}>Customer attendees</label>
      {contacts.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>No contacts on file for this customer yet.</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
          {contacts.map(c => {
            const on = linkedIds.has(c.id);
            return (
              <button key={c.id} type="button" onClick={() => toggleContact(c)}
                aria-pressed={on}
                title={c.title || ''}
                style={{ background: on ? 'var(--info-soft, var(--bg-elevated))' : 'var(--bg-elevated)', border: `1px solid ${on ? 'var(--info)' : 'var(--border-soft)'}`, color: on ? 'var(--info)' : 'var(--text-body)', borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: on ? 700 : 500, cursor: 'pointer' }}>
                {on ? '✓ ' : '+ '}{contactName(c)}
              </button>
            );
          })}
        </div>
      )}
      {freeCustomer.map(r => (
        <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr auto', gap: 6, marginBottom: 6 }}>
          <input style={IS} value={r.name || ''} onChange={e => replace(r, { name: e.target.value })} placeholder="Name" aria-label="Customer attendee name" />
          <input style={IS} value={r.email || ''} onChange={e => replace(r, { email: e.target.value })} placeholder="Email (optional)" aria-label="Customer attendee email" />
          <Button onClick={() => remove(r)} variant="destructive" size="sm" style={{ fontSize: 10, padding: '2px 8px' }}>×</Button>
        </div>
      ))}
      <Button onClick={() => add({ id: uid(), side: 'customer', contact_id: '', name: '', email: '', title: '' })} size="sm" style={{ fontSize: 11 }}>
        + Someone not on file
      </Button>

      <label style={{ ...LB, marginTop: 12 }}>Majans attendees</label>
      <datalist id="crm-majans-staff">
        {staffSuggestions.map(n => <option key={n} value={n} />)}
      </datalist>
      {majans.map(r => (
        <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6, marginBottom: 6 }}>
          <input style={IS} list="crm-majans-staff" value={r.name || ''} onChange={e => replace(r, { name: e.target.value })}
            placeholder="Start typing a name…" aria-label="Majans attendee name" />
          <Button onClick={() => remove(r)} variant="destructive" size="sm" style={{ fontSize: 10, padding: '2px 8px' }}>×</Button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6 }}>
        <Button onClick={() => add({ id: uid(), side: 'majans', contact_id: '', name: '', email: '', title: '' })} size="sm" style={{ fontSize: 11 }}>+ Add Majans attendee</Button>
        {currentUserName && !meAlreadyIn && (
          <Button onClick={() => add({ id: uid(), side: 'majans', contact_id: '', name: currentUserName, email: '', title: '' })} size="sm" style={{ fontSize: 11 }}>+ Me</Button>
        )}
      </div>
    </div>
  );
}

/* ─── 057: Next actions ──────────────────────────────────────────────────────
 * Matthew's ask was multiple next actions that "then also turn into a Task", so
 * each row here IS a task — stored once, in commercial.CrmTask, linked back to
 * this activity. Nothing writes the action text onto the activity as well; two
 * copies would drift the first time someone edited one of them.
 *
 * The "Task" tick is the opt-out: some next actions are genuinely just a note
 * ("await their reply"), and forcing those into the Tasks tab is how a task list
 * stops being read. Unticking a row whose task is still Open removes that task;
 * a task already in progress or done is left alone — see saveActivity.
 */
function NextActionsEditor({ value, onChange, ownerOptions = [] }) {
  const rows = Array.isArray(value) ? value : [];
  const update = (row, patch) => onChange(rows.map(r => (r.id === row.id ? { ...r, ...patch } : r)));
  const remove = row => onChange(rows.filter(r => r.id !== row.id));
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={LB}>Next actions {rows.length > 0 && <span style={{ color: 'var(--text-muted)' }}>({rows.length})</span>}</label>
      <datalist id="crm-action-owners">
        {ownerOptions.map(n => <option key={n} value={n} />)}
      </datalist>
      {rows.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>No follow-ups yet — add one and it becomes a task.</div>
      )}
      {rows.map(r => (
        <div key={r.id} style={{ border: '1px solid var(--border-soft)', borderRadius: 6, padding: 8, marginBottom: 6, background: 'var(--bg-elevated)' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input style={IS} value={r.text || ''} onChange={e => update(r, { text: e.target.value })}
              placeholder="What needs to happen next?" aria-label="Next action" />
            <Button onClick={() => remove(r)} variant="destructive" size="sm" style={{ fontSize: 10, padding: '2px 8px' }}>×</Button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 0.9fr auto', gap: 6, alignItems: 'center' }}>
            <input style={{ ...IS, fontSize: 11 }} list="crm-action-owners" value={r.owner || ''}
              onChange={e => update(r, { owner: e.target.value })} placeholder="Owner" aria-label="Next action owner" />
            <input style={{ ...IS, fontSize: 11 }} type="date" value={r.due_date || ''}
              onChange={e => update(r, { due_date: e.target.value })} aria-label="Next action due date" />
            <select style={{ ...IS, fontSize: 11 }} value={r.priority || 'Medium'}
              onChange={e => update(r, { priority: e.target.value })} aria-label="Next action priority">
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={r.make_task !== false} onChange={e => update(r, { make_task: e.target.checked })} style={{ width: 14, height: 14, cursor: 'pointer' }} />
              Task
            </label>
          </div>
          {r.task_id && r.status && r.status !== 'Open' && (
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 5 }}>
              Linked task is <strong>{r.status}</strong> — removing this row will leave it in the Tasks tab.
            </div>
          )}
        </div>
      ))}
      <Button onClick={() => onChange([...rows, { id: uid(), text: '', owner: '', due_date: '', priority: 'Medium', make_task: true, task_id: '' }])} size="sm" style={{ fontSize: 11 }}>
        + Add next action
      </Button>
    </div>
  );
}

/* Seed the editor when an activity is opened. Linked tasks are the truth once
 * they exist; before that, a pre-057 activity's single free-text Next Action
 * becomes row 1, so simply opening and saving an old activity converts it. */
function seedNextActions(activity, linkedTasks) {
  if (linkedTasks && linkedTasks.length > 0) {
    return linkedTasks.map(t => ({
      id: uid(), text: t.title || '', owner: t.owner || '', due_date: t.due_date || '',
      priority: t.priority || 'Medium', make_task: true, task_id: t.id, status: t.status,
    }));
  }
  const legacy = (activity?.next_action || '').trim();
  return legacy ? [{ id: uid(), text: legacy, owner: '', due_date: '', priority: 'Medium', make_task: true, task_id: '' }] : [];
}

// Phase 22: lightweight attachments stored inline as base64 inside the
// activity entity's `attachments` array. NO Azure Blob Storage — the
// per-team blob is the only persistence layer for v1. Hard limits below
// keep us safely under the 5MB TeamCrmData cap even if every activity
// hits its max.
const ATTACHMENT_MAX_BYTES_PER_FILE = 500 * 1024;        // 500 KB per file
const ATTACHMENT_MAX_PER_ACTIVITY  = 5;                  // max 5 files per activity
const ATTACHMENT_TOTAL_WARN_BYTES  = 4_000_000;          // warn at 4MB total blob (5MB cap)

function fileSizeHuman(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Read a File object into a base64 string. We use `readAsDataURL` and
// strip the prefix because raw base64 is cheaper to JSON-serialise than
// the data: URL.
function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error || new Error('FileReader failed'));
    r.onload = () => {
      const result = r.result || '';
      const i = result.indexOf(',');
      resolve(i >= 0 ? result.slice(i + 1) : result);
    };
    r.readAsDataURL(file);
  });
}

// Download an attachment by reconstructing the data: URL and clicking
// a synthetic anchor. Same pattern as exportData() but for binary.
function downloadAttachment(att) {
  const url = `data:${att.mime || 'application/octet-stream'};base64,${att.data}`;
  const a = document.createElement('a');
  a.href = url;
  a.download = att.name || 'attachment';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// Inline attachment chips: shown in activity form (editable) and in the
// activity card (read-only). The form variant takes `onChange` to mutate
// the list; the card variant omits it and renders chips with no remove
// button.
function AttachmentChips({ attachments, onChange, onWarnSize }) {
  const list = attachments || [];
  const totalBytes = list.reduce((s, a) => s + (a.size || 0), 0);
  const editable = !!onChange;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {list.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {list.map((a, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 4, padding: '3px 8px', fontSize: 11, color: 'var(--text-body)' }}>
              <button onClick={(e) => { e.preventDefault(); downloadAttachment(a); }}
                title={`Download ${a.name}`}
                style={{ background: 'none', border: 'none', color: 'var(--info)', cursor: 'pointer', padding: 0, fontSize: 11, textDecoration: 'underline' }}>
                ⇩ {a.name}
              </button>
              <span style={{ color: 'var(--text-muted)' }}>· {fileSizeHuman(a.size || 0)}</span>
              {editable && (
                <button onClick={(e) => { e.preventDefault(); onChange(list.filter((_, j) => j !== i)); }}
                  title="Remove attachment"
                  style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: 0, fontSize: 12 }}>
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {editable && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ background: 'var(--border)', color: 'var(--text-body)', border: '1px solid var(--border-soft)', borderRadius: 'var(--radius)', display: 'inline-block', cursor: 'pointer', padding: '4px 10px', fontSize: 11 }}>
            + Attach file
            <input type="file" multiple style={{ display: 'none' }}
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                e.target.value = '';
                let next = [...list];
                const errs = [];
                for (const f of files) {
                  if (next.length >= ATTACHMENT_MAX_PER_ACTIVITY) {
                    errs.push(`Cap reached (${ATTACHMENT_MAX_PER_ACTIVITY} files per activity).`);
                    break;
                  }
                  if (f.size > ATTACHMENT_MAX_BYTES_PER_FILE) {
                    errs.push(`${f.name} is ${fileSizeHuman(f.size)} — over the ${fileSizeHuman(ATTACHMENT_MAX_BYTES_PER_FILE)} per-file cap.`);
                    continue;
                  }
                  try {
                    const data = await readFileAsBase64(f);
                    next.push({ name: f.name, size: f.size, mime: f.type, data });
                  } catch (err) {
                    errs.push(`${f.name}: ${err.message}`);
                  }
                }
                if (errs.length > 0) alert(`Some files couldn't be attached:\n· ${errs.join('\n· ')}`);
                onChange(next);
                if (onWarnSize) onWarnSize(next.reduce((s, a) => s + (a.size || 0), 0));
              }} />
          </label>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
            {list.length}/{ATTACHMENT_MAX_PER_ACTIVITY} files · {fileSizeHuman(totalBytes)} of {fileSizeHuman(ATTACHMENT_MAX_PER_ACTIVITY * ATTACHMENT_MAX_BYTES_PER_FILE)}
          </span>
        </div>
      )}
    </div>
  );
}

// Phase 21: starter pack of activity templates. Hardcoded for v1 — v2 can
// promote these to commercial.JsonStore key 'crm-templates' for shared
// editing. The frontend lookup is by template `key` so renaming labels won't
// break stored references (when v2 adds custom user templates).
const ACTIVITY_TEMPLATES = [
  {
    key: 'range-review-proposal',
    label: 'Range review proposal',
    type: 'Email',
    subject: 'Range review proposal — [Customer]',
    body: 'Hi [Buyer],\n\nFollowing on from our discussion, please find below our range review proposal:\n\n· Investment: [details]\n· Pricing: [details]\n· Promotional support: [details]\n\nKey assumptions:\n· [assumption 1]\n· [assumption 2]\n\nHappy to walk through on a call when suits. Targeting a decision by [date].\n\nThanks,\n[Your name]',
  },
  {
    key: 'post-meeting-recap',
    label: 'Post-meeting recap',
    type: 'Email',
    subject: 'Recap — [Meeting topic] — [Date]',
    body: 'Hi [Attendees],\n\nThanks for the time today. Quick recap of what we covered:\n\nWhat we discussed:\n· [topic 1]\n· [topic 2]\n\nWhat we agreed:\n· [decision 1]\n· [decision 2]\n\nNext steps:\n· [action 1] — owner: [name], by [date]\n· [action 2] — owner: [name], by [date]\n\nLet me know if I missed anything.\n\nThanks,\n[Your name]',
  },
  {
    key: 'bpv-new-line-setup',
    label: 'BPV new-line setup',
    type: 'Email',
    subject: 'BPV new-line setup — [SKU] — for submission',
    body: 'Hi [Buyer / BPV team],\n\nPlease find attached the BPV new-line setup form for [SKU description].\n\nKey details:\n· GTIN: [13-digit]\n· Pallet config: [Ti×Hi]\n· Wholesale price: $[X.XX]\n· On-shelf date target: [date]\n\nAll supporting docs (artwork, NPS, nutrition) attached / linked.\n\nAny questions, let me know.\n\nThanks,\n[Your name]',
  },
  {
    key: 'mlor-clearance',
    label: 'MLOR clearance request',
    type: 'Email',
    subject: 'MLOR assist — PO [number] — [SKU]',
    body: 'Hi [Buyer / Supply Chain],\n\nFlagging stock under MLOR on PO [number]:\n\n· SKU: [description]\n· Quantity: [N] cartons\n· UBD: [date]\n· DC destination: [state / DC code]\n\nReason: [over-forecast / late shipment / etc].\n\nRequesting acceptance under reduced MLOR — confident the stock will clear in normal sell-through window.\n\nThanks,\n[Your name]',
  },
  {
    key: 'business-review-agenda',
    label: 'Business review agenda',
    type: 'Meeting',
    subject: 'Business Review — [Customer] — [Date]',
    body: 'Business Review with [Buyer] · [Date] · [Location / Teams]\n\nAgenda:\n1. Performance vs last review (volume + value, both brands)\n2. Current promo program — what worked, what didn\'t\n3. Range health — distribution gaps, ranged-but-not-stocked\n4. Innovation pipeline (next 6 months)\n5. Joint business priorities for the quarter\n6. AOB\n\nMaterials to send pre-meeting:\n· Scorecard\n· Promo effectiveness deck\n· Range gap analysis',
  },
];

// Phase 20: render the per-entity "edited by Hayley · 3d ago" caption when
// the entity carries updated_by_name + updated_at metadata. Tiny — sits at
// the bottom of cards. Falls back to created_by_name if only created_at is
// known (legacy entries pre-Phase-20).
function EditedCaption({ entity }) {
  const by = entity?.updated_by_name || entity?.created_by_name;
  const at = entity?.updated_at;
  if (!by && !at) return null;
  const rel = at ? formatRelativeTime(new Date(at)) : '';
  const text = by && rel ? `${by} · ${rel}` : (by || rel);
  return (
    <div title={at ? new Date(at).toLocaleString() : undefined}
      style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6, fontStyle: 'italic', opacity: 0.75 }}>
      edited by {text}
    </div>
  );
}

// Phase 19: parse @[Name] mentions and render with a highlight. We accept
// both `@[Full Name]` (explicit brackets when the name contains spaces) and
// `@Name` (single token without spaces). NOTE: this is v1 — purely visual.
// External notifications (Teams ping, email) need Graph permissions + user-
// directory mapping and are deferred until that's in place.
const MENTION_RE = /@\[([^\]]+)\]|@([A-Za-z][\w'.-]*)/g;
function RenderWithMentions({ text }) {
  if (!text) return null;
  const parts = [];
  let lastIndex = 0;
  let m;
  // Reset the global regex's lastIndex between calls — required for /g regex.
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const name = m[1] || m[2];
    parts.push(<span key={parts.length} style={{ color: 'var(--info)', background: 'var(--info-soft)', borderRadius: 'var(--radius-sm)', padding: '0 4px', fontWeight: 600 }}>@{name}</span>);
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}

// CSV: quote fields containing commas, quotes, or newlines; escape quotes by doubling.
function csvCell(v) {
  if (v == null) return '';
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function toCsv(rows, columns) {
  const header = columns.map(c => csvCell(c.label)).join(',');
  const body = rows.map(r => columns.map(c => csvCell(c.get(r))).join(',')).join('\r\n');
  return `${header}\r\n${body}\r\n`;
}
// Trigger a browser download. Uses BOM so Excel opens UTF-8 with the right codepage.
function downloadFile(filename, content, mime) {
  const blob = new Blob([mime?.startsWith('text/csv') ? '﻿' + content : content], { type: mime || 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function contactInitials(c) {
  const f = (c.first_name || c.name || '').trim();
  const l = (c.last_name || '').trim();
  return ((f[0] || '') + (l[0] || '')).toUpperCase() || '?';
}
const AVATAR_COLORS = ['#1d4ed8','#0369a1','#0e7490','#0f766e','#15803d','#4d7c0f','#b45309','#b91c1c','#9333ea','#c2410c','#0284c7','#7c3aed'];
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function ContactAvatar({ contact, size = 36 }) {
  const name = contactName(contact);
  const [imgErr, setImgErr] = useState(false);
  if (contact.photo_url && !imgErr) {
    return (
      <img
        src={contact.photo_url}
        alt={name}
        onError={() => setImgErr(true)}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid var(--border-soft)' }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: avatarColor(name), display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 700, color: '#fff', letterSpacing: 0.5,
    }}>
      {contactInitials(contact)}
    </div>
  );
}

// Default state when neither DB nor localStorage has data.
const DEFAULT_CRM = () => ({
  customers: { ...SEED_CUSTOMERS },
  contacts: { ...SEED_CONTACTS },
  activities: { ...SEED_ACTIVITIES },
  tasks: {},
});

// Migration helper: read legacy localStorage state and merge with seeds.
// Used ONCE on first API load if the user has no DB row yet.
function loadLegacyCRM() {
  try {
    const s = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (s) {
      const d = JSON.parse(s);
      // Remove legacy NDC entry — merged into CUS0044
      if (d.customers['CUS0183']) {
        delete d.customers['CUS0183'];
      }
      // Merge any new seed customers not yet stored
      for (const [code, cust] of Object.entries(SEED_CUSTOMERS)) {
        if (!d.customers[code]) d.customers[code] = cust;
        // Patch name if it still shows old RDC label
        else if (d.customers[code].short_name === 'Woolworths (RDC)') {
          d.customers[code].short_name = 'Woolworths';
          d.customers[code].name = 'Woolworths';
        }
      }
      // Merge any new seed contacts not yet stored
      if (!d.contacts) d.contacts = {};
      for (const [id, contact] of Object.entries(SEED_CONTACTS)) {
        if (!d.contacts[id]) {
          d.contacts[id] = contact;
        } else {
          // Patch existing contacts: split legacy `name` → first_name/last_name, add active field
          const c = d.contacts[id];
          if (!c.first_name && c.name) {
            const spaceIdx = c.name.indexOf(' ');
            c.first_name = spaceIdx > 0 ? c.name.slice(0, spaceIdx) : c.name;
            c.last_name  = spaceIdx > 0 ? c.name.slice(spaceIdx + 1) : '';
          }
          if (c.active === undefined) c.active = true;
        }
      }
      // Migrate any user-created contacts with legacy `name` field
      for (const c of Object.values(d.contacts)) {
        if (!c.first_name && c.name) {
          const spaceIdx = c.name.indexOf(' ');
          c.first_name = spaceIdx > 0 ? c.name.slice(0, spaceIdx) : c.name;
          c.last_name  = spaceIdx > 0 ? c.name.slice(spaceIdx + 1) : '';
        }
        if (c.active === undefined) c.active = true;
      }
      // Default is_current to true for any customer missing the field
      for (const code of Object.keys(d.customers)) {
        if (d.customers[code].is_current === undefined) d.customers[code].is_current = true;
      }
      // Merge any new seed activities not yet stored
      if (!d.activities) d.activities = {};
      for (const [id, act] of Object.entries(SEED_ACTIVITIES)) {
        if (!d.activities[id]) d.activities[id] = act;
      }
      return d;
    }
  } catch { /* ignore */ }
  return null; // No legacy data — caller falls back to DEFAULT_CRM
}

// ─── Shared styles ─────────────────────────────────────────────────────────────
const IS = { background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13, padding: '7px 10px', width: '100%', boxSizing: 'border-box' };
const TA = { ...IS, resize: 'vertical', minHeight: 72, fontFamily: 'inherit' };
const LB = { color: 'var(--text-muted)', fontSize: 11, marginBottom: 4, display: 'block' };
// Buttons formerly defined here as BTN / BTNP / BTND inline style objects.
// All call sites now use <Button variant="default|primary|destructive|ai-soft">
// from ../components/ui. Drop these consts when no caller uses them.

const PRIORITY_COLOR = { High: 'var(--danger)', Medium: 'var(--warn)', Low: 'var(--text-muted)' };
const STATUS_COLOR   = { Open: 'var(--info)', 'In Progress': 'var(--warn)', Done: 'var(--success)' };
const ACT_ICON       = { Meeting: '📅', Call: '📞', Email: '✉', 'Site Visit': '🏪', 'Video Call': '💻', 'Trade Show': '🎪', 'Range Review': '📊', 'Promo Discussion': '🏷', 'JBP Session': '🤝', Other: '📝' };

// Badge now imported from ../components/ui — the shared primitive accepts the
// same { text, color, bg } props (with optional `variant` for semantic shortcuts)
// and unifies the 14% bg / 30% border treatment used here and in NewBusiness.jsx.

/* ─── Form drafts (Matthew, 2026-09-17) ──────────────────────────────────────
 * "When entering a new contact, activity or task and you accidently click out of
 * the box prior to finishing, all the details disappear."
 *
 * Two halves. The form mirrors what you have typed into localStorage as you type,
 * and the Modal stops treating a stray backdrop click or Escape as "throw it away"
 * — it asks. Browser-local on purpose: a half-typed form is one person's private
 * scratch, it must never reach the shared board, and it must not be visible to a
 * colleague. Drafts older than 14 days are dropped when the form next opens.
 */
const DRAFT_PREFIX = 'crm-draft/';
const DRAFT_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

// Key is stable per (kind, customer, record) so editing two different contacts
// keeps two independent drafts, and a new one never collides with an edit.
function draftKey(kind, customerCode, itemId) {
  return `${DRAFT_PREFIX}${kind}/${customerCode || '-'}/${itemId || 'new'}`;
}
function readDraft(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { at, value } = JSON.parse(raw);
    if (!at || !value || Date.now() - at > DRAFT_MAX_AGE_MS) { localStorage.removeItem(key); return null; }
    return value;
  } catch { return null; }          // private mode / corrupt entry — behave as "no draft"
}
function writeDraft(key, value) {
  try { localStorage.setItem(key, JSON.stringify({ at: Date.now(), value })); } catch { /* quota or private mode */ }
}
function clearDraft(key) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/**
 * Form state that survives the modal being dismissed.
 *
 * Returns `[value, setValue, draft]`, where draft carries:
 *   restored — the form opened on a saved draft rather than a clean slate
 *   dirty    — value differs from what the form would hold with no draft
 *   discard  — drop the draft AND reset the form (the "option to delete")
 *   clear    — forget the draft, leave the form alone (after a successful save)
 *
 * Dirty is measured against the baseline, not against "has the user typed", so
 * opening a form and closing it untouched never leaves a draft behind.
 */
function useDraftedForm(key, makeBaseline) {
  // State, not a ref: computed once by the lazy initialiser and never reassigned,
  // so it is readable during render.
  const [baseline] = useState(makeBaseline);
  const [restored, setRestored] = useState(() => readDraft(key) !== null);
  const [value, setValue] = useState(() => {
    const d = readDraft(key);
    // Spread over the baseline, not instead of it: a draft written before a field
    // was added must not leave that field undefined.
    return d ? { ...baseline, ...d } : baseline;
  });

  const dirty = JSON.stringify(value) !== JSON.stringify(baseline);

  useEffect(() => {
    if (!dirty) { clearDraft(key); return; }
    const t = setTimeout(() => writeDraft(key, value), 500);   // debounced — not per keystroke
    return () => clearTimeout(t);
  }, [key, value, dirty]);

  // Stable identities: the Modal registration effect below depends on `discard`,
  // and a fresh closure each render would re-register on every keystroke.
  const discard = useCallback(() => {
    clearDraft(key);
    setValue(baseline);
    setRestored(false);
  }, [key, baseline]);
  const clear = useCallback(() => { clearDraft(key); setRestored(false); }, [key]);

  return [value, setValue, { restored, dirty, discard, clear }];
}

// How a form tells the Modal it is holding unsaved work. Context rather than props
// because the form is the Modal's `children` — the state lives in the child.
const ModalDraftContext = createContext(null);

function useModalDraft(draft) {
  const register = useContext(ModalDraftContext);
  const { dirty, discard } = draft;
  useEffect(() => {
    if (!register) return undefined;
    register({ dirty, discard });
    return () => register({ dirty: false, discard: null });
  }, [register, dirty, discard]);
}

// The banner a restored draft shows, with Matthew's "option to delete".
function DraftBanner({ draft }) {
  if (!draft.restored) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 11, color: 'var(--text-body)', background: 'var(--info-soft)', border: '1px solid var(--info-border)', borderRadius: 6, padding: '6px 10px', marginBottom: 12 }}>
      <span>Unsaved draft restored — picking up where you left off.</span>
      <button type="button" onClick={draft.discard}
        style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 11, cursor: 'pointer', padding: 0, whiteSpace: 'nowrap' }}>
        Discard draft
      </button>
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  // A dismiss used to be unconditional, which is exactly how a half-typed record
  // got thrown away. Now every dismiss route goes through attemptClose: clean form
  // closes immediately, dirty form asks first and the draft is already saved either way.
  const [draftState, setDraftState] = useState({ dirty: false, discard: null });
  const [confirming, setConfirming] = useState(false);
  const attemptClose = useCallback(
    () => (draftState.dirty ? setConfirming(true) : onClose()),
    [draftState.dirty, onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') attemptClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attemptClose]);

  return (
    <div onClick={attemptClose} role="dialog" aria-modal="true"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, width: wide ? 680 : 520, maxWidth: '95vw', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 16 }}>{title}</span>
          <button onClick={attemptClose} aria-label="Close" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        {confirming && (
          <div style={{ border: '1px solid var(--info-border)', background: 'var(--info-soft)', borderRadius: 8, padding: 12, marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--text-primary)', marginBottom: 8 }}>
              You have unsaved changes. Keep them as a draft to finish later?
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={onClose} variant="primary" size="sm">Keep draft</Button>
              <Button onClick={() => { draftState.discard?.(); onClose(); }} variant="destructive" size="sm">Discard</Button>
              <Button onClick={() => setConfirming(false)} size="sm">Keep editing</Button>
            </div>
          </div>
        )}
        <ModalDraftContext.Provider value={setDraftState}>{children}</ModalDraftContext.Provider>
      </div>
    </div>
  );
}

function F({ label, error, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={LB}>{label}</label>
      {children}
      {error && <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function SaveBtn({ onClick, label = 'Save' }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
      <Button onClick={onClick} variant="primary" size="sm">{label}</Button>
    </div>
  );
}

// Compact indicator that surfaces persistence state to the user.
// States (priority order): Failed > Saving > Unsaved > Saved > idle (nothing).
function formatRelativeTime(date) {
  if (!date) return '';
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return date.toLocaleString();
}
// Phase 18: cross-customer activity feed view. Renders ALL activities from
// data.activities (filtered + grouped by date). Click any item to jump to
// its customer in the per-customer view.
function ActivityFeed({ data, onJumpToActivity }) {
  const [userFilter, setUserFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [daysFilter, setDaysFilter] = useState(14); // 7 | 14 | 30 | 90 | All
  const today = new Date().toISOString().slice(0, 10);

  const allActivities = useMemo(
    () => Object.values(data.activities || {}).sort((a, b) => cmpDesc(a.date, b.date)),
    [data.activities],
  );

  const users = useMemo(() => {
    const set = new Set();
    for (const a of allActivities) if (a.created_by_name) set.add(a.created_by_name);
    return ['All', ...Array.from(set).sort()];
  }, [allActivities]);

  const filtered = useMemo(() => {
    let list = allActivities;
    if (userFilter !== 'All') list = list.filter(a => a.created_by_name === userFilter);
    if (typeFilter !== 'All') list = list.filter(a => a.type === typeFilter);
    if (daysFilter !== 'All') {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - Number(daysFilter));
      const cutoffStr = cutoff.toISOString().slice(0, 10);
      list = list.filter(a => (a.date || '') >= cutoffStr);
    }
    return list;
  }, [allActivities, userFilter, typeFilter, daysFilter]);

  // Group by date for readable scanning. Map preserves insertion order = date desc.
  const grouped = useMemo(() => {
    const m = new Map();
    for (const a of filtered) {
      const key = a.date || '(undated)';
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(a);
    }
    return Array.from(m.entries());
  }, [filtered]);

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 18, fontWeight: 700 }}>All Activity</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {filtered.length === allActivities.length ? `${allActivities.length} entries` : `${filtered.length} of ${allActivities.length} entries`}
            {' · '}cross-customer team feed
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
          <span>Last:</span>
          <select value={daysFilter} onChange={e => setDaysFilter(e.target.value === 'All' ? 'All' : Number(e.target.value))}
            style={{ ...IS, width: 'auto', fontSize: 11, padding: '4px 8px' }}>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value="All">All</option>
          </select>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            style={{ ...IS, width: 'auto', fontSize: 11, padding: '4px 8px' }}>
            <option value="All">All types</option>
            {ACT_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
          <select value={userFilter} onChange={e => setUserFilter(e.target.value)}
            style={{ ...IS, width: 'auto', fontSize: 11, padding: '4px 8px' }}>
            {users.map(u => <option key={u}>{u}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 40, textAlign: 'center' }}>
          No activities match the current filters.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {grouped.map(([date, items]) => {
            const dateLabel = date === today ? 'Today'
              : date === '(undated)' ? 'Undated'
              : new Date(date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' });
            return (
              <div key={date}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  {dateLabel} <span style={{ color: 'var(--text-muted)', fontWeight: 400, opacity: 0.6 }}>· {items.length}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {items.map(a => {
                    const customer = data.customers[a.customer_code];
                    // 057: the whole room, and the follow-ups still outstanding.
                    const who = attendeeSummary(a, data.contacts);
                    const openActions = nextActionTasks(data.tasks, a.id).filter(t => t.status !== 'Done');
                    return (
                      <div key={a.id} onClick={() => onJumpToActivity(a)}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 6 }}>
                        <span style={{ fontSize: 16, lineHeight: 1.2 }}>{ACT_ICON[a.type] || '📝'}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>
                              {customer?.short_name || customer?.name || a.customer_code}
                            </span>
                            <span style={{ fontSize: 12, color: 'var(--text-body)' }}>· {a.subject}</span>
                            {a.completed && <Badge text="Done" color="var(--success)" />}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                            {a.created_by_name ? `${a.created_by_name} · ` : ''}{a.type}
                            {who ? ` · ${who}` : ''}
                            {openActions.length > 0
                              ? <span style={{ color: 'var(--warn)' }}> · → {openActions.map(t => t.title).join(' · ')}</span>
                              : a.next_action && <span style={{ color: 'var(--warn)' }}> · → {a.next_action}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SaveStatus({ isSaving, isError, errorMessage, hasUnsaved, lastSavedAt, lastSavedByName, onRetry }) {
  // Tick once a minute so "Saved Ns ago" stays fresh without re-rendering the whole page.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  const base = { fontSize: 11, padding: '4px 10px', borderRadius: 'var(--radius)', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, lineHeight: 1.4 };
  if (isError) {
    return (
      <span style={{ ...base, background: 'var(--danger-soft)', color: 'var(--danger)', border: '1px solid var(--danger-border)' }} title={errorMessage || 'Save failed'}>
        <span aria-hidden>⚠</span> Save failed
        <button onClick={onRetry} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontWeight: 700, fontSize: 11, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
          retry
        </button>
      </span>
    );
  }
  if (isSaving) {
    return (
      <span style={{ ...base, background: 'var(--info-soft)', color: 'var(--info)', border: '1px solid var(--info-border)' }}>
        <span aria-hidden>●</span> Saving…
      </span>
    );
  }
  if (hasUnsaved) {
    return (
      <span style={{ ...base, background: 'var(--warn-soft)', color: 'var(--warn-text)', border: '1px solid var(--warn-border)' }}>
        <span aria-hidden>●</span> Unsaved
      </span>
    );
  }
  if (lastSavedAt) {
    // Phase 17: when the team blob's last writer was someone else, prefix "by X".
    // After this user saves themselves, lastSavedByName is cleared so the badge
    // reads "Saved Ns ago" (own writes feel local; other writes feel collaborative).
    const suffix = lastSavedByName ? `by ${lastSavedByName} · ${formatRelativeTime(lastSavedAt)}` : formatRelativeTime(lastSavedAt);
    return (
      <span style={{ ...base, background: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border-soft)' }} title={lastSavedAt.toLocaleString()}>
        <span aria-hidden style={{ color: 'var(--success)' }}>✓</span> Saved {suffix}
      </span>
    );
  }
  return null;
}

// ─── Customer Form ─────────────────────────────────────────────────────────────
// New/potential customers may have no D365 CUS#### code yet. Generate a readable,
// unique placeholder key (≤20 chars, to fit commercial.Customer.CustomerCode) so
// the record still has a stable identity; the user can instead type a clean short
// code like "WINC" by filling the field.
/*
 * The primary Majans account manager: the FIRST entry in majans_owners. Same convention as
 * 057's primary contact — the list is ordered and the top one owns the account. Returns null
 * where nobody is recorded, so those customers group under no owner rather than a blank string
 * that sorts oddly in the picker.
 */
function primaryOwnerName(cust) {
  const first = (cust?.majans_owners ?? [])[0];
  const name = typeof first === 'string' ? first : first?.name;
  return (name ?? '').trim() || null;
}

/*
 * Pick a customer that already exists in the master and put it on the CRM board.
 *
 * Deliberately a PICKER, not another code field. The whole class of bug Matthew has hit
 * twice this week — Snack Proud landing on Virgin's code, then CUS0035 being refused —
 * comes from a free-text code being the only way to name an account. Here the code and
 * the name both come from the master record, so there is nothing to mistype.
 *
 * The list is the exact complement of the board's inclusion rule (server-side), so it can
 * never offer something that is already one click away in the sidebar.
 */
function AdoptCustomerForm({ apiFetch, prefill, onDone, onOpen }) {
  const [q, setQ] = useState(prefill || '');
  const [picked, setPicked] = useState(null);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    const t = setTimeout(async () => {
      try {
        const res = await apiFetch(`/crm-customer-candidates${q ? `?q=${encodeURIComponent(q)}` : ''}`);
        if (!live) return;
        if (!res.ok) { setError(`Couldn't load the customer list (HTTP ${res.status}).`); return; }
        setError('');
        setRows(await res.json());
      } catch {
        if (live) setError('Network error loading the customer list.');
      }
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [q, apiFetch]);

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
        These are customers that already exist in the master — usually from D365 — but have
        never been worked in the CRM. Adding one keeps its real name and code, and brings
        across nothing else: no contacts, no activity, because there aren't any yet.
      </div>
      <input style={{ ...IS, marginBottom: 10 }} value={q} onChange={e => setQ(e.target.value)}
        placeholder="Search by name or customer code…" autoFocus />
      {error && (
        <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6,
          color: 'var(--danger)', padding: '8px 10px', fontSize: 12, marginBottom: 10 }}>{error}</div>
      )}
      <div style={{ maxHeight: '44vh', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
        {rows === null && <div style={{ padding: 10, fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>}
        {rows?.length === 0 && (
          <div style={{ padding: 10, fontSize: 12, color: 'var(--text-muted)' }}>
            {q
              ? 'Nothing matches — either it is already on the board, or it is not in the customer master yet. Use "+ Add Customer" for a brand-new account.'
              : 'Every customer in the master is already on the board.'}
          </div>
        )}
        {(rows ?? []).map(r => r.onBoard ? (
          // A search hit that is already on the board: say where it is, rather than hiding
          // it and letting "Nothing matches" imply it doesn't exist (Matthew, 27 Oct, CUS0040).
          <div key={r.code} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px',
            borderBottom: '1px solid var(--border-soft)', fontSize: 12 }}>
            <span style={{ flex: 1, color: 'var(--text-primary)' }}>
              {r.name}{r.shortName && r.shortName !== r.name ? ` (listed as "${r.shortName}")` : ''}
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>Already on the board</span>
            <span style={{ color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace' }}>{r.code}</span>
            <Button onClick={() => onOpen(r.code)}>Open</Button>
          </div>
        ) : (
          <label key={r.code} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px',
            borderBottom: '1px solid var(--border-soft)', fontSize: 12, cursor: 'pointer',
            background: picked === r.code ? 'var(--info-soft)' : 'transparent' }}>
            <input type="radio" name="adopt" checked={picked === r.code} onChange={() => setPicked(r.code)} />
            <span style={{ flex: 1, color: 'var(--text-primary)' }}>{r.name}</span>
            {/* Why this account is in the master at all — the reassurance that it is a real
                customer rather than a stray row. */}
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
              {[r.pricingRows ? `${r.pricingRows} price${r.pricingRows === 1 ? '' : 's'}` : null,
                r.stores ? `${r.stores} store${r.stores === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')}
            </span>
            <span style={{ color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace' }}>{r.code}</span>
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <Button variant="primary" disabled={!picked} onClick={() => onDone(picked)}>Add to CRM</Button>
      </div>
    </div>
  );
}

/*
 * Distribution countries (migration 076) — Matthew, 2026-09-21: "If we have a customer and
 * we select the channel as 'Export' ... we can select one or multiple countries they
 * distribute to as a general business."
 *
 * Shown when the segment is Export, and ALSO whenever countries are already recorded — a
 * customer moved off the Export channel must not have its countries silently disappear from
 * the form while they are still in the database and still on the map.
 *
 * Saved by its own endpoint, not with the customer: it is a child collection, and folding
 * it into the customer upsert would mean the board diff carrying a second entity.
 */
function CountryPicker({ value, onChange }) {
  const [q, setQ] = useState('');
  const selected = value ?? [];
  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return COUNTRY_OPTIONS
      .filter(c => c.name.toLowerCase().includes(needle) || c.code.toLowerCase() === needle)
      .filter(c => !selected.includes(c.code))
      .slice(0, 8);
  }, [q, selected]);

  const add = (code) => { onChange([...selected, code]); setQ(''); };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
        {selected.length === 0 && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>No countries recorded yet.</span>
        )}
        {selected.map(code => (
          <span key={code} style={{ background: 'var(--info-soft)', color: 'var(--info)', borderRadius: 10,
            padding: '2px 6px 2px 9px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            {COUNTRY_BY_CODE[code]?.name ?? code}
            <button type="button" aria-label={`Remove ${COUNTRY_BY_CODE[code]?.name ?? code}`}
              onClick={() => onChange(selected.filter(c => c !== code))}
              style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer',
                fontSize: 13, lineHeight: 1, padding: '0 2px' }}>&times;</button>
          </span>
        ))}
      </div>
      <input style={IS} value={q} onChange={e => setQ(e.target.value)}
        placeholder="Type a country to add\u2026" />
      {matches.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginTop: 4,
          background: 'var(--bg-card)', maxHeight: 190, overflowY: 'auto' }}>
          {matches.map(c => (
            <button key={c.code} type="button" onClick={() => add(c.code)}
              style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none',
                border: 'none', borderBottom: '1px solid var(--border-soft)', padding: '6px 9px',
                fontSize: 12, color: 'var(--text-primary)', cursor: 'pointer' }}>
              {c.name} <span style={{ color: 'var(--text-muted)' }}>{c.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function genCustomerKey(name) {
  const base = (name || '').toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, 8) || 'ACCT';
  const suffix = Date.now().toString(36).toUpperCase().slice(-4);
  return `NEW-${base}-${suffix}`;
}

// ─── Segment vocabulary (migration 061) ──────────────────────────────────────
// One query, shared by the customer form and the sidebar filter. Falls back to
// FALLBACK_SEGMENTS so the picker still works if the API is unreachable —
// `loaded` tells callers whether editing is actually available.
function useSegments(apiFetch) {
  const { data } = useQuery({
    queryKey: ['crm-segments'],
    queryFn: async () => {
      const res = await apiFetch('/crm-segments');
      if (!res.ok) throw new Error(`Failed to load segments: HTTP ${res.status}`);
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const loaded = Array.isArray(data) && data.length > 0;
  const segments = loaded
    ? data
    : FALLBACK_SEGMENTS.map((name, i) => ({ name, sortOrder: (i + 1) * 10, isActive: true, inUse: 0 }));
  return { segments, loaded };
}

// Options for a customer's Segment dropdown: the active vocabulary, plus whatever
// this customer already carries. A retired segment must still appear on the records
// that hold it — otherwise the select falls back to its first option and a plain
// Save silently reclassifies the account.
function segmentOptionsFor(segments, current) {
  const names = segments.filter(g => g.isActive).map(g => g.name);
  if (current && !names.includes(current)) names.unshift(current);
  return names;
}

// Add / rename / retire / delete segments. Rendered inline inside the customer form
// (Matthew asked for it "in here") — not as a second modal over the first.
function SegmentManager({ apiFetch, onRemap, onClose }) {
  const queryClient = useQueryClient();
  const { segments, loaded } = useSegments(apiFetch);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState(null);   // { from, to }
  const [deleting, setDeleting] = useState(null); // { name, inUse, reassignTo }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Only the vocabulary is refetched here. The board deliberately is NOT: it hydrates
  // once into local page state, so a refetch would change nothing. The customer rows a
  // rename/reassign touched are remapped in the page via onRemap instead.
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['crm-segments'] });
  };

  const run = async (fn) => {
    if (busy) return false;
    setBusy(true); setError('');
    try {
      const res = await fn();
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        let msg = `Something went wrong (HTTP ${res.status}).`;
        try { msg = JSON.parse(t).error || msg; } catch { /* keep default */ }
        setError(msg);
        return false;
      }
      await refresh();
      return true;
    } catch {
      setError('Network error — please try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    const n = newName.trim();
    if (!n) return;
    const nextOrder = Math.max(0, ...segments.map(g => g.sortOrder || 0)) + 10;
    if (await run(() => apiFetch('/crm-segments', { method: 'PUT', body: JSON.stringify({ name: n, sortOrder: nextOrder }) }))) {
      setNewName('');
    }
  };

  const saveRename = async () => {
    const to = (editing?.to || '').trim();
    if (!to || to === editing.from) { setEditing(null); return; }
    const from = editing.from;
    if (await run(() => apiFetch('/crm-segments', { method: 'PUT', body: JSON.stringify({ name: from, newName: to }) }))) {
      onRemap?.(from, to);
      setEditing(null);
    }
  };

  const toggleActive = (g) =>
    run(() => apiFetch('/crm-segments', { method: 'PUT', body: JSON.stringify({ name: g.name, isActive: !g.isActive }) }));

  const confirmDelete = async () => {
    // reassignTo is always sent (possibly empty) once the user has confirmed, so the
    // server does not have to guess between "move them" and "clear the field".
    const qs = deleting.inUse > 0 || deleting.reassignTo
      ? `?reassignTo=${encodeURIComponent(deleting.reassignTo || '')}`
      : '';
    const { name, reassignTo } = deleting;
    if (await run(() => apiFetch(`/crm-segments/${encodeURIComponent(name)}${qs}`, { method: 'DELETE' }))) {
      onRemap?.(name, reassignTo || null);
      setDeleting(null);
    }
  };

  const RowBtn = { background: 'none', border: 'none', color: 'var(--info)', fontSize: 11, cursor: 'pointer', padding: '0 2px' };

  return (
    <div style={{ border: '1px solid var(--border-soft)', borderRadius: 8, padding: 12, marginBottom: 14, background: 'var(--bg-base, transparent)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>Manage segments</span>
        <button onClick={onClose} style={{ ...RowBtn, color: 'var(--text-muted)' }}>Done</button>
      </div>

      {!loaded && (
        <div style={{ fontSize: 11, color: 'var(--warning, var(--text-muted))', marginBottom: 8 }}>
          Showing the built-in list — the segment service is unreachable, so editing is unavailable right now.
        </div>
      )}
      {error && <div style={{ fontSize: 11, color: 'var(--danger)', marginBottom: 8 }}>{error}</div>}

      <div style={{ maxHeight: 210, overflowY: 'auto', marginBottom: 10 }}>
        {segments.map(g => (
          <div key={g.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--border-soft)' }}>
            {editing?.from === g.name ? (
              <>
                <input
                  autoFocus style={{ ...IS, fontSize: 12, padding: '4px 8px' }} value={editing.to}
                  onChange={e => setEditing({ ...editing, to: e.target.value })}
                  onKeyDown={e => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setEditing(null); }}
                />
                <button onClick={saveRename} disabled={busy} style={RowBtn}>Save</button>
                <button onClick={() => setEditing(null)} style={{ ...RowBtn, color: 'var(--text-muted)' }}>Cancel</button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, fontSize: 12, color: g.isActive ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                  {g.name}{!g.isActive && ' (retired)'}
                </span>
                <span style={{ fontSize: 10, color: 'var(--text-muted)', minWidth: 58, textAlign: 'right' }}>
                  {g.inUse} customer{g.inUse === 1 ? '' : 's'}
                </span>
                <button onClick={() => setEditing({ from: g.name, to: g.name })} disabled={!loaded || busy} style={RowBtn}>Rename</button>
                <button onClick={() => toggleActive(g)} disabled={!loaded || busy} style={RowBtn}>{g.isActive ? 'Retire' : 'Restore'}</button>
                <button onClick={() => setDeleting({ name: g.name, inUse: g.inUse, reassignTo: '' })} disabled={!loaded || busy}
                  style={{ ...RowBtn, color: 'var(--danger)' }}>Delete</button>
              </>
            )}
          </div>
        ))}
      </div>

      {deleting && (
        <div style={{ border: '1px solid var(--danger-border)', background: 'var(--danger-soft)', borderRadius: 6, padding: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--text-primary)', marginBottom: 6 }}>
            Delete “{deleting.name}”?
            {deleting.inUse > 0 && ` ${deleting.inUse} customer${deleting.inUse === 1 ? ' carries' : 's carry'} it.`}
          </div>
          {deleting.inUse > 0 && (
            <select style={{ ...IS, fontSize: 12, padding: '4px 8px', marginBottom: 8 }}
              value={deleting.reassignTo} onChange={e => setDeleting({ ...deleting, reassignTo: e.target.value })}>
              <option value="">Leave them with no segment</option>
              {segments.filter(g => g.name !== deleting.name).map(g => <option key={g.name} value={g.name}>Move them to “{g.name}”</option>)}
            </select>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={confirmDelete} variant="destructive" size="sm" disabled={busy}>Delete</Button>
            <Button onClick={() => setDeleting(null)} size="sm">Cancel</Button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          style={{ ...IS, fontSize: 12, padding: '5px 9px' }} value={newName} placeholder="New segment name…"
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <Button onClick={add} variant="primary" size="sm" disabled={!loaded || busy || !newName.trim()}>Add</Button>
      </div>
    </div>
  );
}

/* Website is stored as the user typed it (Matthew will paste "woolworths.com.au" as often
 * as a full URL), so the two places that consume it normalise on the way out rather than
 * rewriting what he entered: a link needs a scheme, a label reads better without one. */
function normaliseUrl(v) {
  const t = String(v || '').trim();
  if (!t) return '';
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
}
function displayDomain(v) {
  const t = String(v || '').trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  return t.replace(/\/.*$/, '') || t;
}
// Deliberately loose: something.tld, optionally with a scheme, www and a path. Tight URL
// regexes reject real addresses and the field is informational, not a security boundary.
const WEBSITE_RE = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i;

/* ─── Suggested contacts from your mailbox (migration 065) ───────────────────
 *
 * Matthew, 10 Sep: people we correspond with who are not yet on the Contacts tab.
 *
 * These are PROPOSALS. Add opens the normal contact form pre-filled, so the row is
 * created by the same governed write as any other contact and a human has seen every
 * field before it lands. Dismiss is per (customer, email) for the whole team, so the
 * list gets quieter as it is worked rather than re-offering the same person to each
 * colleague in turn.
 *
 * Only the signed-in user's own run can propose — the suggestions live on YOUR
 * correspondence row, because they came out of YOUR mailbox.
 */
function SuggestedContactsPanel({ apiFetch, customerCode, currentUserOid, existingContacts, onAdd }) {
  const queryClient = useQueryClient();
  const { data: byCustomer } = useQuery({
    queryKey: ['crm-correspondence'],
    queryFn: async () => {
      const res = await apiFetch('/crm-correspondence');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    staleTime: 60 * 1000,
    retry: false,
  });
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  // Already-held addresses are filtered again here, not only server-side: the run that
  // produced these may predate a contact someone has since added, and re-offering a
  // person who is already on the tab makes the whole panel look broken.
  const held = useMemo(
    () => new Set((existingContacts || []).map(c => (c.email || '').trim().toLowerCase()).filter(Boolean)),
    [existingContacts]);

  const suggestions = useMemo(() => {
    const mine = (byCustomer?.[customerCode] || []).find(e => e.generated_by_oid === currentUserOid);
    return (mine?.suggested_contacts || []).filter(s => !held.has((s.email || '').toLowerCase()));
  }, [byCustomer, customerCode, currentUserOid, held]);

  const dismiss = async (email) => {
    setBusy(email); setError('');
    try {
      const res = await apiFetch(`/crm-contact-suggestion-dismiss/${encodeURIComponent(customerCode)}`, {
        method: 'POST', body: JSON.stringify({ email }),
      });
      if (!res.ok) { setError(`Couldn't dismiss (HTTP ${res.status}).`); return; }
      await queryClient.invalidateQueries({ queryKey: ['crm-correspondence'] });
    } catch {
      setError('Network error — please try again.');
    } finally {
      setBusy('');
    }
  };

  // Split a header display name into first/last for the contact form. "Blake, Matthew"
  // is as common as "Matthew Blake" in Exchange, so handle both rather than producing
  // a contact whose first name is "Blake,".
  const splitName = (name, email) => {
    const n = (name || '').trim();
    if (!n) return { first: (email || '').split('@')[0], last: '' };
    if (n.includes(',')) {
      const [last, first] = n.split(',', 2).map(s => s.trim());
      return { first: first || last, last: first ? last : '' };
    }
    const parts = n.split(/\s+/);
    return { first: parts[0], last: parts.slice(1).join(' ') };
  };

  if (suggestions.length === 0) return null;

  return (
    <div className="card" style={{ marginBottom: 16, borderColor: 'var(--ai-border)' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>
        Suggested from your email
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
        People at this customer you have corresponded with who aren’t on the tab yet.
        Nothing is saved until you press Add.
      </div>
      {error && <div style={{ fontSize: 11, color: 'var(--danger)', marginBottom: 8 }}>{error}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {suggestions.map(s => (
          <div key={s.email} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid var(--border-soft)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                {s.name || s.email}
                {s.title && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}> · {s.title}</span>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {[s.email, s.phone].filter(Boolean).join(' · ')}
              </div>
            </div>
            {/* Evidence count, so a single cc reads as a single cc and not as a regular. */}
            <span style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {s.evidenceCount} email{s.evidenceCount === 1 ? '' : 's'}
            </span>
            <Button size="sm" variant="primary" disabled={busy === s.email}
              onClick={() => {
                const { first, last } = splitName(s.name, s.email);
                onAdd({ first_name: first, last_name: last, title: s.title || '', email: s.email, phone: s.phone || '', active: true });
              }}>
              Add
            </Button>
            <button onClick={() => dismiss(s.email)} disabled={busy === s.email}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer' }}>
              Dismiss
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomerForm({ init, onSave, apiFetch, onSegmentRemap }) {
  const { segments } = useSegments(apiFetch);
  const [manageSegments, setManageSegments] = useState(false);
  const [f, setF, draft] = useDraftedForm(
    draftKey('customer', init?.code, init?.code),
    () => init || { code: '', name: '', short_name: '', segment: '', majans_owners: [], notes: '', head_office: '', website: '', is_current: true });
  useModalDraft(draft);

  /*
   * Distribution countries (076) live in their own table, so they are fetched per
   * customer rather than riding on the board.
   *
   * ⚠ `loadedCountriesFor` guards against clobbering a DRAFT. useDraftedForm may have
   * restored a half-typed form including countries the user picked before closing the
   * modal; without the guard this fetch would land afterwards and overwrite them. It only
   * seeds when nothing is there yet, and only once per customer code.
   */
  const loadedCountriesFor = useRef(null);
  useEffect(() => {
    const code = init?.code;
    if (!code || !apiFetch || loadedCountriesFor.current === code) return;
    loadedCountriesFor.current = code;
    let live = true;
    (async () => {
      try {
        const r = await apiFetch('/customer-countries');
        if (!live || !r.ok) return;
        const mine = (await r.json()).filter(x => x.customerCode === code).map(x => x.countryCode);
        setF(x => (x.countries === undefined ? { ...x, countries: mine } : x));
      } catch { /* the picker simply starts empty; saving still works */ }
    })();
    return () => { live = false; };
  }, [init?.code, apiFetch, setF]);

  const segOptions = segmentOptionsFor(segments, f.segment);
  // A new customer falls back to the first segment in the managed order, which is only
  // known once the list has loaded. Derived, not seeded into state by an effect — and
  // read on save, so what the user sees in the select is what gets written.
  const segValue = f.segment || segOptions[0] || '';
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    if (busy) return;
    const errs = {};
    if (!f.name) errs.name = 'Required';
    if (f.website && !WEBSITE_RE.test(f.website.trim())) errs.website = 'Looks like: woolworths.com.au or https://woolworths.com.au';
    // Customer Code is the record's identity key but is NOT mandatory: new/potential
    // customers often have no D365 CUS#### code yet. It IS editable on an existing
    // customer — changing it renames the key and cascades to all linked records
    // server-side (/crm-customer-rename). Validate format whenever a code is present.
    const typed = (f.code || '').trim().toUpperCase();
    if (typed && !/^[A-Z][A-Z0-9-]{1,19}$/.test(typed)) {
      errs.code = 'Up to 20 letters, digits or hyphens (e.g. CUS0044 or WINC)';
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const oldCode = init ? init.code : null;
    // Blank on edit keeps the existing code (can't null a PK); blank on add → generate.
    const code = typed || (init ? init.code : genCustomerKey(f.name));

    /*
     * On ADD with a typed code, check it is free BEFORE saving. The server refuses a
     * colliding create too (?mode=create), but that refusal surfaces through the
     * background sync long after the modal has closed — by which time the user thinks
     * they have added a customer. Checked against the FULL master list, not the board:
     * the board holds only the CRM-tracked subset, and CUS0207 style collisions happen
     * precisely on codes you cannot see.
     */
    if (!init && typed && apiFetch) {
      setBusy(true);
      try {
        const res = await apiFetch('/master-customers?all=true');
        if (res.ok) {
          const all = await res.json();
          const hit = (Array.isArray(all) ? all : []).find(
            x => String(x.customerCode || '').toUpperCase() === code);
          if (hit) {
            /*
             * Two different refusals wearing one message, which is what Matthew ran into
             * on 18 Sep. Ask the server which one this is: a code the CRM already shows
             * (the Virgin collision — genuinely pick another) versus a code that exists
             * in the master but has never been on the board (MNB Variety Imports — he
             * should adopt it, and telling him to "pick a different code" is nonsense
             * because CUS0035 IS the right code).
             */
            let adoptable = false;
            try {
              const c = await apiFetch(`/crm-customer-candidates?q=${encodeURIComponent(code)}`);
              if (c.ok) {
                const cands = await c.json();
                adoptable = (Array.isArray(cands) ? cands : []).some(
                  x => !x.onBoard && String(x.code || '').toUpperCase() === code);
              }
            } catch { /* unreachable: fall back to the plain collision message */ }
            setErrors({
              code: adoptable
                ? `${code} is "${hit.name}" in the customer master but isn't on the CRM board yet — close this and use "Add an existing customer" to bring it across.`
                : `Already used by "${hit.name}". Pick a different code, or open that customer if it's the one you meant.`,
            });
            setBusy(false);
            return;
          }
        }
        // A failed check must not block the save — the server-side ?mode=create guard
        // is the one that actually protects the data.
      } catch { /* offline: fall through to the server guard */ }
      setBusy(false);
    }
    // Identity (PK) change on an existing customer → cascade on the server FIRST, so
    // local state only moves once the database has. Confirmed (it relinks records).
    if (oldCode && code !== oldCode) {
      if (!window.confirm(`Change customer code from ${oldCode} to ${code}?\n\nThis updates every linked contact, activity, task and pricing record.`)) return;
      setBusy(true);
      try {
        const res = await apiFetch('/crm-customer-rename', { method: 'POST', body: JSON.stringify({ from: oldCode, to: code }) });
        if (!res.ok) {
          let msg = `Couldn't change the code (HTTP ${res.status}).`;
          if (res.status === 409) msg = `Code "${code}" is already used by another customer.`;
          else { const t = await res.text().catch(() => ''); try { msg = JSON.parse(t).error || msg; } catch { /* keep default */ } }
          setErrors({ code: msg });
          setBusy(false);
          return;
        }
      } catch {
        setErrors({ code: 'Network error changing the code — please try again.' });
        setBusy(false);
        return;
      }
      setBusy(false);
    }
    /*
     * Countries save through their own endpoint, after any code rename above has landed —
     * so they attach to the code the customer now has, not the one it used to.
     *
     * Only when the form actually carries them: `undefined` means the picker never loaded
     * for this customer, and PUTting an empty array then would silently delete countries
     * the user never saw. An empty ARRAY is a real instruction and is sent.
     */
    if (Array.isArray(f.countries) && apiFetch) {
      try {
        const r = await apiFetch(`/customer-countries/${encodeURIComponent(code)}`, {
          method: 'PUT', body: JSON.stringify({ countries: f.countries }),
        });
        if (!r.ok) {
          const msg = (await r.json().catch(() => ({})))?.error;
          setErrors({ name: `Customer saved, but the countries did not: ${msg || `HTTP ${r.status}`}` });
        }
      } catch {
        setErrors({ name: 'Customer saved, but the countries did not reach the server.' });
      }
    }

    draft.clear();
    onSave({ ...f, segment: segValue, code, created: f.created || new Date().toISOString().slice(0, 10) }, oldCode);
  };
  return (
    <>
      <DraftBanner draft={draft} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <F label="Customer Code" error={errors.code}><input style={IS} value={f.code} onChange={set('code')} placeholder="Optional — e.g. CUS0044 or WINC" /></F>
        <F label={
          <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Segment
            <button type="button" onClick={() => setManageSegments(v => !v)}
              style={{ background: 'none', border: 'none', color: 'var(--info)', fontSize: 11, cursor: 'pointer', padding: 0 }}>
              {manageSegments ? 'Close' : 'Manage…'}
            </button>
          </span>
        }>
          <select style={IS} value={segValue} onChange={set('segment')}>
            {segOptions.map(s => <option key={s}>{s}</option>)}
          </select>
        </F>
      </div>
      {manageSegments && <SegmentManager apiFetch={apiFetch} onRemap={onSegmentRemap} onClose={() => setManageSegments(false)} />}
      <F label="Full Name *" error={errors.name}><input style={IS} value={f.name} onChange={set('name')} placeholder="Company legal name" /></F>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <F label="Short Name"><input style={IS} value={f.short_name} onChange={set('short_name')} placeholder="Used in lists" /></F>
        <F label="Head Office"><input style={IS} value={f.head_office || ''} onChange={set('head_office')} placeholder="Suburb, State — or full address" /></F>
      </div>
      <F label="Website" error={errors.website}>
        <input style={IS} value={f.website || ''} onChange={set('website')} placeholder="woolworths.com.au" />
      </F>
      {/* Addressable owners (migration 055) — who runs this account on our side, and
          more than one, because internal cover overlaps. Since 063 this is the ONLY
          owner field: the free-text Account Manager beside it has been retired, and
          every value it held was carried in here. */}
      <OwnerListEditor value={f.majans_owners ?? []} onChange={next => setF(x => ({ ...x, majans_owners: next }))} />
      {(segValue === 'Export' || (f.countries ?? []).length > 0) && (
        <F label="Distributes to (countries)">
          <CountryPicker value={f.countries ?? []} onChange={next => setF(x => ({ ...x, countries: next }))} />
        </F>
      )}
      <F label="Notes"><textarea style={TA} value={f.notes} onChange={set('notes')} /></F>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <input type="checkbox" id="is_current" checked={f.is_current !== false} onChange={e => setF(x => ({ ...x, is_current: e.target.checked }))} style={{ width: 16, height: 16, cursor: 'pointer' }} />
        <label htmlFor="is_current" style={{ ...LB, marginBottom: 0, cursor: 'pointer', fontSize: 12 }}>Current customer (active trading relationship)</label>
      </div>
      <SaveBtn onClick={save} />
    </>
  );
}

// ─── Contact Form ──────────────────────────────────────────────────────────────
function ContactForm({ init, onSave, draftKeyBase }) {
  const [f, setF, draft] = useDraftedForm(
    draftKey('contact', draftKeyBase, init?.id),
    () => init || { first_name: '', last_name: '', title: '', email: '', phone: '', linkedin: '', photo_url: '', notes: '', active: true });
  useModalDraft(draft);
  const [errors, setErrors] = useState({});
  const set = k => e => setF(x => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = () => {
    const errs = {};
    if (!f.first_name) errs.first_name = 'Required';
    if (f.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) errs.email = 'Invalid email format';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    draft.clear();
    onSave({ ...f, created: f.created || new Date().toISOString().slice(0, 10) });
  };
  return (
    <>
      <DraftBanner draft={draft} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <F label="First Name *" error={errors.first_name}><input style={IS} value={f.first_name || ''} onChange={set('first_name')} /></F>
        <F label="Last Name"><input style={IS} value={f.last_name || ''} onChange={set('last_name')} /></F>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <F label="Title / Role"><input style={IS} value={f.title || ''} onChange={set('title')} placeholder="Category Manager – Snacks" /></F>
        <F label="Phone"><input style={IS} value={f.phone || ''} onChange={set('phone')} /></F>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <F label="Email" error={errors.email}><input style={IS} value={f.email || ''} onChange={set('email')} type="email" /></F>
        <F label="LinkedIn"><input style={IS} value={f.linkedin || ''} onChange={set('linkedin')} /></F>
      </div>
      <F label="Photo URL"><input style={IS} value={f.photo_url || ''} onChange={set('photo_url')} placeholder="https://…" /></F>
      <F label="Notes"><textarea style={TA} value={f.notes || ''} onChange={set('notes')} /></F>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <input type="checkbox" id="contact_active" checked={f.active !== false} onChange={set('active')} style={{ width: 16, height: 16 }} />
        <label htmlFor="contact_active" style={{ ...LB, marginBottom: 0, cursor: 'pointer', fontSize: 12 }}>Still active at this organisation</label>
      </div>
      <SaveBtn onClick={save} />
    </>
  );
}

// ─── Activity Form ─────────────────────────────────────────────────────────────
function ActivityForm({ init, contacts, contactsById, linkedTasks, staffSuggestions, currentUserName, onSave, apiFetch, draftKeyBase }) {
  const today = new Date().toISOString().slice(0, 10);
  // 057: attendees and next actions are derived on open — attendeesOf() lifts a
  // pre-057 activity's single contact into the list, seedNextActions() lifts its
  // single free-text follow-up — so an old record edits like a new one.
  // The derived shape IS the baseline, so a reopened activity is not "dirty" just
  // because attendees were lifted out of the legacy single contact_id.
  const [f, setF, draft] = useDraftedForm(draftKey('activity', draftKeyBase, init?.id), () => {
    const base = init || { type: 'Meeting', date: today, contact_id: '', subject: '', location: '', notes: '', outcome: '', next_action: '', completed: false };
    return { ...base, attendees: attendeesOf(base, contactsById), next_actions: seedNextActions(base, linkedTasks) };
  });
  useModalDraft(draft);
  const [errors, setErrors] = useState({});
  // Owner suggestions for a follow-up: the people actually in this meeting come
  // first, then the wider staff list. Assigning to someone who wasn't there is
  // allowed — it just isn't the first thing offered.
  const ownerOptions = useMemo(() => {
    const fromMeeting = (f.attendees || []).map(a => attendeeName(a, contactsById)).filter(Boolean);
    return [...new Set([...fromMeeting, currentUserName, ...(staffSuggestions || [])].filter(Boolean))];
  }, [f.attendees, contactsById, currentUserName, staffSuggestions]);
  // Phase 24: paste-an-email-thread extractor
  const [pasteThread, setPasteThread] = useState('');
  const [extractState, setExtractState] = useState({ status: 'idle', error: '' });
  // Phase 26: voice dictation via browser-native SpeechRecognition (Chrome/Edge).
  // Hold-to-record button appends interim + final transcripts to Notes.
  const [recording, setRecording] = useState(false);
  const recognitionRef = useRef(null);
  const VoiceCtor = typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;
  const voiceSupported = !!VoiceCtor;
  function startVoice() {
    if (!VoiceCtor) return;
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch { /* ignore */ } }
    const rec = new VoiceCtor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-AU';
    let appendBase = f.notes || '';
    if (appendBase && !appendBase.endsWith('\n')) appendBase += '\n';
    rec.onresult = (e) => {
      let interim = '';
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t + ' '; else interim += t;
      }
      // Append finals onto base; show interim after for live feedback.
      if (final) appendBase += final;
      setF(x => ({ ...x, notes: appendBase + interim }));
    };
    rec.onerror = (e) => {
      console.error('[voice] recognition error', e);
      setRecording(false);
    };
    rec.onend = () => setRecording(false);
    recognitionRef.current = rec;
    rec.start();
    setRecording(true);
  }
  function stopVoice() {
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch { /* ignore */ } }
    setRecording(false);
  }
  // Stop recognition if the form unmounts mid-recording.
  useEffect(() => () => { if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch { /* ignore */ } } }, []);
  const set = k => e => setF(x => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = () => {
    const errs = {};
    if (!f.subject) errs.subject = 'Required';
    if (!f.date) errs.date = 'Required';
    // Same loose shape as the customer website field — a link people paste, not a
    // security boundary. Catches a typo, not a determined mistake.
    if (f.notion_url && !WEBSITE_RE.test(f.notion_url.trim())) errs.notion_url = 'Paste a link, e.g. https://notion.so/…';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    // Drop attendee rows and next actions the user added but never filled in —
    // saving a blank row would create a nameless attendee or an untitled task.
    const attendees = (f.attendees || []).filter(a => a.contact_id || (a.name || '').trim());
    const nextActions = (f.next_actions || []).filter(r => (r.text || '').trim());
    draft.clear();
    onSave({ ...f, notion_url: (f.notion_url || '').trim(), attendees, next_actions: nextActions, contact_id: primaryContactId(attendees), created: f.created || today });
  };
  // Phase 24: ask Claude to extract activity fields from a pasted email thread.
  async function extractFromPaste() {
    if (!pasteThread.trim()) { setExtractState({ status: 'error', error: 'Paste an email or chat thread first.' }); return; }
    if (!apiFetch) { setExtractState({ status: 'error', error: 'AI extractor unavailable (no apiFetch).' }); return; }
    setExtractState({ status: 'loading', error: '' });
    try {
      const allowedTypes = ACT_TYPES.join(' | ');
      const contactOptions = contacts.map(c => `${c.id}=${contactName(c)}${c.title ? ` (${c.title})` : ''}`).join('; ') || '(none)';
      const result = await callAi(apiFetch, {
        system: `You extract structured activity records from email or chat threads for a CRM. Reply with ONLY a JSON object — no markdown, no prose, no code fence. Schema: {"type": one of [${allowedTypes}], "date": "YYYY-MM-DD", "subject": short title <=80 chars, "notes": full thread summary 2-4 sentences, "outcome": one sentence on what was agreed (empty string if unclear), "next_actions": array of 0-5 short follow-up actions, one sentence each (empty array if none), "contact_ids": array of the provided contact ids that took part (empty array if none match), "majans_attendees": array of names of the supplier-side (Majans) people who took part, from the thread's senders and recipients (empty array if none)}. Only list contact_ids from the provided list — never invent one. If the date isn't obvious, use today's date.`,
        prompt: `Today's date: ${today}\nKnown contacts for this customer (id=name): ${contactOptions}\n\nThread:\n"""\n${pasteThread.slice(0, 12000)}\n"""`,
        maxTokens: 1000,
      });
      // Tolerant parse — strip markdown code fences if Claude added them anyway.
      let raw = result.text.trim().replace(/^```(?:json)?\s*/i, '').replace(/```$/, '').trim();
      const parsed = JSON.parse(raw);
      // The model proposes attendees and follow-ups; both are MERGED into what
      // the user already has rather than replacing it, and every customer-side
      // id is checked against the real contact list so an invented id can't
      // create a phantom attendee. Tolerates the old single-value keys too.
      const arr = v => (Array.isArray(v) ? v : []);
      const proposedContacts = arr(parsed.contact_ids).concat(parsed.contact_id ? [parsed.contact_id] : []);
      const proposedActions = arr(parsed.next_actions).concat(parsed.next_action ? [parsed.next_action] : []);
      setF(x => {
        const attendees = [...(x.attendees || [])];
        const has = (side, key) => attendees.some(a => a.side === side && (a.contact_id === key || (a.name || '').trim().toLowerCase() === String(key).trim().toLowerCase()));
        for (const id of proposedContacts) {
          const c = contacts.find(k => k.id === id);
          if (c && !has('customer', c.id)) attendees.push({ id: uid(), side: 'customer', contact_id: c.id, name: contactName(c), email: c.email || '', title: c.title || '' });
        }
        for (const n of arr(parsed.majans_attendees)) {
          const name = typeof n === 'string' ? n.trim() : '';
          if (name && !has('majans', name)) attendees.push({ id: uid(), side: 'majans', contact_id: '', name, email: '', title: '' });
        }
        const nextActions = [...(x.next_actions || [])];
        for (const a of proposedActions) {
          const text = typeof a === 'string' ? a.trim() : '';
          if (text && !nextActions.some(r => (r.text || '').trim().toLowerCase() === text.toLowerCase())) {
            nextActions.push({ id: uid(), text, owner: '', due_date: '', priority: 'Medium', make_task: true, task_id: '' });
          }
        }
        return {
          ...x,
          type:    ACT_TYPES.includes(parsed.type) ? parsed.type : x.type,
          date:    /^\d{4}-\d{2}-\d{2}$/.test(parsed.date) ? parsed.date : x.date,
          subject: parsed.subject || x.subject,
          notes:   parsed.notes || x.notes,
          outcome: parsed.outcome || x.outcome,
          attendees,
          next_actions: nextActions,
        };
      });
      setExtractState({ status: 'done', error: '' });
    } catch (err) {
      setExtractState({ status: 'error', error: err.message || 'extraction failed' });
    }
  }
  return (
    <>
      {/* Phase 24: paste-an-email AI extractor */}
      <div style={{ background: 'var(--ai-soft)', border: '1px solid var(--ai-border)', borderRadius: 'var(--radius-md)', padding: '10px 12px', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ai-text)', textTransform: 'uppercase', letterSpacing: 0.5 }}>✨ Paste email thread to auto-fill</div>
          <button onClick={extractFromPaste} disabled={extractState.status === 'loading' || !pasteThread.trim()}
            style={{ background: 'var(--ai)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', padding: '4px 12px', fontSize: 11, fontWeight: 600, cursor: extractState.status === 'loading' ? 'wait' : 'pointer', opacity: pasteThread.trim() ? 1 : 0.5 }}>
            {extractState.status === 'loading' ? 'Extracting…' : 'Extract'}
          </button>
        </div>
        <textarea value={pasteThread} onChange={e => setPasteThread(e.target.value)}
          placeholder="Paste an email thread, Teams chat, or meeting notes — Claude will fill type, date, subject, contact, notes, outcome, next action."
          style={{ ...TA, minHeight: 50, fontSize: 11, background: 'var(--bg-card)' }} />
        {extractState.error && <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 6 }}>{extractState.error}</div>}
        {extractState.status === 'done' && <div style={{ fontSize: 11, color: 'var(--success)', marginTop: 6 }}>Filled — review and adjust below before saving.</div>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <F label="Type">
          <select style={IS} value={f.type} onChange={set('type')}>
            {ACT_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </F>
        <F label="Date *" error={errors.date}><input style={IS} type="date" value={f.date} onChange={set('date')} /></F>
      </div>
      <AttendeePicker
        value={f.attendees}
        onChange={next => setF(x => ({ ...x, attendees: next }))}
        contacts={contacts}
        staffSuggestions={staffSuggestions}
        currentUserName={currentUserName}
      />
      <F label="Subject *" error={errors.subject}><input style={IS} value={f.subject} onChange={set('subject')} placeholder="What was this interaction about?" /></F>
      <F label="Location"><input style={IS} value={f.location || ''} onChange={set('location')} placeholder="Venue, address, or video link…" /></F>
      {/* 066 — link this activity back to where the notes actually live. The page is
          NOT read by the server (see the migration header for why), so this is a
          pointer, not an import: to bring the content in, paste it into the extractor
          above and the existing AI proposes subject, notes, outcome and next actions. */}
      <F label="Meeting notes link" error={errors.notion_url}>
        <input style={IS} value={f.notion_url || ''} onChange={set('notion_url')}
          placeholder="https://notion.so/… — or any page where the notes live" />
      </F>
      {/* Phase 21: template picker — pre-fills type, subject, and notes from a starter pack. */}
      <div style={{ marginBottom: 14 }}>
        <label style={LB}>Insert template</label>
        <select style={{ ...IS, fontSize: 11 }} value=""
          onChange={(e) => {
            const tpl = ACTIVITY_TEMPLATES.find(t => t.key === e.target.value);
            if (!tpl) return;
            setF(x => ({
              ...x,
              type: tpl.type || x.type,
              // Don't overwrite a user-typed subject; do replace empty/placeholder.
              subject: x.subject ? x.subject : tpl.subject,
              // Append into notes so user's existing input isn't lost.
              notes: x.notes ? `${x.notes}\n\n${tpl.body}` : tpl.body,
            }));
            e.target.value = '';
          }}>
          <option value="">— Choose a template to insert…</option>
          {ACTIVITY_TEMPLATES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>
      {/* Phase 26: voice dictation. Browser-native — no API key. Chrome/Edge only. */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <label style={LB}>Notes / Discussion</label>
          {voiceSupported ? (
            <button onClick={recording ? stopVoice : startVoice}
              style={{ background: recording ? 'var(--danger-soft)' : 'var(--bg-elevated)', border: '1px solid ' + (recording ? 'var(--danger)' : 'var(--border-soft)'), color: recording ? 'var(--danger)' : 'var(--text-body)', borderRadius: 'var(--radius)', padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              {recording ? '⏹ Stop dictation' : '🎤 Dictate'}
            </button>
          ) : (
            <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }} title="Browser doesn't support SpeechRecognition (use Chrome or Edge)">🎤 not supported</span>
          )}
        </div>
        <textarea style={TA} value={f.notes} onChange={set('notes')} placeholder="Key points discussed…" />
      </div>
      <F label="Outcome"><textarea style={{ ...TA, minHeight: 52 }} value={f.outcome} onChange={set('outcome')} placeholder="What was agreed or decided?" /></F>
      <NextActionsEditor
        value={f.next_actions}
        onChange={next => setF(x => ({ ...x, next_actions: next }))}
        ownerOptions={ownerOptions}
      />
      {/* Phase 22: inline attachments. Limits enforced in AttachmentChips. */}
      <F label={`Attachments${f.attachments?.length ? ` (${f.attachments.length})` : ''}`}>
        <AttachmentChips
          attachments={f.attachments}
          onChange={(next) => setF(x => ({ ...x, attachments: next }))}
        />
      </F>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <input type="checkbox" id="completed" checked={f.completed} onChange={set('completed')} style={{ width: 16, height: 16 }} />
        <label htmlFor="completed" style={{ ...LB, marginBottom: 0, cursor: 'pointer' }}>Mark as completed</label>
      </div>
      <SaveBtn onClick={save} />
    </>
  );
}

// ─── Task Form ─────────────────────────────────────────────────────────────────
function TaskForm({ init, contacts, staffSuggestions = [], sourceActivity, onSave, draftKeyBase }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF, draft] = useDraftedForm(
    draftKey('task', draftKeyBase, init?.id),
    () => init || { title: '', due_date: '', contact_id: '', priority: 'Medium', status: 'Open', notes: '', owner: '' });
  useModalDraft(draft);
  const [errors, setErrors] = useState({});
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  const save = () => {
    const errs = {};
    if (!f.title) errs.title = 'Required';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    draft.clear();
    onSave({ ...f, created: f.created || today });
  };
  return (
    <>
      <DraftBanner draft={draft} />
      {/* 057: a task created from a meeting's Next Actions says so, and stays
          linked — editing it here does not detach it from that activity. */}
      {sourceActivity && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 6, padding: '6px 10px', marginBottom: 12 }}>
          Next action from <strong style={{ color: 'var(--text-body)' }}>{sourceActivity.subject}</strong> ({sourceActivity.date})
        </div>
      )}
      <F label="Task Title *" error={errors.title}><input style={IS} value={f.title} onChange={set('title')} placeholder="What needs to be done?" /></F>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <F label="Due Date"><input style={IS} type="date" value={f.due_date} onChange={set('due_date')} /></F>
        <F label="Priority">
          <select style={IS} value={f.priority} onChange={set('priority')}>
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
        </F>
        <F label="Status">
          <select style={IS} value={f.status} onChange={set('status')}>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </F>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <F label="Owner">
          <input style={IS} list="crm-task-owners" value={f.owner || ''} onChange={set('owner')} placeholder="Who is doing this?" />
          <datalist id="crm-task-owners">
            {staffSuggestions.map(n => <option key={n} value={n} />)}
          </datalist>
        </F>
        <F label="Contact">
          <select style={IS} value={f.contact_id} onChange={set('contact_id')}>
            <option value="">— no specific contact —</option>
            {contacts.map(c => <option key={c.id} value={c.id}>{contactName(c)} · {c.title}</option>)}
          </select>
        </F>
      </div>
      <F label="Notes"><textarea style={{ ...TA, minHeight: 52 }} value={f.notes} onChange={set('notes')} /></F>
      <SaveBtn onClick={save} />
    </>
  );
}

// ─── Main CRM ─────────────────────────────────────────────────────────────────
export default function CRM() {
  const apiFetch = useApi();
  const { accounts, instance } = useMsal();
  // Phase 18: capture the signed-in user's display name so we can stamp it
  // onto newly created activities/tasks (`created_by_name`) and surface it
  // in the activity feed view. Same account-pick order as useApi.ts.
  const currentUserName = useMemo(() => {
    const a = instance.getActiveAccount() ?? accounts.find(x => x.tenantId === MAJANS_TENANT_ID) ?? accounts[0];
    return a?.name || a?.username || '';
  }, [accounts, instance]);
  // The oid — same claim the API keys correspondence contributions on, so the
  // page can tell "your summary" from a colleague's without matching on email.
  const currentUserOid = useMemo(() => {
    const a = instance.getActiveAccount() ?? accounts.find(x => x.tenantId === MAJANS_TENANT_ID) ?? accounts[0];
    return a?.idTokenClaims?.oid || a?.localAccountId || '';
  }, [accounts, instance]);

  const [data, setData]         = useState(DEFAULT_CRM);
  const [selCust, setSelCust]   = useState(Object.keys(SEED_CUSTOMERS)[0]);
  const [tab, setTab]           = useState('overview');
  // Phase 18: top-level view toggle. 'customer' = existing per-customer view;
  // 'feed' = cross-customer activity stream.
  const [view, setView]         = useState('customer');
  const [search, setSearch]     = useState('');
  const [modal, setModal]       = useState(null); // { kind, item }
  const [segFilter, setSegFilter] = useState('All');
  // Primary Majans account manager filter (Matthew, 17 Sep). "Primary" = the FIRST entry in
  // majans_owners, which is the same convention 057 uses for the primary contact: the list is
  // ordered, and the person at the top is the one who owns the account.
  const [ownerFilter, setOwnerFilter] = useState('All');
  // Per-tab filters. Each is 'All' by default; selecting a value narrows the list.
  const [actTypeFilter,    setActTypeFilter]    = useState('All');
  const [actStatusFilter,  setActStatusFilter]  = useState('All'); // All | Open | Completed
  const [taskStatusFilter, setTaskStatusFilter] = useState('Active'); // Active | All | <STATUS>
  const [taskPrioFilter,   setTaskPrioFilter]   = useState('All');
  // Bulk-select state for activities / tasks. Cleared when switching customer.
  const [selActIds,  setSelActIds]  = useState(() => new Set());
  const [selTaskIds, setSelTaskIds] = useState(() => new Set());
  useEffect(() => { setSelActIds(new Set()); setSelTaskIds(new Set()); }, [selCust]);

  // Phase 3 migration machinery — same recipe as ChannelStrategy.
  // - hasHydrated:     hydration runs exactly once on first successful API load
  // - mutateRef:       stable ref to the React Query mutate fn (avoids identity loop)
  // - lastSavedJsonRef: last-saved JSON; effect short-circuits if no change
  // - pendingSaveRef:  true while the debounce timer is armed (drives "unsaved" badge + unmount flush)
  // - latestDataRef:   always-current data, read by the unmount flush
  // - knownUpdatedAtRef: server's last-known UpdatedAt; sent as If-Match for concurrency protection
  const hasHydrated = useRef(false);
  const lastSavedJsonRef = useRef(null);
  const pendingSaveRef = useRef(false);
  const latestDataRef = useRef(null);
  const knownUpdatedAtRef = useRef(null);
  // #3 cutover: last-synced snapshot of {customers,contacts,activities,tasks} for
  // per-entity diff (PUT changed/added, DELETE removed) against the typed endpoints.
  const syncedRef = useRef({ customers: {}, contacts: {}, activities: {}, tasks: {} });
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastSavedByName, setLastSavedByName] = useState(null); // Phase 17: who last wrote the team blob
  const [conflict, setConflict] = useState(null); // { current: {data, updatedAt}, mine: data } when a 409 arrives
  // Bumped on every render that flips pendingSaveRef so the SaveStatus indicator re-renders.
  const [, setStatusTick] = useState(0);
  const bumpStatus = () => setStatusTick(t => t + 1);

  // Phase 17: READS now come from the team-shared blob so Hayley's edits are
  // visible to James. WRITES still go to /api/crm-data (which dual-writes to
  // both per-user and team blobs — see Phase 16). Concurrency is on per-user
  // only; the team blob is last-writer-wins for now.
  // #3: reads now come from the typed Crm* tables via /crm-board, which returns
  // the same {customers,contacts,activities,tasks} shape the page already renders.
  const { data: serverData, error: serverError, refetch: refetchCrm, isFetching: isLoadingCrm } = useQuery({
    queryKey: ['crm-board'],
    queryFn: async () => {
      const res = await apiFetch('/crm-board');
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`Failed to load CRM: HTTP ${res.status} ${body.slice(0, 200)}`);
      }
      const board = await res.json();
      return { data: board, updatedAt: null, updatedByName: null };
    },
    refetchOnMount: 'always',
    retry: false,
  });
  // Surface load errors to the console (otherwise the page silently shows defaults)
  useEffect(() => {
    if (serverError) {
      // eslint-disable-next-line no-console
      console.error('[CRM] failed to load /crm-data', serverError);
    }
  }, [serverError]);

  // #3: persist by diffing the whole blob against the last-synced snapshot and
  // issuing per-entity PUT/DELETE to the typed endpoints (customers are upserted,
  // never deleted — they're master data). Last-writer-wins per row (no If-Match).
  const saveCrm = useMutation({
    mutationFn: async (next) => {
      const prev = syncedRef.current || { customers: {}, contacts: {}, activities: {}, tasks: {} };
      const ops = [];
      const diff = (n, p, base, isCustomer) => {
        for (const id of Object.keys(n || {})) {
          if (JSON.stringify(n[id]) !== JSON.stringify((p || {})[id] ?? null)) {
            // A customer the baseline has never seen is a CREATE, and the server is told
            // so: the upsert would otherwise land silently on an existing code and
            // overwrite that customer (Matthew / CUS0207 / Virgin Australia, 17 Sep).
            const isNew = isCustomer && !(p || {})[id];
            ops.push({
              m: 'PUT',
              url: isCustomer
                ? `/crm-customer/${encodeURIComponent(id)}${isNew ? '?mode=create' : ''}`
                : `/${base}`,
              body: n[id],
            });
          }
        }
        if (!isCustomer) {
          for (const id of Object.keys(p || {})) {
            if (!(n || {})[id]) ops.push({ m: 'DELETE', url: `/${base}/${encodeURIComponent(id)}` });
          }
        }
      };
      diff(next.customers, prev.customers, 'crm-customer', true);
      diff(next.contacts, prev.contacts, 'crm-contact', false);
      diff(next.activities, prev.activities, 'crm-activity', false);
      diff(next.tasks, prev.tasks, 'crm-task', false);
      // The server can redirect an activity write onto a row we didn't know about
      // — someone else already logged that email thread or meeting. It tells us so
      // via {id, requestedId, folded}; if we ignored that (we used to) our local
      // id would be a phantom that never matches the server, and every later edit
      // would keep re-folding onto their row.
      const remaps = [];
      for (const op of ops) {
        const res = await apiFetch(op.url, op.m === 'DELETE' ? { method: 'DELETE' } : { method: 'PUT', body: JSON.stringify(op.body) });
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(`CRM save failed (${op.m} ${op.url}): HTTP ${res.status} ${body.slice(0, 160)}`);
        }
        if (op.m === 'PUT' && op.url === '/crm-activity') {
          const j = await res.json().catch(() => null);
          if (j?.folded && j.id && j.requestedId && j.id !== j.requestedId) remaps.push([j.requestedId, j.id]);
        }
      }

      let settled = next;
      if (remaps.length > 0) {
        const activities = { ...next.activities };
        for (const [mine, theirs] of remaps) {
          const row = activities[mine];
          if (!row) continue;
          delete activities[mine];
          activities[theirs] = { ...row, id: theirs };
        }
        settled = { ...next, activities };
        // Adopt the server's ids locally so the board and the diff baseline agree.
        setData(cur => {
          const a = { ...cur.activities };
          for (const [mine, theirs] of remaps) {
            const row = a[mine];
            if (!row) continue;
            delete a[mine];
            a[theirs] = { ...row, id: theirs };
          }
          return { ...cur, activities: a };
        });
      }

      syncedRef.current = JSON.parse(JSON.stringify(settled));
      return { ops: ops.length, folded: remaps.length };
    },
    onSuccess: () => { setLastSavedAt(new Date()); setLastSavedByName(null); },
    onError: (err) => { console.error('[CRM] save error', err); },
  });
  const mutateRef = useRef(saveCrm.mutate);
  mutateRef.current = saveCrm.mutate;

  // Hydrate local state from API response (runs once).
  useEffect(() => {
    if (hasHydrated.current || !serverData) return;
    hasHydrated.current = true;

    // Strict shape check: must be a plain object with at least one of the
    // expected top-level keys. Catches the case where the API returns the
    // raw stored string (JSON.parse failure server-side falls back to the
    // raw value) instead of a parsed object.
    const d = serverData.data;
    const isValidCrmShape = d
      && typeof d === 'object'
      && !Array.isArray(d)
      && (d.customers || d.contacts || d.activities || d.tasks);

    if (isValidCrmShape) {
      setData(d);
      lastSavedJsonRef.current = JSON.stringify(d);
      syncedRef.current = JSON.parse(JSON.stringify(d)); // diff baseline
      if (serverData.updatedAt) setLastSavedAt(new Date(serverData.updatedAt));
      if (serverData.updatedByName) setLastSavedByName(serverData.updatedByName);
      return;
    }

    // No/invalid data from the board — keep DEFAULT_CRM as the baseline so we
    // don't immediately PUT defaults. (The localStorage migration is retired —
    // the source of truth is now the typed Crm* tables.)
    if (d != null) {
      // eslint-disable-next-line no-console
      console.error('[CRM] /crm-board returned an unexpected shape; using defaults', { type: typeof d, isArray: Array.isArray(d) });
    }
    lastSavedJsonRef.current = JSON.stringify(data);
    syncedRef.current = JSON.parse(JSON.stringify(data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverData]);

  // Debounced save on data change. Skip until hydrated AND only when changed.
  // Maintains pendingSaveRef so the SaveStatus indicator and unmount flush can
  // observe whether there is queued-but-unsaved work.
  const saveTimer = useRef(null);
  useEffect(() => {
    latestDataRef.current = data;
    if (!hasHydrated.current) return;
    const currentJson = JSON.stringify(data);
    if (currentJson === lastSavedJsonRef.current) return;
    if (!pendingSaveRef.current) { pendingSaveRef.current = true; bumpStatus(); }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      mutateRef.current(data);
      lastSavedJsonRef.current = currentJson;
      pendingSaveRef.current = false;
      bumpStatus();
    }, 1000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [data]);

  // Unmount flush: if the user navigates away while a debounce timer is armed,
  // fire the save synchronously so the last edit isn't silently dropped.
  // Runs once on unmount — does NOT depend on `data` so its closure stays
  // empty; the latest value is read from latestDataRef.
  useEffect(() => {
    return () => {
      if (!pendingSaveRef.current) return;
      const latest = latestDataRef.current;
      if (latest == null) return;
      const currentJson = JSON.stringify(latest);
      if (currentJson !== lastSavedJsonRef.current) {
        mutateRef.current(latest);
      }
    };
  }, []);

  // Warn the user before closing the tab if there's queued-but-unsaved work.
  // The async save will continue from cleanup above, but warning gives the
  // user a chance to wait for the indicator to flip to "Saved".
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (pendingSaveRef.current || saveCrm.isPending) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [saveCrm.isPending]);

  function upd(newData) { setData(newData); }

  // Renaming or deleting a segment rewrites Customer.Channel server-side, but the board
  // hydrates ONCE (hasHydrated), so nothing would bring that back into the open page —
  // and the next edit of an affected customer would write the old label straight back.
  // Apply the same remap to local state AND to both save baselines: the server has
  // already persisted it, so it must not read as an unsaved edit. Remapping the
  // baselines (rather than replacing them wholesale) keeps any genuinely pending edit.
  function applySegmentRemap(from, to) {
    const remap = (obj) => {
      if (!obj || !obj.customers) return obj;
      const customers = {};
      for (const [code, c] of Object.entries(obj.customers)) {
        customers[code] = c.segment === from ? { ...c, segment: to } : c;
      }
      return { ...obj, customers };
    };
    setData(cur => remap(cur));
    syncedRef.current = remap(syncedRef.current);
    if (lastSavedJsonRef.current) {
      try {
        lastSavedJsonRef.current = JSON.stringify(remap(JSON.parse(lastSavedJsonRef.current)));
      } catch { /* unparseable baseline — leave it; the diff guard just re-saves once */ }
    }
  }

  const segments = useMemo(() => ['All', ...new Set(Object.values(data.customers).map(c => c.segment))].sort(), [data.customers]);

  // Derived from the customers themselves, so the list can never offer someone who owns
  // nothing, and never miss someone who does.
  const owners = useMemo(() => ['All', ...new Set(
    Object.values(data.customers).map(primaryOwnerName).filter(Boolean),
  )].sort((a, b) => (a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b))), [data.customers]);

  const custList = useMemo(() => {
    let list = Object.values(data.customers).sort((a, b) => a.name.localeCompare(b.name));
    if (segFilter !== 'All') list = list.filter(c => c.segment === segFilter);
    if (ownerFilter !== 'All') list = list.filter(c => primaryOwnerName(c) === ownerFilter);
    if (search) {
      const q = search.toLowerCase();
      // Index customer_code → bool for any contact whose name/email matches.
      // Cheaper than O(customers × contacts) when many customers but few contacts overall.
      const contactHit = new Set();
      for (const ct of Object.values(data.contacts)) {
        if (contactName(ct).toLowerCase().includes(q) || (ct.email || '').toLowerCase().includes(q)) {
          contactHit.add(ct.customer_code);
        }
      }
      list = list.filter(c =>
        c.name.toLowerCase().includes(q) ||
        (c.short_name || '').toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        contactHit.has(c.code)
      );
    }
    return list;
  }, [data.customers, data.contacts, search, segFilter, ownerFilter]);

  const cust       = data.customers[selCust];
  const contacts   = useMemo(() => Object.values(data.contacts).filter(c => c.customer_code === selCust).sort((a, b) => contactName(a).localeCompare(contactName(b))), [data.contacts, selCust]);
  const activities = useMemo(() => Object.values(data.activities).filter(a => a.customer_code === selCust).sort((a, b) => cmpDesc(a.date, b.date)), [data.activities, selCust]);
  const tasks      = useMemo(() => Object.values(data.tasks).filter(t => t.customer_code === selCust).sort((a, b) => cmpAsc(a.due_date, b.due_date)), [data.tasks, selCust]);

  /*
   * 057: the Majans-side name suggestions. Built entirely from people already
   * present in the data — this account's owners, anyone who has logged or been
   * assigned anything, plus you — so it self-populates as the team uses the CRM
   * and no name is ever hardcoded (the Governance user picker works the same way).
   *
   * Deliberately NOT a Graph /users directory lookup: that needs a User.Read.All
   * grant on the app registration SHARED with app-magic, which is a permission
   * decision, not an implementation detail.
   */
  const staffSuggestions = useMemo(() => {
    const names = new Set();
    for (const o of (cust?.majans_owners ?? [])) if (o?.name) names.add(o.name.trim());
    for (const a of Object.values(data.activities)) {
      if (a.created_by_name) names.add(a.created_by_name.trim());
      for (const at of (Array.isArray(a.attendees) ? a.attendees : [])) {
        if (at.side === 'majans' && at.name) names.add(at.name.trim());
      }
    }
    for (const t of Object.values(data.tasks)) {
      if (t.created_by_name) names.add(t.created_by_name.trim());
      if (t.owner) names.add(t.owner.trim());
    }
    if (currentUserName) names.add(currentUserName.trim());
    names.delete('');
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [cust, data.activities, data.tasks, currentUserName]);

  // Filtered views for the Activities / Tasks tabs. Kept separate from the raw
  // lists so tab badge counts (and dependent UI) still reflect totals.
  const filteredActivities = useMemo(() => activities.filter(a =>
    (actTypeFilter   === 'All' || a.type === actTypeFilter) &&
    (actStatusFilter === 'All'
      || (actStatusFilter === 'Open'      && !a.completed)
      || (actStatusFilter === 'Completed' &&  a.completed)
    )
  ), [activities, actTypeFilter, actStatusFilter]);
  const filteredTasks = useMemo(() => tasks.filter(t =>
    (taskStatusFilter === 'All' ||
     (taskStatusFilter === 'Active' && t.status !== 'Done') ||
     t.status === taskStatusFilter) &&
    (taskPrioFilter === 'All' || t.priority === taskPrioFilter)
  ), [tasks, taskStatusFilter, taskPrioFilter]);

  const openTasks  = useMemo(() => Object.values(data.tasks).filter(t => t.status !== 'Done').sort((a, b) => cmpAsc(a.due_date, b.due_date)), [data.tasks]);
  const today      = new Date().toISOString().slice(0, 10);
  /*
   * Matthew, 17 Sep: "any overdue tasks over the right side on the panel should also filter by
   * the selection on the drop down". Both dropdowns, not just the owner one — a rail that
   * ignored the segment filter while honouring the owner filter would be the odd one out.
   * A task on a customer that is off the board (retired, or filtered away) is dropped with it:
   * the rail is a view of what you are looking at, not a second inbox.
   */
  const overdueTasks = useMemo(() => {
    const visible = new Set(custList.map(c => c.code));
    return openTasks.filter(t => t.due_date && t.due_date < today && visible.has(t.customer_code));
  }, [openTasks, custList, today]);

  // Phase 20 helper: stamp updated_by_name + updated_at on every save (create
  // OR edit). Distinct from Phase 18's created_by_name which only stamps on
  // first create. Together they give "who created this" + "who last touched it".
  function stamp(entity) {
    return { ...entity, updated_by_name: currentUserName || '', updated_at: new Date().toISOString() };
  }

  function saveCust(c, oldCode) {
    const renamed = !!oldCode && oldCode !== c.code;
    const stamped = stamp(c);
    let next;
    if (renamed) {
      // The /crm-customer-rename endpoint already moved the customer + repointed its
      // children in the DB; mirror that locally and realign the diff baseline so the
      // debounced saver doesn't re-PUT the moved rows (or strand the old key).
      const customers = { ...data.customers };
      delete customers[oldCode];
      customers[c.code] = stamped;
      const repoint = coll => Object.fromEntries(
        Object.entries(coll).map(([id, r]) => [id, r.customer_code === oldCode ? { ...r, customer_code: c.code } : r])
      );
      next = { ...data, customers, contacts: repoint(data.contacts), activities: repoint(data.activities), tasks: repoint(data.tasks) };
      syncedRef.current = JSON.parse(JSON.stringify(next));
      lastSavedJsonRef.current = JSON.stringify(next);
    } else {
      next = { ...data, customers: { ...data.customers, [c.code]: stamped } };
    }
    setData(next);
    setSelCust(c.code);
    setModal(null);
  }

  /*
   * Adopt = bring an account that already exists in the customer master onto the CRM board.
   *
   * The server has already stamped Customer.CrmAddedAt by the time we get here, so this is
   * purely the local mirror — and it has to touch BOTH save baselines, not just state. The
   * board hydrates once (hasHydrated), so a refetch will not reach the open page; and if the
   * new customer were only in `data` and not in the baselines, the debounced saver would read
   * it as a brand-new customer and PUT it back with ?mode=create, which the server would then
   * refuse as a collision with the very row we just adopted. Same trap as applySegmentRemap.
   */
  async function adoptCust(code) {
    if (!code) return;
    let res;
    try {
      res = await apiFetch(`/crm-customer/${encodeURIComponent(code)}/adopt`, { method: 'POST' });
    } catch {
      alert('Network error adding that customer — please try again.');
      return;
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      alert(body?.error || `Couldn't add that customer (HTTP ${res.status}).`);
      return;
    }
    const { name } = await res.json();
    // Only the master fields exist yet — no contacts, activities or tasks, which is the
    // honest state of an account nobody has worked in the CRM. is_current mirrors the
    // master's IsActive default rather than asserting anything new about the account.
    const adopted = {
      code, name, short_name: '', segment: '', majans_owners: [], notes: '',
      is_current: true, head_office: '', website: '',
    };
    const add = (obj) => (obj && obj.customers
      ? { ...obj, customers: { ...obj.customers, [code]: adopted } }
      : obj);
    setData(cur => add(cur));
    syncedRef.current = add(syncedRef.current);
    if (lastSavedJsonRef.current) {
      try {
        lastSavedJsonRef.current = JSON.stringify(add(JSON.parse(lastSavedJsonRef.current)));
      } catch { /* unparseable baseline — leave it; the diff guard just re-saves once */ }
    }
    setSelCust(code);
  }

  /*
   * Retire = take the account off the CRM board without destroying anything. This is the
   * answer when delete refuses because pricing / stores / scan data still reference it.
   *
   * ⚠ Retiring is a COLUMN (migration 067), never a note. Migration 059 retired six
   * duplicates by writing "Retired … duplicate of CUS…" into Notes, and because the board
   * includes any customer carrying a note, the breadcrumb is what kept them visible. If you
   * are ever tempted to record state in Notes, this is the bug you are about to rewrite.
   */
  async function retireCust(code) {
    const cust = data.customers[code];
    if (!cust) return;
    const reason = prompt(`Retire ${cust.name}?\n\nThis takes it off the CRM board. Nothing is deleted.\n\nWhy is it being retired?`, 'Duplicate account merged');
    if (reason === null) return;
    const supersededBy = (prompt('If its records were merged into another account, enter that customer code (e.g. CUS0032).\n\nLeave blank if there isn\'t one.', '') || '').trim().toUpperCase();

    let res;
    try {
      res = await apiFetch(`/crm-customer/${encodeURIComponent(code)}/retire`, {
        method: 'POST', body: JSON.stringify({ reason, supersededBy: supersededBy || null }),
      });
    } catch {
      alert('Network error retiring this customer — nothing was changed.');
      return;
    }
    if (!res.ok) {
      let msg = `Couldn't retire this customer (HTTP ${res.status}).`;
      const t = await res.text().catch(() => '');
      try { msg = JSON.parse(t).error || msg; } catch { /* keep default */ }
      alert(msg);
      return;
    }

    // It is off the board now, so drop it locally and move the baselines with it —
    // same reasoning as delCust: the pending diff must not try to re-save a row the
    // board will no longer return.
    const d = JSON.parse(JSON.stringify(data));
    delete d.customers[code];
    syncedRef.current = JSON.parse(JSON.stringify(d));
    lastSavedJsonRef.current = JSON.stringify(d);
    setData(d);
    setSelCust(Object.keys(d.customers)[0] || null);
  }

  /*
   * Merge = move EVERY record (contacts, activities, pricing, ranging column + cells, …) onto
   * another account, then retire this one as superseded by it. One server call, one
   * transaction (crmMerge.ts); the Ranging Matrix column menu calls the same endpoint. The
   * server dry-runs first so the confirm names exactly what moves, and refuses (409) when
   * both accounts hold a record with the same key rather than picking a winner.
   */
  async function mergeCust(code) {
    const cust = data.customers[code];
    if (!cust) return;
    const to = (prompt(`Merge ${cust.name} (${code}) into which customer?\n\nEnter the surviving account's code (e.g. CUS0206). Every contact, activity, task, pricing and ranging record moves to it, and ${code} is retired as merged.`, '') || '').trim().toUpperCase();
    if (!to) return;
    const call = async (dryRun) => {
      let res;
      try {
        res = await apiFetch(`/crm-customer/${encodeURIComponent(code)}/merge-into/${encodeURIComponent(to)}`, { method: 'POST', body: JSON.stringify({ dryRun }) });
      } catch { throw new Error('Network error — nothing was changed.'); }
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || `Couldn't merge (HTTP ${res.status}).`);
      return j;
    };
    let plan;
    try { ({ plan } = await call(true)); } catch (e) { alert(e.message); return; }
    const lines = plan.moves.map((m) => `• ${m.n} ${m.label}`);
    if (plan.supplyRouteCopied) lines.push(`• supply method copied to ${to}, which had none`);
    const summary = lines.join('\n') || '• nothing to move';
    if (!confirm(`Move everything on ${plan.from.name} (${code}) to ${plan.to.name} (${to})?\n\n${summary}\n\n${code} is then retired as merged into ${to}. This is recorded in the audit trail.`)) return;
    try { await call(false); } catch (e) { alert(e.message); return; }

    // Same as retireCust: it is off the board now, and the target's records changed
    // server-side, so refetch rather than patch — the board hydrates once, so reload.
    window.location.reload();
  }

  /*
   * Deleting a customer goes to the server FIRST and local state only follows.
   *
   * It used to be local-only. The board diff skips customers by design ("upserted,
   * never deleted — they're master data"), so the customer came straight back on the
   * next load — which is what Matthew reported on 2026-09-17. Worse, the diff DID
   * pick up the contacts, activities and tasks this function removed alongside it,
   * so the delete quietly destroyed the relationship history and left the customer.
   *
   * DELETE /api/crm-customer/{code} now owns the whole operation: it refuses (409)
   * while pricing, promo, trade, ranging, store or scan records still reference the
   * account, and removes the CRM's own children with it when they don't.
   */
  async function delCust(code) {
    const cust = data.customers[code];
    if (!cust) return;
    const kids = [
      Object.values(data.contacts).filter(x => x.customer_code === code).length,
      Object.values(data.activities).filter(x => x.customer_code === code).length,
      Object.values(data.tasks).filter(x => x.customer_code === code).length,
    ];
    const kidNote = kids.some(Boolean)
      ? `\n\nThis also removes ${kids[0]} contact(s), ${kids[1]} activity(ies) and ${kids[2]} task(s).`
      : '';
    if (!confirm(`Delete ${cust.name}?${kidNote}`)) return;

    let res;
    try {
      res = await apiFetch(`/crm-customer/${encodeURIComponent(code)}`, { method: 'DELETE' });
    } catch {
      alert('Network error deleting this customer — nothing was changed.');
      return;
    }
    if (!res.ok) {
      let msg = `Couldn't delete this customer (HTTP ${res.status}).`;
      const t = await res.text().catch(() => '');
      try { msg = JSON.parse(t).error || msg; } catch { /* keep default */ }
      alert(msg);
      return;
    }

    // Server is done. Mirror it locally AND into the diff baseline, so the pending
    // save doesn't then issue deletes for rows the server has already removed.
    const d = JSON.parse(JSON.stringify(data));
    delete d.customers[code];
    for (const id of Object.keys(d.contacts))   { if (d.contacts[id].customer_code   === code) delete d.contacts[id]; }
    for (const id of Object.keys(d.activities)) { if (d.activities[id].customer_code === code) delete d.activities[id]; }
    for (const id of Object.keys(d.tasks))      { if (d.tasks[id].customer_code      === code) delete d.tasks[id]; }
    syncedRef.current = JSON.parse(JSON.stringify(d));
    lastSavedJsonRef.current = JSON.stringify(d);
    setData(d);
    setSelCust(Object.keys(d.customers)[0] || null);
  }

  function saveContact(c) {
    const id = c.id || uid();
    upd({ ...data, contacts: { ...data.contacts, [id]: stamp({ ...c, id, customer_code: selCust }) } });
    setModal(null);
  }
  function delContact(id) {
    const c = data.contacts[id];
    if (!c) return;
    if (!confirm(`Delete contact ${contactName(c) || 'this contact'}?`)) return;
    const d = JSON.parse(JSON.stringify(data));
    delete d.contacts[id];
    upd(d);
  }

  /*
   * 057: saving an activity also reconciles the tasks its Next Actions produced.
   *
   * `next_actions` is editor state, not a column — each ticked row IS a
   * commercial.CrmTask carrying activity_id back to this activity. Writing the
   * text onto the activity as well would drift the moment someone edited one
   * side, so the legacy `next_action` string is cleared once rows exist.
   *
   * Removing or unticking a row deletes its task ONLY while that task is still
   * Open. Anything In Progress or Done is left in the Tasks tab, merely detached
   * — tidying an agenda must never delete work someone has already started.
   *
   * Everything lands in `data`, so the existing blob-diff saver emits the right
   * PUT/DELETE calls by itself; no new save machinery.
   */
  function saveActivity(a) {
    const id = a.id || uid();
    // Phase 18: stamp created_by_name on NEW activities only (preserve on edit).
    const existing = data.activities[id];
    const created_by_name = existing?.created_by_name ?? currentUserName ?? '';
    const rows = Array.isArray(a.next_actions) ? a.next_actions : [];
    const { next_actions: _drop, ...activityFields } = a;

    const tasks = { ...data.tasks };
    const kept = new Set();
    for (const r of rows) {
      if (r.make_task === false || !(r.text || '').trim()) continue;
      const taskId = r.task_id || uid();
      const prior = tasks[taskId];
      kept.add(taskId);
      const candidate = {
        ...prior,
        id: taskId,
        customer_code: selCust,
        activity_id: id,
        contact_id: prior?.contact_id || a.contact_id || '',
        title: r.text.trim(),
        owner: r.owner || '',
        due_date: r.due_date || '',
        priority: r.priority || 'Medium',
        // Preserve progress: re-saving the meeting must not reset a task to Open.
        status: prior?.status || 'Open',
        notes: prior?.notes || '',
        created: prior?.created || new Date().toISOString().slice(0, 10),
        created_by_name: prior?.created_by_name || currentUserName || '',
        // Compared BEFORE stamping — stamp() moves updated_at every time, which
        // would make an untouched task look changed and re-PUT every linked task
        // on every save of the meeting.
        updated_by_name: prior?.updated_by_name,
        updated_at: prior?.updated_at,
      };
      tasks[taskId] = JSON.stringify(candidate) === JSON.stringify(prior) ? prior : stamp(candidate);
    }
    for (const t of Object.values(data.tasks)) {
      if (t.activity_id !== id || kept.has(t.id)) continue;
      if ((t.status || 'Open') === 'Open') delete tasks[t.id];
      else tasks[t.id] = stamp({ ...t, activity_id: '' });
    }

    const activity = stamp({
      ...activityFields, id, customer_code: selCust, created_by_name,
      // Converted: the one-line legacy field would otherwise render alongside
      // the very tasks it just became.
      next_action: rows.length > 0 ? '' : (a.next_action || ''),
    });
    upd({ ...data, activities: { ...data.activities, [id]: activity }, tasks });
    setModal(null);
  }
  function delActivity(id) {
    const a = data.activities[id];
    if (!a) return;
    /*
     * 057: the tasks this meeting produced are NOT deleted with it — the DB's FK
     * is ON DELETE SET NULL for exactly this reason, since deleting a mis-typed
     * activity must not silently bin work someone is part-way through. Mirror
     * that locally so the page and the DB agree before the next refetch, and say
     * so in the prompt rather than letting it be a surprise.
     */
    const linked = nextActionTasks(data.tasks, id);
    const note = linked.length > 0
      ? `\n\n${linked.length} task${linked.length > 1 ? 's' : ''} from its next actions will be KEPT (unlinked), not deleted.`
      : '';
    if (!confirm(`Delete activity "${a.subject || 'untitled'}"?${note}`)) return;
    const d = JSON.parse(JSON.stringify(data));
    delete d.activities[id];
    for (const t of linked) d.tasks[t.id] = { ...d.tasks[t.id], activity_id: '' };
    upd(d);
  }

  function saveTask(t) {
    const id = t.id || uid();
    // Phase 18: same created_by_name stamping as saveActivity.
    const existing = data.tasks[id];
    const created_by_name = existing?.created_by_name ?? currentUserName ?? '';
    upd({ ...data, tasks: { ...data.tasks, [id]: stamp({ ...t, id, customer_code: selCust, created_by_name }) } });
    setModal(null);
  }
  // Accept one AI-proposed follow-up from the correspondence card. Goes through
  // the same task write as everything else — governed + audited server-side — so
  // an AI suggestion becomes a real task only on a human click, never on its own.
  /*
   * Accept AI-proposed activities into Recent Activity (Matthew, 2026-08-04).
   * Takes an ARRAY so "Add all" is one state update and one save cycle rather
   * than N racing ones. `source_key` rides along so the server MERGE converges on
   * the same row if the same thread is ever accepted twice.
   */
  function addSuggestedActivities(list) {
    if (!list || list.length === 0) return;
    const contactsForCust = Object.values(data.contacts).filter(c => c.customer_code === selCust);
    const next = { ...data.activities };
    for (const a of list) {
      // Match the proposal's correspondent back to a CRM contact so the activity
      // links to a person, not just a customer.
      const match = a.contactEmail
        ? contactsForCust.find(c => (c.email || '').trim().toLowerCase() === a.contactEmail.trim().toLowerCase())
        : null;
      // Reuse the existing row if this source was already logged, so accepting
      // twice edits rather than duplicates (the server enforces this too).
      const existing = Object.values(data.activities).find(
        x => x.customer_code === selCust && x.source_key && x.source_key === a.sourceKey,
      );
      const id = existing?.id || uid();
      /*
       * 057: a thread or a meeting invite knows EVERY correspondent, so an
       * accepted proposal arrives with its full attendee list rather than one
       * name. Addresses that match a CRM contact link to it; the rest are kept
       * as their raw address so "who else was on this" isn't silently dropped.
       * An existing row's attendees win — accepting twice must not overwrite
       * what a human has since curated (the additive-fold rule from 056).
       */
      const attendees = (existing?.attendees?.length ? existing.attendees : (a.participantEmails || [])
        .map(email => {
          const c = contactsForCust.find(k => (k.email || '').trim().toLowerCase() === String(email).trim().toLowerCase());
          return c
            ? { id: uid(), side: 'customer', contact_id: c.id, name: contactName(c), email: c.email || '', title: c.title || '' }
            : { id: uid(), side: 'customer', contact_id: '', name: String(email), email: String(email), title: '' };
        }));
      next[id] = stamp({
        id,
        customer_code: selCust,
        attendees,
        contact_id: match?.id || existing?.contact_id || '',
        type: a.type,
        date: a.date,
        subject: a.subject,
        location: a.location || '',
        notes: a.notes || '',
        outcome: a.outcome || '',
        next_action: a.nextAction || '',
        completed: true,          // it already happened — it's a log, not a plan
        source_type: a.sourceType,
        source_key: a.sourceKey,
        created: existing?.created || new Date().toISOString().slice(0, 10),
        created_by_name: existing?.created_by_name || currentUserName || '',
      });
    }
    upd({ ...data, activities: next });
  }

  function addSuggestedTask(t) {
    const id = uid();
    upd({ ...data, tasks: { ...data.tasks, [id]: stamp({
      id,
      customer_code: selCust,
      title: t.title,
      due_date: t.due_date || '',
      priority: t.priority || 'Medium',
      status: 'Open',
      notes: t.notes || '',
      contact_id: '',
      created: new Date().toISOString().slice(0, 10),
      created_by_name: currentUserName || '',
    }) } });
  }

  function delTask(id) {
    const t = data.tasks[id];
    if (!t) return;
    if (!confirm(`Delete task "${t.title || 'untitled'}"?`)) return;
    const d = JSON.parse(JSON.stringify(data));
    delete d.tasks[id];
    upd(d);
  }

  // ─── Bulk delete ───────────────────────────────────────────────────────────
  function toggleSel(setter, id) {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleSelAll(setter, ids, allSelected) {
    setter(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  }
  function bulkDelete(kind, ids) {
    if (ids.size === 0) return;
    const noun = kind === 'activities' ? 'activities' : 'tasks';
    if (!confirm(`Delete ${ids.size} ${noun}? This cannot be undone.`)) return;
    const d = JSON.parse(JSON.stringify(data));
    for (const id of ids) {
      delete d[kind][id];
      // 057: same rule as delActivity — the tasks a deleted meeting produced are
      // unlinked, never deleted (the FK is ON DELETE SET NULL server-side).
      if (kind === 'activities') {
        for (const t of nextActionTasks(d.tasks, id)) d.tasks[t.id] = { ...d.tasks[t.id], activity_id: '' };
      }
    }
    upd(d);
    if (kind === 'activities') setSelActIds(new Set());
    else setSelTaskIds(new Set());
  }

  function cycleTaskStatus(id) {
    const t = data.tasks[id]; if (!t) return;
    const next = STATUSES[(STATUSES.indexOf(t.status) + 1) % STATUSES.length];
    upd({ ...data, tasks: { ...data.tasks, [id]: { ...t, status: next } } });
  }

  // ─── JSON import (restore from a downloaded backup) ────────────────────────
  // Reads a user-selected .json file, validates shape, shows a diff summary,
  // and replaces local state on confirm. The next debounced save writes it out.
  async function importJson(file) {
    if (!file) return;
    let parsed;
    try {
      const text = await file.text();
      parsed = JSON.parse(text);
    } catch (err) {
      alert(`Could not parse file as JSON: ${err.message}`);
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      alert('Backup file does not contain a CRM object.');
      return;
    }
    if (!parsed.customers && !parsed.contacts && !parsed.activities && !parsed.tasks) {
      alert('Backup file does not look like a CRM export (no customers/contacts/activities/tasks).');
      return;
    }
    if (typeof parsed._version === 'number' && parsed._version > CRM_SCHEMA_VERSION) {
      if (!confirm(`This backup was created with a newer schema (v${parsed._version} > v${CRM_SCHEMA_VERSION}). Importing may lose fields this version doesn't understand. Continue?`)) return;
    }
    const summary = [
      `Customers:  ${Object.keys(data.customers).length} → ${Object.keys(parsed.customers || {}).length}`,
      `Contacts:   ${Object.keys(data.contacts).length} → ${Object.keys(parsed.contacts || {}).length}`,
      `Activities: ${Object.keys(data.activities).length} → ${Object.keys(parsed.activities || {}).length}`,
      `Tasks:      ${Object.keys(data.tasks).length} → ${Object.keys(parsed.tasks || {}).length}`,
    ].join('\n');
    if (!confirm(`Replace your current CRM with this backup?\n\n${summary}\n\nYour current data will be overwritten — download a backup first if you want to keep it.`)) return;
    // Fill missing top-level keys with empty objects so the shape is always valid.
    const next = {
      customers:  parsed.customers  || {},
      contacts:   parsed.contacts   || {},
      activities: parsed.activities || {},
      tasks:      parsed.tasks      || {},
    };
    setData(next);
    setSelCust(Object.keys(next.customers)[0] || null);
    // The user explicitly confirmed "overwrite" — clear the version token so
    // the next save uses the unconditional path. Otherwise the server's
    // current UpdatedAt won't match our stale token and the conflict modal
    // would fire on what was an intentional restore.
    knownUpdatedAtRef.current = null;
    // The debounced save will pick this up via the change effect; no explicit mutate needed.
  }

  // ─── Conflict resolution (409 from server) ─────────────────────────────────
  // "Use server version" — discard local edits, adopt server state.
  function resolveTakeTheirs() {
    if (!conflict?.current) return;
    const next = conflict.current.data;
    setData(next);
    const json = JSON.stringify(next);
    lastSavedJsonRef.current = json;
    knownUpdatedAtRef.current = conflict.current.updatedAt;
    if (conflict.current.updatedAt) setLastSavedAt(new Date(conflict.current.updatedAt));
    pendingSaveRef.current = false;
    bumpStatus();
    setConflict(null);
  }
  // "Keep my version" — force-overwrite: clear the version token so the next
  // save uses the unconditional MERGE path. Save immediately.
  function resolveKeepMine() {
    knownUpdatedAtRef.current = null;
    setConflict(null);
    // Immediate save; the debounced effect will mark pending=false when it completes.
    mutateRef.current(latestDataRef.current);
  }

  // ─── Export ────────────────────────────────────────────────────────────────
  function exportData(kind) {
    const stamp = new Date().toISOString().slice(0, 10);
    const custName = (code) => data.customers[code]?.short_name || data.customers[code]?.name || code;
    const contactNameById = (id) => { const c = id && data.contacts[id]; return c ? contactName(c) : ''; };

    if (kind === 'json') {
      downloadFile(`crm-backup-${stamp}.json`, JSON.stringify(data, null, 2), 'application/json');
      return;
    }
    if (kind === 'customers') {
      const rows = Object.values(data.customers).sort((a, b) => a.name.localeCompare(b.name));
      const csv = toCsv(rows, [
        { label: 'code',            get: r => r.code },
        { label: 'name',            get: r => r.name },
        { label: 'short_name',      get: r => r.short_name },
        { label: 'segment',         get: r => r.segment },
        { label: 'head_office',     get: r => r.head_office },
        { label: 'website',         get: r => r.website },
        { label: 'is_current',      get: r => r.is_current !== false },
        { label: 'notes',           get: r => r.notes },
        { label: 'created',         get: r => r.created },
      ]);
      downloadFile(`crm-customers-${stamp}.csv`, csv, 'text/csv');
      return;
    }
    if (kind === 'contacts') {
      const rows = Object.values(data.contacts).sort((a, b) => contactName(a).localeCompare(contactName(b)));
      const csv = toCsv(rows, [
        { label: 'customer_code', get: r => r.customer_code },
        { label: 'customer',      get: r => custName(r.customer_code) },
        { label: 'first_name',    get: r => r.first_name },
        { label: 'last_name',     get: r => r.last_name },
        { label: 'title',         get: r => r.title },
        { label: 'email',         get: r => r.email },
        { label: 'phone',         get: r => r.phone },
        { label: 'linkedin',      get: r => r.linkedin },
        { label: 'active',        get: r => r.active !== false },
        { label: 'notes',         get: r => r.notes },
        { label: 'created',       get: r => r.created },
      ]);
      downloadFile(`crm-contacts-${stamp}.csv`, csv, 'text/csv');
      return;
    }
    if (kind === 'activities') {
      const rows = Object.values(data.activities).sort((a, b) => cmpDesc(a.date, b.date));
      const csv = toCsv(rows, [
        { label: 'date',          get: r => r.date },
        { label: 'customer_code', get: r => r.customer_code },
        { label: 'customer',      get: r => custName(r.customer_code) },
        { label: 'type',          get: r => r.type },
        { label: 'subject',       get: r => r.subject },
        { label: 'contact',       get: r => contactNameById(r.contact_id) },
        // 057: the full room and every follow-up, so the export answers the same
        // questions the page does.
        { label: 'customer_attendees', get: r => attendeesOf(r, data.contacts).filter(p => p.side === 'customer').map(p => attendeeName(p, data.contacts)).join('; ') },
        { label: 'majans_attendees',   get: r => attendeesOf(r, data.contacts).filter(p => p.side === 'majans').map(p => attendeeName(p, data.contacts)).join('; ') },
        { label: 'location',      get: r => r.location },
        { label: 'notes',         get: r => r.notes },
        { label: 'outcome',       get: r => r.outcome },
        { label: 'next_actions',  get: r => {
          const ts = nextActionTasks(data.tasks, r.id);
          return ts.length > 0 ? ts.map(t => `${t.title}${t.owner ? ` [${t.owner}]` : ''} (${t.status})`).join('; ') : (r.next_action || '');
        } },
        { label: 'completed',     get: r => !!r.completed },
        { label: 'created',       get: r => r.created },
      ]);
      downloadFile(`crm-activities-${stamp}.csv`, csv, 'text/csv');
      return;
    }
    if (kind === 'tasks') {
      const rows = Object.values(data.tasks).sort((a, b) => cmpAsc(a.due_date, b.due_date));
      const csv = toCsv(rows, [
        { label: 'due_date',      get: r => r.due_date },
        { label: 'customer_code', get: r => r.customer_code },
        { label: 'customer',      get: r => custName(r.customer_code) },
        { label: 'title',         get: r => r.title },
        { label: 'priority',      get: r => r.priority },
        { label: 'status',        get: r => r.status },
        { label: 'owner',         get: r => r.owner || '' },
        { label: 'contact',       get: r => contactNameById(r.contact_id) },
        // 057: where this task came from, when it came out of a logged meeting.
        { label: 'from_activity', get: r => (r.activity_id && data.activities[r.activity_id]?.subject) || '' },
        { label: 'notes',         get: r => r.notes },
        { label: 'created',       get: r => r.created },
      ]);
      downloadFile(`crm-tasks-${stamp}.csv`, csv, 'text/csv');
      return;
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="crm-root" style={{ display: 'flex', gap: 0, height: 'calc(100vh - 110px)', minHeight: 500 }}>
      <style>{RESPONSIVE_CSS}</style>
      {/* Sidebar */}
      <div className="crm-sidebar" style={{ width: 240, minWidth: 240, background: 'var(--bg-elevated)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '12px 12px 8px' }}>
          <input
            placeholder="Search customers…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ ...IS, fontSize: 12, padding: '6px 10px', marginBottom: 8 }}
          />
          <select value={segFilter} onChange={e => setSegFilter(e.target.value)}
            style={{ ...IS, fontSize: 11, padding: '4px 8px', marginBottom: 6 }}>
            {segments.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}
            aria-label="Filter by Majans account manager"
            style={{ ...IS, fontSize: 11, padding: '4px 8px', marginBottom: 8 }}>
            {/* value MUST be set explicitly: without it the option's value is its LABEL, so
                picking "All account managers" set the filter to that string, which matches no
                owner and emptied the board. The segment select above gets away with it only
                because its labels and values are the same text. */}
            {owners.map(o => <option key={o} value={o}>{o === 'All' ? 'All account managers' : o}</option>)}
          </select>
          <Button onClick={() => setModal({ kind: 'customer', item: null })} variant="primary" fullWidth>
            + Add Customer
          </Button>
          {/*
            * Matthew, 18 Sep: "I just tried to add an existing customer on the CRM which is not
            * there which is MNB Variety Imports CUS0035, however, I had the following error come
            * up but it does not appear to be used already on the CRM."
            *
            * Both true at once: CUS0035 is a real customer in the master (it has pricing) but has
            * never been on the CRM board, and the 17 Sep create guard refuses a code that exists.
            * 38 of 88 live customers sit in that gap. Typing a code was the only door in, and
            * typing one that exists is exactly what overwrote Virgin Australia. So: a second
            * door, where you pick the account instead of retyping its code.
            */}
          <Button onClick={() => setModal({ kind: 'adopt-customer', item: null })} size="sm" fullWidth
            style={{ marginTop: 6 }}>
            Add an existing customer…
          </Button>
          {/* Phase 18: toggle to the cross-customer activity feed view */}
          <Button onClick={() => setView(v => v === 'feed' ? 'customer' : 'feed')}
            variant={view === 'feed' ? 'primary' : 'default'} size="sm" fullWidth
            style={{ marginTop: 6 }}>
            📰 {view === 'feed' ? '← Customer view' : 'All activity feed'}
          </Button>
          <Button onClick={() => setModal({ kind: 'export', item: null })} size="sm" fullWidth style={{ marginTop: 6 }}>
            ⇩ Export…
          </Button>
        </div>
        <div className="crm-sidebar-list" style={{ flex: 1, overflowY: 'auto' }}>
          {custList.map(c => {
            const custOpenTasks = Object.values(data.tasks).filter(t => t.customer_code === c.code && t.status !== 'Done').length;
            const custOverdue   = Object.values(data.tasks).filter(t => t.customer_code === c.code && t.status !== 'Done' && t.due_date && t.due_date < today).length;
            return (
              <div
                key={c.code}
                onClick={() => setSelCust(c.code)}
                style={{
                  padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
                  background: selCust === c.code ? 'var(--border)' : 'transparent',
                  borderLeft: `3px solid ${selCust === c.code ? 'var(--info)' : 'transparent'}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.is_current !== false ? 'var(--success)' : 'var(--text-muted)', flexShrink: 0, display: 'inline-block' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: selCust === c.code ? 'var(--text-primary)' : 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {c.short_name || c.name}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>{c.segment}</div>
                {custOpenTasks > 0 && (
                  <div style={{ fontSize: 10, marginTop: 3 }}>
                    <span style={{ color: custOverdue > 0 ? 'var(--danger)' : 'var(--warn)' }}>
                      {custOpenTasks} open task{custOpenTasks > 1 ? 's' : ''}{custOverdue > 0 ? ` · ${custOverdue} overdue` : ''}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
          {custList.length === 0 && (
            <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 12 }}>No customers found.</div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="crm-content" style={{ flex: 1, overflowY: 'auto', padding: '0 0 24px' }}>
        {/* Load-failure banner: hydration never completed, so edits won't save.
            Critical to surface — without this, users see seed defaults, edit them,
            and silently lose their work because the save effect short-circuits
            until hydration succeeds. */}
        {serverError && !hasHydrated.current && (
          <div style={{ background: 'var(--danger-soft)', borderBottom: '1px solid var(--danger-border)', color: 'var(--danger)', padding: '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13 }}>
              <strong>Could not load your CRM data.</strong>{' '}
              <span>Any edits you make below will <u>not be saved</u> until this loads successfully.</span>
              <div style={{ fontSize: 11, marginTop: 4, opacity: 0.85 }}>
                {String(serverError?.message || serverError).slice(0, 200)}
              </div>
            </div>
            <button onClick={() => refetchCrm()} disabled={isLoadingCrm}
              style={{ background: 'var(--bg-card)', border: '1px solid var(--danger)', color: 'var(--danger)', borderRadius: 'var(--radius)', padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: isLoadingCrm ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
              {isLoadingCrm ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}
        {view === 'feed' ? (
          <ActivityFeed
            data={data}
            onJumpToActivity={(a) => {
              setSelCust(a.customer_code);
              setView('customer');
              setTab('activities');
            }}
          />
        ) : !cust ? (
          <div style={{ padding: 32, color: 'var(--text-muted)' }}>Select a customer to view details.</div>
        ) : (
          <>
            {/* Customer header */}
            <div className="crm-customer-header" style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', padding: '16px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 18, fontWeight: 700 }}>{cust.name}</h2>
                    <Badge text={cust.code} color="var(--text-muted)" />
                    <Badge text={cust.segment} color="var(--info)" />
                    {cust.is_current !== false
                      ? <Badge text="Current" color="var(--success)" />
                      : <Badge text="Inactive" color="var(--text-muted)" bg="var(--bg-input)" />
                    }
                  </div>
                  {(cust.head_office || cust.website) && (
                    <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                      {cust.head_office && <span>📍 {cust.head_office}</span>}
                      {cust.website && (
                        <a href={normaliseUrl(cust.website)} target="_blank" rel="noopener noreferrer"
                           style={{ color: 'var(--info)', textDecoration: 'none' }}>
                          🔗 {displayDomain(cust.website)}
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <SaveStatus
                    isSaving={saveCrm.isPending}
                    isError={saveCrm.isError}
                    errorMessage={saveCrm.error?.message}
                    hasUnsaved={pendingSaveRef.current}
                    lastSavedAt={lastSavedAt}
                    lastSavedByName={lastSavedByName}
                    onRetry={() => mutateRef.current(data)}
                  />
                  {/* Phase 25: suggest-next-action via AI. Opens task form pre-filled. */}
                  <Button variant="ai-soft" onClick={async () => {
                    const ctx = summariseCustomerContext(data, selCust, { daysBack: 60 });
                    if (!ctx) { alert('No customer context available.'); return; }
                    try {
                      const result = await callAi(apiFetch, {
                        system: 'You suggest one concrete next action for a Majans commercial-team member based on a customer\'s recent activity. Reply with ONLY a JSON object: {"title": short imperative <=70 chars, "priority": "High"|"Medium"|"Low", "due_date": "YYYY-MM-DD" 3-14 days from today, "notes": one paragraph explaining why this is the right next step}. No markdown, no code fence.',
                        prompt: `Today: ${today}\n\n${ctx}\n\nWhat is the single most important next action for this customer?`,
                        maxTokens: 400,
                      });
                      const raw = result.text.trim().replace(/^```(?:json)?\s*/i, '').replace(/```$/, '').trim();
                      const parsed = JSON.parse(raw);
                      setModal({ kind: 'task', item: {
                        title:    parsed.title || '',
                        priority: ['High', 'Medium', 'Low'].includes(parsed.priority) ? parsed.priority : 'Medium',
                        due_date: /^\d{4}-\d{2}-\d{2}$/.test(parsed.due_date) ? parsed.due_date : '',
                        notes:    (parsed.notes || '') + '\n\n[suggested by AI — review before save]',
                        status:   'Open',
                        contact_id: '',
                      }});
                    } catch (err) {
                      alert(`Couldn't suggest a task: ${err.message}`);
                    }
                  }}>✨ Suggest task</Button>
                  <Button onClick={() => setModal({ kind: 'customer', item: cust })}>Edit</Button>
                  <Button onClick={() => retireCust(cust.code)}>Retire…</Button>
                  <Button onClick={() => mergeCust(cust.code)} title="Move every record onto another account and retire this one">Merge into…</Button>
                  <Button onClick={() => delCust(cust.code)} variant="destructive">Delete</Button>
                </div>
              </div>
              {cust.notes && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8, background: 'var(--bg-elevated)', borderRadius: 6, padding: '8px 12px', whiteSpace: 'pre-wrap' }}>
                  <RenderWithMentions text={cust.notes} />
                </div>
              )}
              <EditedCaption entity={cust} />
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
              {[
                { id: 'overview',    label: 'Overview' },
                { id: 'contacts',    label: `Contacts (${contacts.length})` },
                { id: 'activities',  label: `Activities (${activities.length})` },
                { id: 'tasks',       label: `Tasks (${tasks.filter(t => t.status !== 'Done').length} open)` },
              ].map(t => {
                const active = tab === t.id;
                return (
                  <button key={t.id} onClick={() => setTab(t.id)}
                    className={active ? 'tab-active' : 'tab-inactive'}
                    style={{
                      padding: '10px 18px',
                      background: 'none',
                      borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                      // Class supplies border-bottom for active state; inline keeps
                      // inactive at transparent so the row stays aligned.
                      borderBottom: active ? undefined : '2px solid transparent',
                      marginBottom: -1,
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: active ? 600 : 500,
                      transition: 'color 0.15s, border-color 0.15s',
                    }}>
                    {t.label}
                  </button>
                );
              })}
            </div>

            <div className="crm-tab-content" style={{ padding: '20px 24px' }}>
              {/* OVERVIEW TAB */}
              {tab === 'overview' && (
                <div className="crm-overview-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {/* Phase 23: AI summary card spans both columns. AiSummaryCard
                      paints its own purple block, so this wrapper is just for grid layout. */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <AiSummaryCard apiFetch={apiFetch} data={data} customerCode={selCust} />
                  </div>
                  {/* Correspondence summary (migration 055). Complements the card
                      above: that one summarises what's LOGGED in the CRM, this one
                      summarises what's in your INBOX. */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <CorrespondenceCard
                      apiFetch={apiFetch}
                      customerCode={selCust}
                      customer={cust}
                      contacts={contacts}
                      tasks={tasks}
                      activities={activities}
                      currentUserOid={currentUserOid}
                      onAddTask={addSuggestedTask}
                      onAddActivities={addSuggestedActivities}
                    />
                  </div>
                  {/* Recent activities */}
                  <div className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Recent Activity</span>
                      <Button onClick={() => setModal({ kind: 'activity', item: null })} size="sm">+ Log</Button>
                    </div>
                    {activities.slice(0, 5).length === 0 ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>No activities logged yet.</div>
                    ) : activities.slice(0, 5).map(a => {
                      const who = attendeeSummary(a, data.contacts);
                      const actionTasks = nextActionTasks(data.tasks, a.id);
                      return (
                      <div key={a.id} style={{ borderBottom: '1px solid var(--border)', paddingBottom: 10, marginBottom: 10 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={{ fontSize: 14 }}>{ACT_ICON[a.type] || '📝'}</span>
                          <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 12 }}>{a.subject}</span>
                          <Badge text={a.type} color="var(--text-muted)" />
                          {a.completed && <Badge text="Done" color="var(--success)" />}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{a.date}{who ? ` · ${who}` : ''}{a.location ? ` · 📍 ${a.location}` : ''}</div>
                        {a.outcome && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>↳ {a.outcome}</div>}
                        {actionTasks.map(t => (
                          <div key={t.id} style={{ fontSize: 11, color: t.status === 'Done' ? 'var(--text-muted)' : 'var(--warn)', marginTop: 2, textDecoration: t.status === 'Done' ? 'line-through' : 'none' }}>
                            → {t.title}{t.owner ? ` · ${t.owner}` : ''}{t.due_date ? ` · due ${t.due_date}` : ''}
                          </div>
                        ))}
                        {a.next_action && actionTasks.length === 0 && <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 2 }}>→ {a.next_action}</div>}
                      </div>
                      );
                    })}
                    {activities.length > 5 && (
                      <Button onClick={() => setTab('activities')} size="sm" fullWidth style={{ textAlign: 'center' }}>
                        View all {activities.length} activities →
                      </Button>
                    )}
                  </div>

                  {/* Open tasks */}
                  <div className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Open Tasks</span>
                      <Button onClick={() => setModal({ kind: 'task', item: null })} size="sm">+ Add Task</Button>
                    </div>
                    {tasks.filter(t => t.status !== 'Done').length === 0 ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>No open tasks.</div>
                    ) : tasks.filter(t => t.status !== 'Done').slice(0, 6).map(t => {
                      const isOverdue = t.due_date && t.due_date < today;
                      return (
                        <div key={t.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', borderBottom: '1px solid var(--border)', paddingBottom: 8, marginBottom: 8 }}>
                          <button onClick={() => cycleTaskStatus(t.id)}
                            style={{ background: 'none', border: `1px solid ${STATUS_COLOR[t.status]}`, borderRadius: 4, padding: '2px 6px', fontSize: 10, color: STATUS_COLOR[t.status], cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            {t.status}
                          </button>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>{t.title}</div>
                            {t.due_date && (
                              <div style={{ fontSize: 10, color: isOverdue ? 'var(--danger)' : 'var(--text-muted)', marginTop: 2 }}>
                                {isOverdue ? '⚠ Overdue · ' : ''}{t.due_date}
                              </div>
                            )}
                          </div>
                          <Badge text={t.priority} color={PRIORITY_COLOR[t.priority]} />
                        </div>
                      );
                    })}
                  </div>

                  {/* Contacts summary */}
                  <div className="card" style={{ gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Key Contacts</span>
                      <Button onClick={() => setModal({ kind: 'contact', item: null })} size="sm">+ Add Contact</Button>
                    </div>
                    {contacts.length === 0 ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>No contacts added yet.</div>
                    ) : (
                      <div className="crm-contacts-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                        {contacts.map(c => (
                          <div key={c.id} style={{ background: 'var(--bg-elevated)', border: `1px solid ${c.active === false ? 'var(--border)' : 'var(--border)'}`, borderRadius: 8, padding: '12px 14px', position: 'relative', opacity: c.active === false ? 0.6 : 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>{contactName(c)}</div>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <Button onClick={() => setModal({ kind: 'contact', item: c })} size="sm" style={{ fontSize: 10, padding: '2px 7px' }}>Edit</Button>
                                <Button onClick={() => delContact(c.id)} variant="destructive" size="sm" style={{ fontSize: 10, padding: '2px 7px' }}>×</Button>
                              </div>
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{c.title}</div>
                            {c.email && <div style={{ fontSize: 11, marginTop: 4 }}><a href={`mailto:${c.email}`} style={{ color: 'var(--info)', textDecoration: 'none' }} onClick={e => e.stopPropagation()}>{c.email}</a></div>}
                            {c.phone && <div style={{ fontSize: 11 }}><a href={`tel:${c.phone.replace(/[^+\d]/g, '')}`} style={{ color: 'var(--text-muted)', textDecoration: 'none' }} onClick={e => e.stopPropagation()}>{c.phone}</a></div>}
                            <div style={{ marginTop: 6 }}>
                              <button onClick={() => saveContact({ ...c, active: c.active === false ? true : false })}
                                style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer',
                                  background: c.active === false ? 'var(--bg-input)' : 'var(--success-soft)',
                                  color: c.active === false ? 'var(--text-muted)' : 'var(--success)' }}>
                                {c.active === false ? 'Inactive' : 'Active'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* CONTACTS TAB */}
              {tab === 'contacts' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                    <Button onClick={() => setModal({ kind: 'contact', item: null })} variant="primary">+ Add Contact</Button>
                  </div>
                  <SuggestedContactsPanel
                    apiFetch={apiFetch}
                    customerCode={selCust}
                    currentUserOid={currentUserOid}
                    existingContacts={contacts}
                    onAdd={(prefill) => setModal({ kind: 'contact', item: null, prefill })}
                  />
                  {contacts.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No contacts yet — add your first contact above.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {contacts.map(c => (
                        <div key={c.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', opacity: c.active === false ? 0.65 : 1 }}>
                          <div style={{ display: 'flex', gap: 12, flex: 1 }}>
                            <ContactAvatar contact={c} size={40} />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                              <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 14 }}>{contactName(c)}</span>
                              {c.title && <Badge text={c.title} color="var(--text-muted)" />}
                              <button onClick={() => saveContact({ ...c, active: c.active === false ? true : false })}
                                style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer',
                                  background: c.active === false ? 'var(--bg-input)' : 'var(--success-soft)',
                                  color: c.active === false ? 'var(--text-body)' : 'var(--success)' }}>
                                {c.active === false ? 'Inactive' : 'Active'}
                              </button>
                            </div>
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                              {c.email && <a href={`mailto:${c.email}`} style={{ fontSize: 12, color: 'var(--info)', textDecoration: 'none' }}>{c.email}</a>}
                              {c.phone && <a href={`tel:${c.phone.replace(/[^+\d]/g, '')}`} style={{ fontSize: 12, color: 'var(--text-muted)', textDecoration: 'none' }}>{c.phone}</a>}
                              {c.linkedin && <a href={c.linkedin.startsWith('http') ? c.linkedin : `https://www.linkedin.com/in/${c.linkedin}`} target="_blank" rel="noreferrer noopener" style={{ fontSize: 12, color: '#0e76a8', textDecoration: 'none' }}>{c.linkedin}</a>}
                            </div>
                            {c.notes && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, whiteSpace: 'pre-wrap' }}><RenderWithMentions text={c.notes} /></div>}
                          </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6, marginLeft: 16 }}>
                            <Button onClick={() => setModal({ kind: 'contact', item: c })}>Edit</Button>
                            <Button onClick={() => delContact(c.id)} variant="destructive">×</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ACTIVITIES TAB */}
              {tab === 'activities' && (
                <div>
                  <div className="crm-filter-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
                      <span>Filter:</span>
                      <select value={actTypeFilter} onChange={e => setActTypeFilter(e.target.value)} style={{ ...IS, width: 'auto', fontSize: 11, padding: '4px 8px' }}>
                        <option value="All">All types</option>
                        {ACT_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                      <select value={actStatusFilter} onChange={e => setActStatusFilter(e.target.value)} style={{ ...IS, width: 'auto', fontSize: 11, padding: '4px 8px' }}>
                        <option value="All">All status</option>
                        <option value="Open">Open</option>
                        <option value="Completed">Completed</option>
                      </select>
                      <span style={{ fontSize: 11 }}>
                        {filteredActivities.length === activities.length
                          ? `${activities.length} total`
                          : `${filteredActivities.length} of ${activities.length}`}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {selActIds.size > 0 && (
                        <>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{selActIds.size} selected</span>
                          <Button onClick={() => setSelActIds(new Set())} size="sm">Clear</Button>
                          <Button onClick={() => bulkDelete('activities', selActIds)} variant="destructive" size="sm">Delete {selActIds.size}</Button>
                        </>
                      )}
                      <Button onClick={() => setModal({ kind: 'activity', item: null })} variant="primary">+ Log Activity</Button>
                    </div>
                  </div>
                  {activities.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No activities logged yet.</div>
                  ) : filteredActivities.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No activities match the current filters.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {filteredActivities.map(a => {
                        const contact = a.contact_id ? data.contacts[a.contact_id] : null;
                        const isSel = selActIds.has(a.id);
                        // 057: everyone who was there, and the tasks this meeting produced.
                        const people = attendeesOf(a, data.contacts);
                        const custPeople = people.filter(p => p.side === 'customer');
                        const majansPeople = people.filter(p => p.side === 'majans');
                        const actionTasks = nextActionTasks(data.tasks, a.id);
                        return (
                          <div key={a.id} className="card" style={isSel ? { outline: '2px solid var(--info)' } : undefined}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                              <input type="checkbox" checked={isSel} onChange={() => toggleSel(setSelActIds, a.id)}
                                aria-label={`Select activity ${a.subject}`}
                                style={{ marginTop: 4, width: 14, height: 14, cursor: 'pointer', flexShrink: 0 }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                  <span style={{ fontSize: 18 }}>{ACT_ICON[a.type] || '📝'}</span>
                                  <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 14 }}>{a.subject}</span>
                                  <Badge text={a.type} color="var(--text-muted)" />
                                  {a.completed && <Badge text="Completed" color="var(--success)" />}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, flexWrap: 'wrap' }}>
                                  {contact && <ContactAvatar contact={contact} size={18} />}
                                  {a.date}
                                  {custPeople.length > 0 && (
                                    <span>
                                      · {custPeople.map(p => attendeeName(p, data.contacts)).filter(Boolean).join(', ')}
                                      {custPeople.length === 1 && contact?.title ? ` (${contact.title})` : ''}
                                    </span>
                                  )}
                                </div>
                                {majansPeople.length > 0 && (
                                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: a.location ? 4 : 8 }}>
                                    🧑‍💼 Majans: {majansPeople.map(p => attendeeName(p, data.contacts)).filter(Boolean).join(', ')}
                                  </div>
                                )}
                                {a.location && (
                                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                                    📍 {a.location}
                                  </div>
                                )}
                                {a.notion_url && (
                                  <div style={{ fontSize: 12, marginBottom: 8 }}>
                                    <a href={normaliseUrl(a.notion_url)} target="_blank" rel="noopener noreferrer"
                                       style={{ color: 'var(--info)', textDecoration: 'none' }}>
                                      📄 Meeting notes
                                    </a>
                                  </div>
                                )}
                                {a.notes && (
                                  <div style={{ fontSize: 12, color: 'var(--text-body)', background: 'var(--bg-elevated)', borderRadius: 6, padding: '8px 12px', marginBottom: 8, whiteSpace: 'pre-wrap' }}>
                                    <RenderWithMentions text={a.notes} />
                                  </div>
                                )}
                                {a.outcome && (
                                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                                    <strong style={{ color: 'var(--text-body)' }}>Outcome: </strong>{a.outcome}
                                  </div>
                                )}
                                {/* 057: next actions are real tasks — status and owner
                                    come straight off the CrmTask row, so ticking one
                                    off in the Tasks tab shows here immediately. */}
                                {actionTasks.length > 0 && (
                                  <div style={{ marginTop: 2 }}>
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 3 }}>Next actions</div>
                                    {actionTasks.map(t => (
                                      <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 2, flexWrap: 'wrap' }}>
                                        <button onClick={() => cycleTaskStatus(t.id)} title="Click to change status"
                                          style={{ background: 'none', border: `1px solid ${STATUS_COLOR[t.status]}`, borderRadius: 5, padding: '1px 6px', fontSize: 10, color: STATUS_COLOR[t.status], cursor: 'pointer', fontWeight: 600 }}>
                                          {t.status}
                                        </button>
                                        <span style={{ color: t.status === 'Done' ? 'var(--text-muted)' : 'var(--warn)', textDecoration: t.status === 'Done' ? 'line-through' : 'none' }}>
                                          {t.title}
                                        </span>
                                        {t.owner && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {t.owner}</span>}
                                        {t.due_date && <span style={{ fontSize: 11, color: t.status !== 'Done' && t.due_date < today ? 'var(--danger)' : 'var(--text-muted)' }}>· due {t.due_date}</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* Pre-057 activities whose single Next Action was never
                                    converted to a task still read the way they always did. */}
                                {a.next_action && actionTasks.length === 0 && (
                                  <div style={{ fontSize: 12, color: 'var(--warn)' }}>
                                    → Next: {a.next_action}
                                  </div>
                                )}
                                {a.attachments && a.attachments.length > 0 && (
                                  <div style={{ marginTop: 8 }}>
                                    <AttachmentChips attachments={a.attachments} />
                                  </div>
                                )}
                                <EditedCaption entity={a} />
                              </div>
                              <div style={{ display: 'flex', gap: 6, marginLeft: 16 }}>
                                <Button onClick={() => setModal({ kind: 'activity', item: a })}>Edit</Button>
                                <Button onClick={() => delActivity(a.id)} variant="destructive">×</Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* TASKS TAB */}
              {tab === 'tasks' && (
                <div>
                  <div className="crm-filter-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
                      <span>Filter:</span>
                      <select value={taskStatusFilter} onChange={e => setTaskStatusFilter(e.target.value)} style={{ ...IS, width: 'auto', fontSize: 11, padding: '4px 8px' }}>
                        <option value="Active">Active (not done)</option>
                        <option value="All">All</option>
                        {STATUSES.map(s => <option key={s}>{s}</option>)}
                      </select>
                      <select value={taskPrioFilter} onChange={e => setTaskPrioFilter(e.target.value)} style={{ ...IS, width: 'auto', fontSize: 11, padding: '4px 8px' }}>
                        <option value="All">All priority</option>
                        {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                      </select>
                      <span style={{ fontSize: 11 }}>
                        {filteredTasks.length === tasks.length
                          ? `${tasks.length} total`
                          : `${filteredTasks.length} of ${tasks.length}`}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {selTaskIds.size > 0 && (
                        <>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{selTaskIds.size} selected</span>
                          <Button onClick={() => setSelTaskIds(new Set())} size="sm">Clear</Button>
                          <Button onClick={() => bulkDelete('tasks', selTaskIds)} variant="destructive" size="sm">Delete {selTaskIds.size}</Button>
                        </>
                      )}
                      <Button onClick={() => setModal({ kind: 'task', item: null })} variant="primary">+ Add Task</Button>
                    </div>
                  </div>
                  {tasks.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No tasks yet.</div>
                  ) : filteredTasks.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No tasks match the current filters.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {filteredTasks.map(t => {
                        const isOverdue = t.status !== 'Done' && t.due_date && t.due_date < today;
                        const contact   = t.contact_id ? data.contacts[t.contact_id] : null;
                        const isSel     = selTaskIds.has(t.id);
                        return (
                          <div key={t.id} className="card" style={{ opacity: t.status === 'Done' ? 0.55 : 1, outline: isSel ? '2px solid var(--info)' : undefined }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                              <input type="checkbox" checked={isSel} onChange={() => toggleSel(setSelTaskIds, t.id)}
                                aria-label={`Select task ${t.title}`}
                                style={{ marginTop: 4, width: 14, height: 14, cursor: 'pointer', flexShrink: 0 }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <button onClick={() => cycleTaskStatus(t.id)}
                                    style={{ background: 'none', border: `1px solid ${STATUS_COLOR[t.status]}`, borderRadius: 5, padding: '2px 8px', fontSize: 11, color: STATUS_COLOR[t.status], cursor: 'pointer', fontWeight: 600 }}>
                                    {t.status}
                                  </button>
                                  <span style={{ fontWeight: 600, color: t.status === 'Done' ? 'var(--text-muted)' : 'var(--text-primary)', fontSize: 13, textDecoration: t.status === 'Done' ? 'line-through' : 'none' }}>
                                    {t.title}
                                  </span>
                                  <Badge text={t.priority} color={PRIORITY_COLOR[t.priority]} />
                                  {isOverdue && <Badge text="Overdue" color="var(--danger)" />}
                                </div>
                                {(t.due_date || contact || t.owner) && (
                                  <div style={{ fontSize: 11, color: isOverdue ? 'var(--danger)' : 'var(--text-muted)', marginTop: 4 }}>
                                    {[t.due_date && `Due ${t.due_date}`, t.owner && `Owner: ${t.owner}`, contact && contactName(contact)].filter(Boolean).join(' · ')}
                                  </div>
                                )}
                                {/* 057: a task born out of a logged meeting says which one. */}
                                {t.activity_id && data.activities[t.activity_id] && (
                                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>
                                    ↳ from {ACT_ICON[data.activities[t.activity_id].type] || '📝'} {data.activities[t.activity_id].subject}
                                  </div>
                                )}
                                {t.notes && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}><RenderWithMentions text={t.notes} /></div>}
                                <EditedCaption entity={t} />
                              </div>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <Button onClick={() => setModal({ kind: 'task', item: t })}>Edit</Button>
                                <Button onClick={() => delTask(t.id)} variant="destructive">×</Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Global tasks sidebar if overdue */}
      {overdueTasks.length > 0 && (
        <div className="crm-overdue-rail" style={{ width: 220, minWidth: 220, background: 'var(--bg-elevated)', borderLeft: '1px solid var(--border)', padding: '12px', overflowY: 'auto' }}>
          <div style={{ fontWeight: 700, color: 'var(--danger)', fontSize: 12, marginBottom: 10 }}>
            ⚠ {overdueTasks.length} Overdue Task{overdueTasks.length > 1 ? 's' : ''}
          </div>
          {overdueTasks.slice(0, 10).map(t => {
            const custName = data.customers[t.customer_code]?.short_name || t.customer_code;
            return (
              <div key={t.id}
                onClick={() => { setSelCust(t.customer_code); setTab('tasks'); }}
                style={{ cursor: 'pointer', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 600 }}>{custName}</div>
                <div style={{ fontSize: 11, color: 'var(--text-primary)' }}>{t.title}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Due {t.due_date}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {modal?.kind === 'customer' && (
        <Modal title={modal.item ? 'Edit Customer' : 'Add Customer'} onClose={() => setModal(null)}>
          <CustomerForm init={modal.item} onSave={saveCust} apiFetch={apiFetch} onSegmentRemap={applySegmentRemap} />
        </Modal>
      )}
      {modal?.kind === 'adopt-customer' && (
        <Modal title="Add an existing customer" onClose={() => setModal(null)}>
          <AdoptCustomerForm
            apiFetch={apiFetch}
            prefill={modal.prefill}
            onDone={(code) => { setModal(null); adoptCust(code); }}
            onOpen={(code) => {
              setModal(null);
              setSearch(''); setSegFilter('All'); setOwnerFilter('All');
              setSelCust(code); setView('customer');
            }}
          />
        </Modal>
      )}
      {modal?.kind === 'contact' && (
        <Modal title={modal.item ? 'Edit Contact' : 'Add Contact'} onClose={() => setModal(null)}>
          <ContactForm init={modal.item ?? modal.prefill ?? null} onSave={saveContact} draftKeyBase={selCust} />
        </Modal>
      )}
      {modal?.kind === 'activity' && (
        <Modal title={modal.item ? 'Edit Activity' : 'Log Activity'} onClose={() => setModal(null)} wide>
          <ActivityForm
            init={modal.item}
            contacts={contacts}
            contactsById={data.contacts}
            linkedTasks={nextActionTasks(data.tasks, modal.item?.id)}
            staffSuggestions={staffSuggestions}
            currentUserName={currentUserName}
            onSave={saveActivity}
            apiFetch={apiFetch}
            draftKeyBase={selCust}
          />
        </Modal>
      )}
      {modal?.kind === 'task' && (
        <Modal title={modal.item ? 'Edit Task' : 'Add Task'} onClose={() => setModal(null)}>
          <TaskForm
            init={modal.item}
            contacts={contacts}
            staffSuggestions={staffSuggestions}
            sourceActivity={modal.item?.activity_id ? data.activities[modal.item.activity_id] : null}
            onSave={saveTask}
            draftKeyBase={selCust}
          />
        </Modal>
      )}
      {conflict && (
        <Modal title="CRM was updated elsewhere" onClose={() => { /* require explicit choice */ }} wide>
          <div style={{ fontSize: 13, color: 'var(--text-body)', lineHeight: 1.5 }}>
            <p style={{ marginTop: 0 }}>
              Your CRM data was changed in another tab, browser, or device since you loaded this page.
              If you save your current edits, you will overwrite those changes.
            </p>
            {conflict.current?.updatedAt && (
              <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                Server version: {new Date(conflict.current.updatedAt).toLocaleString()}
              </p>
            )}
            <p>Choose how to proceed:</p>
            <ul style={{ paddingLeft: 18, marginBottom: 16 }}>
              <li><strong>Use server version</strong> — discard your unsaved edits and load the latest data from the server.</li>
              <li><strong>Keep my version</strong> — overwrite the server with your current edits (other changes will be lost).</li>
            </ul>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Tip: download a backup first (Export → Full backup) if you want to keep both versions.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <Button onClick={resolveTakeTheirs}>Use server version</Button>
              <Button onClick={resolveKeepMine} variant="destructive">Keep my version (overwrite)</Button>
            </div>
          </div>
        </Modal>
      )}
      {modal?.kind === 'export' && (
        <Modal title="Backup & Restore CRM data" onClose={() => setModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
              CSV files open directly in Excel. JSON is a complete backup of your CRM (re-importable later).
            </p>
            {[
              { kind: 'customers',  label: 'Customers',  count: Object.keys(data.customers).length },
              { kind: 'contacts',   label: 'Contacts',   count: Object.keys(data.contacts).length },
              { kind: 'activities', label: 'Activities', count: Object.keys(data.activities).length },
              { kind: 'tasks',      label: 'Tasks',      count: Object.keys(data.tasks).length },
            ].map(opt => (
              <Button key={opt.kind}
                onClick={() => { exportData(opt.kind); setModal(null); }}
                style={{ textAlign: 'left', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>⇩ {opt.label} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({opt.count})</span></span>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>CSV</span>
              </Button>
            ))}
            <Button onClick={() => { exportData('json'); setModal(null); }}
              variant="primary"
              style={{ textAlign: 'left', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
              <span>⇩ Full backup</span>
              <span style={{ fontSize: 10 }}>JSON</span>
            </Button>
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 12 }}>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>Restore from backup</div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 8px' }}>
                Select a JSON file from a previous full backup. Your current CRM data will be overwritten — download a backup first if you want to keep it.
              </p>
              <label style={{ background: 'var(--border)', color: 'var(--text-body)', border: '1px solid var(--border-soft)', borderRadius: 'var(--radius)', display: 'inline-block', cursor: 'pointer', padding: '8px 14px', fontSize: 12 }}>
                ⇧ Choose backup file…
                <input type="file" accept=".json,application/json" style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = ''; // allow re-selecting the same file
                    await importJson(file);
                    setModal(null);
                  }} />
              </label>
            </div>
            {/* Phase 17 admin tool: one-shot union of all UserCrmData rows into TeamCrmData.
                Returns 403 for non-Admin so the button is safe to leave visible. */}
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 12 }}>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>Admin: Sync team blob</div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 8px' }}>
                Unions every team-member's per-user CRM data into the shared team blob. Run once after
                Phase 17 deploys, or any time you want to re-derive the union from current per-user copies.
                Admin role required (will 403 otherwise).
              </p>
              <Button onClick={async () => {
                try {
                  const res = await apiFetch('/team-crm-data/merge-from-users', { method: 'POST' });
                  const body = await res.json().catch(() => ({}));
                  if (!res.ok) {
                    alert(`Merge failed (HTTP ${res.status}): ${body?.error || res.statusText}`);
                    return;
                  }
                  alert(`Merge succeeded.\n\nMerged from ${body.userCount} per-user copies:\n· ${body.totals.customers} customers\n· ${body.totals.contacts} contacts\n· ${body.totals.activities} activities\n· ${body.totals.tasks} tasks\n\n${body.mergedBytes.toLocaleString()} bytes written. Reload the page to see the unioned data.`);
                } catch (err) {
                  alert(`Merge failed: ${err.message}`);
                }
              }} style={{ padding: '8px 14px' }}>
                🔀 Run merge-from-users
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
